import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { ShoppingCart, Search, LogOut, Receipt, LayoutGrid, Plus, Minus, Trash2, X, Check, AlertCircle, Loader2, FileText, Euro, RefreshCw, Eye, Zap, Package, User, CreditCard, Banknote, ArrowLeft, Percent, Edit3, Printer, Download, Mail, RotateCcw, DollarSign, Wallet, TrendingUp, BarChart3, Settings, XCircle, CheckCircle, Archive, BookOpen, Save, PlusCircle, Building, Users, Clock, Send, Coffee, Move, Square, Circle, Armchair, ChevronRight, GripVertical, Maximize2, ToggleLeft, ToggleRight, Hash, Utensils, Home, Menu, ChevronDown, ChevronUp, MoreVertical, Smartphone, WifiOff, Wifi } from "lucide-react";

const API_BASE = (() => {
  const h = window.location.hostname, p = window.location.port;
  // Browser dev with port (npm start)
  if (h === "localhost" && p) return "http://localhost/fiscalization/api";
  // Everything else — production (Capacitor, server, direct)
  return "https://efis.pro/api";
})();
// ═══ DATE HELPERS (local timezone, not UTC) ═══
function todayLocal() { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0"); }
function daysAgoLocal(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0"); }
function monthLocal() { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0"); }
// ═══ OFFLINE CACHE SYSTEM ═══
const CACHE_PREFIX = "minipos_cache_";
const OFFLINE_DRAFTS_KEY = "minipos_offline_drafts";
const CACHE_KEYS = { products: "products", categories: "categories", partners: "partners", tcrs: "tcrs", fastMenu: "minipos_fast_menu" };
function cacheSet(key, data) { try { localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ data, ts: Date.now() })); } catch(e) {} }
function cacheGet(key) { try { const raw = localStorage.getItem(CACHE_PREFIX + key); if (!raw) return null; return JSON.parse(raw).data; } catch(e) { return null; } }
function getOfflineDrafts() { try { return JSON.parse(localStorage.getItem(OFFLINE_DRAFTS_KEY) || "[]"); } catch(e) { return []; } }
function saveOfflineDraft(payload) {
  const drafts = getOfflineDrafts();
  drafts.push({ id: Date.now(), payload, created_at: new Date().toISOString() });
  localStorage.setItem(OFFLINE_DRAFTS_KEY, JSON.stringify(drafts));
  return drafts.length;
}
function removeOfflineDraft(id) {
  const drafts = getOfflineDrafts().filter(d => d.id !== id);
  localStorage.setItem(OFFLINE_DRAFTS_KEY, JSON.stringify(drafts));
}
function isOnline() { return navigator.onLine !== false; }

async function api(path, opts = {}) {
  const token = localStorage.getItem("minipos_token");
  const h = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const r = await fetch(`${API_BASE}${path}`, { headers: h, ...opts, signal: controller.signal });
    clearTimeout(timeout);
    if (r.status === 401) { localStorage.removeItem("minipos_token"); localStorage.removeItem("minipos_user"); window.location.reload(); return { success: false }; }
    const data = await r.json();
    // Cache GET responses for offline use
    if (!opts.method || opts.method === "GET") {
      if (path === "/products" && data.success) cacheSet("products", data.data);
      if (path.includes("/categories") && data.success) cacheSet("categories", data.data);
      if (path === "/partners" && data.success) cacheSet("partners", data.data);
      if (path === "/auth/company-config" && data.success) cacheSet("tcrs", data.data);
    }
    return data;
  } catch (e) {
    // Offline: return cached data for GET requests
    if (!opts.method || opts.method === "GET") {
      if (path === "/products") { const c = cacheGet("products"); if (c) return { success: true, data: c, _offline: true }; }
      if (path.includes("/categories")) { const c = cacheGet("categories"); if (c) return { success: true, data: c, _offline: true }; }
      if (path === "/partners") { const c = cacheGet("partners"); if (c) return { success: true, data: c, _offline: true }; }
      if (path === "/auth/company-config") { const c = cacheGet("tcrs"); if (c) return { success: true, data: c, _offline: true }; }
    }
    return { success: false, message: "Network error: " + e.message, _offline: true };
  }
}
function fmt(n) { return parseFloat(n || 0).toFixed(2); }
// fmtQty: formats quantity for display.
// - Integer qty (e.g. 3) → "3" (no .00 noise)
// - Fractional qty (e.g. 0.77) → "0,77" (comma decimal — Montenegrin locale,
//   matches how Poreska-compliant printed receipts show fractional qty)
// Used everywhere the user sees a quantity: cart line, fast-add badge, line
// summary "1.50 € × 0,77". Float comparison guards against 0.1+0.2 drift.
function fmtQty(n) {
  const v = parseFloat(n || 0);
  if (Math.abs(v - Math.round(v)) < 0.0001) return String(Math.round(v));
  return v.toFixed(2).replace(".", ",");
}
function fmtDate(d) { if (!d) return "-"; return d.split(" ")[0].split("-").reverse().join("."); }
function fmtDateTime(d) { if (!d) return "-"; const [dt, tm] = d.split(" "); return dt.split("-").reverse().join(".") + " " + (tm || "").slice(0, 5); }
function shortInvNum(n) { if (!n) return ""; const parts = n.split("/"); if (parts.length >= 3) return parts[1] + "/" + parts[2]; return n; }
function getTcr() { return localStorage.getItem("minipos_tcr") || ""; }

const PRINT_SERVER = "http://localhost:9100";
async function openPdf(path) {
  const token = localStorage.getItem("minipos_token");
  try {
    const isNative = !!(window.Capacitor?.isNativePlatform?.());
    const r = await fetch(`${API_BASE}${path}`, { headers: { "Authorization": `Bearer ${token}` } });
    if (!r.ok) return false;
    const blob = await r.blob();
    
    if (isNative) {
      // Store download info for the download button
      window._pdfDownloadUrl = `${API_BASE}${path}`;
      const reader = new FileReader();
      reader.onloadend = () => { showPdfInOverlay(reader.result); };
      reader.readAsDataURL(blob);
    } else {
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
  } catch(e) { return false; }
  return true;
}

// Save file to device Downloads folder via Java plugin
async function saveFileToDevice(base64Data, filename, mimeType) {
  try {
    // Android: use native SunmiPrinter plugin
    const sp = window.Capacitor?.Plugins?.SunmiPrinter;
    if (sp && sp.saveBase64File) {
      const pureBase64 = base64Data.includes(",") ? base64Data.split(",")[1] : base64Data;
      const result = await sp.saveBase64File({ data: pureBase64, filename });
      return result;
    }
    // iOS: use Capacitor Filesystem plugin
    const Fs = window.Capacitor?.Plugins?.Filesystem;
    if (Fs) {
      const pureBase64 = base64Data.includes(",") ? base64Data.split(",")[1] : base64Data;
      const result = await Fs.writeFile({ path: filename, data: pureBase64, directory: "DOCUMENTS", recursive: true });
      return { success: true, location: "Documents/" + filename, uri: result.uri };
    }
    // Browser fallback
    const blob = base64ToBlob(base64Data, mimeType);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    return { success: true, location: filename };
  } catch (e) {
    return { success: false, error: e.message || String(e) };
  }
}

function base64ToBlob(base64, mimeType) {
  const data = base64.includes(",") ? base64.split(",")[1] : base64;
  const bytes = atob(data);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mimeType || "application/octet-stream" });
}

// Download file from URL to device
async function downloadFileToDevice(url, filename, mimeType, token) {
  try {
    // Android: use native SunmiPrinter plugin (DownloadManager)
    const sp = window.Capacitor?.Plugins?.SunmiPrinter;
    if (sp && sp.downloadFile) {
      const result = await sp.downloadFile({ url, filename, mimeType, token });
      return result;
    }
    // iOS: fetch blob then save via Filesystem plugin
    const r = await fetch(url, { headers: token ? { "Authorization": "Bearer " + token } : {} });
    if (!r.ok) return { success: false, error: "HTTP " + r.status };
    const blob = await r.blob();
    const Fs = window.Capacitor?.Plugins?.Filesystem;
    if (Fs) {
      // Convert blob to base64
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const result = await Fs.writeFile({ path: filename, data: b64, directory: "DOCUMENTS", recursive: true });
      return { success: true, location: "Documents/" + filename, uri: result.uri };
    }
    // Browser fallback
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = blobUrl; a.download = filename; a.click();
    URL.revokeObjectURL(blobUrl);
    return { success: true, location: filename };
  } catch (e) {
    return { success: false, error: e.message || String(e) };
  }
}

function showPdfInOverlay(base64DataUrl) {
  document.getElementById("pdf-overlay")?.remove();
  const overlay = document.createElement("div");
  overlay.id = "pdf-overlay";
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const iosTopPad = (isIOS && window.screen.height >= 812) ? 50 : (isIOS ? 20 : 0);
  overlay.style.cssText = "position:fixed;inset:0;z-index:99999;background:#525659;display:flex;flex-direction:column;padding-top:" + iosTopPad + "px";
  
  const bar = document.createElement("div");
  bar.style.cssText = "display:flex;justify-content:space-between;padding:8px 10px;flex-shrink:0;background:#1e293b;gap:8px";
  
  // Download button — uses Capacitor Filesystem
  const dlBtn = document.createElement("button");
  dlBtn.textContent = "⬇ Preuzmi";
  dlBtn.style.cssText = "padding:8px 14px;background:#3b82f6;color:white;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer";
  dlBtn.onclick = async () => {
    dlBtn.textContent = "Cuvanje...";
    dlBtn.disabled = true;
    const filename = "racun_" + Date.now() + ".pdf";
    const token = localStorage.getItem("minipos_token") || "";
    const url = window._pdfDownloadUrl || "";
    if (url) {
      const result = await downloadFileToDevice(url, filename, "application/pdf", token);
      if (result.success) {
        dlBtn.textContent = "✓ Downloads/" + filename;
        dlBtn.style.background = "#059669";
      } else {
        dlBtn.textContent = result.error || "Greska";
        dlBtn.style.background = "#ef4444";
        setTimeout(() => { dlBtn.textContent = "⬇ Preuzmi"; dlBtn.style.background = "#3b82f6"; dlBtn.disabled = false; }, 3000);
      }
    } else {
      // Fallback: save from base64
      const result = await saveFileToDevice(base64DataUrl, filename, "application/pdf");
      if (result.success) {
        dlBtn.textContent = "✓ Downloads/" + filename;
        dlBtn.style.background = "#059669";
      } else {
        dlBtn.textContent = result.error || "Greska";
        dlBtn.style.background = "#ef4444";
        setTimeout(() => { dlBtn.textContent = "⬇ Preuzmi"; dlBtn.style.background = "#3b82f6"; dlBtn.disabled = false; }, 3000);
      }
    }
  };
  bar.appendChild(dlBtn);
  
  const closeBtn = document.createElement("button");
  closeBtn.textContent = "✕ Zatvori";
  closeBtn.style.cssText = "padding:8px 14px;background:#ef4444;color:white;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer";
  closeBtn.onclick = () => document.getElementById("pdf-overlay")?.remove();
  bar.appendChild(closeBtn);
  overlay.appendChild(bar);
  
  const container = document.createElement("div");
  container.style.cssText = "flex:1;overflow:auto;padding:8px;background:#525659";
  overlay.appendChild(container);
  document.body.appendChild(overlay);
  
  // Load PDF.js and render
  const script = document.createElement("script");
  script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
  script.onload = async () => {
    try {
      const pdfjsLib = window.pdfjsLib;
      pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      const raw = atob(base64DataUrl.split(",")[1]);
      const arr = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
      const pdf = await pdfjsLib.getDocument({ data: arr }).promise;
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const scale = (window.innerWidth - 16) / page.getViewport({ scale: 1 }).width;
        const viewport = page.getViewport({ scale: Math.min(scale, 2) });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width; canvas.height = viewport.height;
        canvas.style.cssText = "display:block;margin:0 auto 8px;background:white;border-radius:4px;max-width:100%";
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        container.appendChild(canvas);
      }
    } catch(e) { container.innerHTML = '<div style="color:white;text-align:center;padding:40px;font-size:14px">PDF prikaz nije uspio.<br>Koristite dugme "Preuzmi" iznad.</div>'; }
  };
  script.onerror = () => { container.innerHTML = '<div style="color:white;text-align:center;padding:40px;font-size:14px">Nema interneta za PDF prikaz.<br>Koristite "Preuzmi" dugme.</div>'; };
  document.head.appendChild(script);
}

function showDocOverlay() {} // unused

const isCapacitor = !!(window.Capacitor?.isNativePlatform?.());

async function printReceiptHTML(invoiceId) {
  const token = localStorage.getItem("minipos_token");
  try {
    const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=html`, { headers: { "Authorization": `Bearer ${token}` } });
    if (!r.ok) return false;
    const html = await r.text();
    return systemPrintHTML(html);
  } catch(e) { return false; }
}

// ═══ SUNMI PRINTER BRIDGE ═══
const isSunmi = (() => {
  try { return !!(window.Capacitor?.Plugins?.SunmiPrinter); } catch(e) { return false; }
})();

// Check if this is the general printer plugin (not actual Sunmi device)
let _isGeneralPrinter = false;
(async () => {
  try {
    const sp = window.Capacitor?.Plugins?.SunmiPrinter;
    if (sp) { const a = await sp.isAvailable(); _isGeneralPrinter = !!a.isGeneral; }
  } catch(e) {}
})();

async function sunmiPrintText(text) {
  try {
    const sp = window.Capacitor?.Plugins?.SunmiPrinter;
    if (!sp) return false;
    const avail = await sp.isAvailable();
    if (!avail.available) return false;
    if (avail.isGeneral) {
      // General printer — send text as-is, plugin routes to configured printer
      await sp.printText({ text });
      return true;
    }
    // Sunmi inner printer — strip ESC/POS binary commands
    let clean = text.replace(/[\x00-\x09\x0B\x0C\x0E-\x1F\x7F-\xFF]/g, '');
    // Only compress 48→32 if text appears to be 48-char wide (don't touch already-32-char text)
    const maxLineLen = Math.max(...clean.split("\n").map(l => l.length));
    if (maxLineLen > 36) {
      clean = clean.split("\n").map(line => {
        return line.replace(/\s{3,}/g, (m) => " ".repeat(Math.max(1, Math.ceil(m.length * 32 / 48))));
      }).join("\n");
    }
    await sp.printText({ text: clean });
    return true;
  } catch(e) { return false; }
}

async function sunmiPrintReceiptWithQR(text, qrUrl) {
  try {
    const sp = window.Capacitor?.Plugins?.SunmiPrinter;
    if (!sp) return false;
    const avail = await sp.isAvailable();
    if (!avail.available) return false;
    // Strip ESC/POS binary commands
    const clean = text.replace(/[\x00-\x09\x0B\x0C\x0E-\x1F\x7F-\xFF]/g, '');
    if (qrUrl) {
      // Split text at QR marker if present, otherwise print QR at end
      const qrMarker = "mapr.tax.gov.me";
      const markerIdx = clean.indexOf(qrMarker);
      let textBefore = clean;
      let textAfter = "";
      if (markerIdx > -1) {
        // Find the line with the marker
        const lines = clean.split("\n");
        const beforeLines = [];
        const afterLines = [];
        let foundMarker = false;
        for (const line of lines) {
          if (!foundMarker && line.includes(qrMarker)) {
            foundMarker = true;
            // Skip the "Verifikacija:" line and the URL line
            continue;
          }
          if (!foundMarker) beforeLines.push(line);
          else afterLines.push(line);
        }
        textBefore = beforeLines.join("\n");
        textAfter = afterLines.join("\n");
      }
      // qrSize 4 gives ~15mm on 58mm paper (was 6, which was ~22mm — too big).
      // Sunmi's printQRCode accepts module size 4-16; 4 is the smallest readable
      // size and matches the visual proportions of the printed receipt.
      await sp.printReceiptWithQR({ textBefore, qrData: qrUrl, textAfter, qrSize: 4 });
    } else {
      await sp.printText({ text: clean });
    }
    return true;
  } catch(e) { return false; }
}

async function thermalPrint(endpoint, data) {
  // Generate text locally for register reports on Sunmi
  if (isSunmi && endpoint === "/print/register" && data) {
    const w = 32;
    const lr = (l, r) => l + " ".repeat(Math.max(1, w - l.length - r.length)) + r;
    let text = "=".repeat(w) + "\n";
    text += data.type === "opening" ? "  OTVARANJE BLAGAJNE\n" : "  ZATVARANJE BLAGAJNE\n";
    text += "=".repeat(w) + "\n";
    text += (data.company_name || "") + "\n";
    text += "TCR: " + (data.tcr_code || "") + "\n";
    text += "-".repeat(w) + "\n";
    if (data.type === "opening") {
      text += lr("Datum:", data.date || "") + "\n";
      text += lr("Kasir:", data.operator_name || "") + "\n";
      text += lr("Depozit:", (parseFloat(data.deposit_amount) || 0).toFixed(2) + " EUR") + "\n";
      if (data.fic) {
        text += "-".repeat(w) + "\n";
        text += "IKOF/FIC:\n";
        text += data.fic + "\n";
      }
    } else {
      text += lr("Otvoreno:", data.opened_at || "") + "\n";
      text += lr("Zatvoreno:", data.closed_at || "") + "\n";
      text += lr("Kasir:", data.operator_name || "") + "\n";
      text += "-".repeat(w) + "\n";
      text += lr("Depozit:", (parseFloat(data.opening_cash) || 0).toFixed(2) + " EUR") + "\n";
      text += lr("Gotovina:", (parseFloat(data.cash_sales) || 0).toFixed(2) + " EUR") + "\n";
      text += lr("Kartica:", (parseFloat(data.card_sales) || 0).toFixed(2) + " EUR") + "\n";
      text += lr("Promet:", (parseFloat(data.total_sales) || 0).toFixed(2) + " EUR") + "\n";
      text += lr("Racuni:", (data.transaction_count || 0) + "") + "\n";
      text += "=".repeat(w) + "\n";
      text += lr("Ocekivano:", (parseFloat(data.expected_cash) || 0).toFixed(2) + " EUR") + "\n";
      text += lr("Stvarno:", (parseFloat(data.actual_cash) || 0).toFixed(2) + " EUR") + "\n";
      text += lr("Razlika:", (parseFloat(data.difference) || 0).toFixed(2) + " EUR") + "\n";
    }
    text += "=".repeat(w) + "\n\n\n";
    const ok = await sunmiPrintText(text);
    if (ok) return true;
  }
  // Try Sunmi built-in printer for text
  if (isSunmi && endpoint === "/print/text" && data.text) {
    const ok = await sunmiPrintText(data.text);
    if (ok) return true;
  }
  // Try network print server if configured
  const pType = localStorage.getItem("minipos_printer_type");
  const addr = localStorage.getItem("minipos_printer_addr");
  if (pType === "network" && addr) {
    try {
      const r = await fetch(`${addr}${endpoint}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
      const d = await r.json(); if (d.success) return true;
    } catch(e) {}
  }
  // Fallback: system print via iframe
  if (endpoint === "/print/text" && data.text) {
    return systemPrintText(data.text);
  }
  if (endpoint === "/print/register" && data) {
    // Generate text for system print fallback
    const w = 32;
    const lr = (l, r) => l + " ".repeat(Math.max(1, w - l.length - r.length)) + r;
    let text = "=".repeat(w) + "\n";
    text += data.type === "opening" ? "OTVARANJE BLAGAJNE\n" : "ZATVARANJE BLAGAJNE\n";
    text += (data.company_name || "") + "\nTCR: " + (data.tcr_code || "") + "\n";
    text += lr("Datum:", data.date || data.opened_at || "") + "\n";
    if (data.type !== "opening") {
      text += lr("Promet:", (parseFloat(data.total_sales) || 0).toFixed(2) + " EUR") + "\n";
    }
    text += lr("Depozit:", (parseFloat(data.deposit_amount || data.opening_cash) || 0).toFixed(2) + " EUR") + "\n";
    text += "=".repeat(w) + "\n";
    return systemPrintText(text);
  }
  return false;
}

function systemPrintText(text) {
  const pw = (localStorage.getItem("minipos_paper_width") || "58") + "mm";
  const html = `<pre style="font-family:monospace;font-size:12px;white-space:pre-wrap;margin:0;padding:2mm;">${text}</pre>`;
  return systemPrintHTML(html);
}

async function systemPrintHTML(html) {
  const pw = (localStorage.getItem("minipos_paper_width") || "58") + "mm";
  return new Promise((resolve) => {
    const old = document.getElementById("minipos-print-frame");
    if (old) old.remove();
    const iframe = document.createElement("iframe");
    iframe.id = "minipos-print-frame";
    iframe.style.cssText = "position:fixed;top:0;left:0;width:100%;height:100%;z-index:99999;border:none;background:white;";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<html><head><style>@page{size:${pw} auto;margin:2mm;}body{font-family:monospace;font-size:12px;margin:0;padding:4mm;width:${pw};}@media print{body{width:${pw};}}</style></head><body>${html}</body></html>`);
    doc.close();
    setTimeout(() => {
      try { iframe.contentWindow.print(); } catch(e) { window.print(); }
      setTimeout(() => { iframe.remove(); }, 1000);
      resolve(true);
    }, 500);
  });
}

async function printReceipt(invoiceId, isCopy = false) {
  const token = localStorage.getItem("minipos_token");

  // Method 1: Native printer (Sunmi inner or General BT/USB/TCP)
  if (isSunmi) {
    try {
      // General printer (BT/USB/TCP) — use type=text with ESC/POS commands including QR
      if (_isGeneralPrinter) {
        const pw = parseInt(localStorage.getItem("minipos_paper_width") || "58");
        const widthParam = pw <= 58 ? "&width=32" : "";
        const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=text${widthParam}`, { headers: { "Authorization": `Bearer ${token}` } });
        if (r.ok) {
          // CRITICAL: response body contains ESC/POS bytes including the QR
          // raster bitmap (0x00..0xFF). Using r.text() would UTF-8-decode the
          // bytes and corrupt every byte > 0x7F (the entire bitmap). Read as
          // ArrayBuffer and convert to a binary string with a 1:1 byte→codepoint
          // mapping so the GeneralPrinterPlugin (ISO-8859-1 encoded) ships the
          // exact bytes the backend produced.
          const buf = await r.arrayBuffer();
          const bytes = new Uint8Array(buf);
          let text = "";
          // Build in chunks to avoid call-stack issues with very large buffers
          const chunkSize = 0x8000;
          for (let i = 0; i < bytes.length; i += chunkSize) {
            text += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
          }
          if (isCopy) {
            // Plain ASCII replacements — safe to run on the binary string since
            // these tokens never collide with QR raster bytes (which start
            // after the verification heading).
            text = text.replace(/FISKALNI RACUN/, "FISKALNI RACUN - KOPIJA").replace(/VIRMAN RACUN/, "VIRMAN RACUN - KOPIJA").replace(/STORNO RACUN/, "STORNO RACUN - KOPIJA").replace(/AVANSNI RACUN/, "AVANSNI RACUN - KOPIJA").replace(/FINALNI RACUN/, "FINALNI RACUN - KOPIJA").replace(/KOREKTIVNI RACUN/, "KOREKTIVNI RACUN - KOPIJA");
          }
          const sp = window.Capacitor?.Plugins?.SunmiPrinter;
          if (sp) { await sp.printText({ text }); return true; }
        }
      } else {
        // Actual Sunmi device — use type=sunmi (plain text, QR via Sunmi API)
        const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=sunmi`, { headers: { "Authorization": `Bearer ${token}` } });
        if (r.ok) {
          let text = await r.text();
          if (isCopy) {
            text = text.replace(/FISKALNI RACUN/, "FISKALNI RACUN - KOPIJA").replace(/VIRMAN RACUN/, "VIRMAN RACUN - KOPIJA").replace(/STORNO RACUN/, "STORNO RACUN - KOPIJA").replace(/AVANSNI RACUN/, "AVANSNI RACUN - KOPIJA").replace(/FINALNI RACUN/, "FINALNI RACUN - KOPIJA").replace(/KOREKTIVNI RACUN/, "KOREKTIVNI RACUN - KOPIJA");
          }
          // Use the backend-built verification URL (mapr.tax.gov.me with all 8
          // spec params, Europe/Podgorica timezone). Keeps the QR byte-identical
          // across receipt printing, PDFs, and the Racuni screen.
          let qrUrl = "";
          try {
            const invR = await fetch(`${API_BASE}/invoices/show/${invoiceId}`, { headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" } });
            if (invR.ok) {
              const invData = await invR.json();
              qrUrl = invData.data?.verification_url || "";
            }
          } catch(e) {}
          const ok = await sunmiPrintReceiptWithQR(text, qrUrl);
          if (ok) return true;
        }
      }
    } catch(e) {}
  }

  // Method 2: Network print server (if configured)
  const pType = localStorage.getItem("minipos_printer_type");
  const addr = localStorage.getItem("minipos_printer_addr");
  if (pType === "network" && addr) {
    try {
      const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=text`, { headers: { "Authorization": `Bearer ${token}` } });
      if (r.ok) {
        const text = await r.text();
        const printUrl = addr.endsWith("/") ? addr.slice(0,-1) + "/print/text" : addr + "/print/text";
        const pr = await fetch(printUrl, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
        const result = await pr.json();
        if (result.success) return true;
      }
    } catch(e) {}
  }

  // Method 3: System print (iframe)
  try {
    const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=html`, { headers: { "Authorization": `Bearer ${token}` } });
    if (r.ok) {
      const html = await r.text();
      return systemPrintHTML(html);
    }
  } catch(e) {}
  return false;
}

async function checkPrintServer() {
  if (isSunmi) {
    try {
      const sp = window.Capacitor?.Plugins?.SunmiPrinter;
      if (sp) { const a = await sp.isAvailable(); return a.available; }
    } catch(e) {}
  }
  const pType = localStorage.getItem("minipos_printer_type");
  const addr = localStorage.getItem("minipos_printer_addr");
  if (pType === "network" && addr) {
    try { const r = await fetch(`${addr}/status`, { signal: AbortSignal.timeout(2000) }); const d = await r.json(); return d.success; } catch(e) { return false; }
  }
  return true;
}

const M = {
  font: "'DM Sans', -apple-system, sans-serif",
  mono: "'JetBrains Mono', monospace",
  dark: "#111827", muted: "#6b7280", border: "#e5e7eb", bg: "#f3f4f6",
  green: "#059669", red: "#dc2626", blue: "#3b82f6", orange: "#ea580c",
  card: { background: "white", borderRadius: 12, border: "1px solid #e5e7eb" },
  input: { width: "100%", padding: "13px 14px", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 16, fontFamily: "'DM Sans', sans-serif", outline: "none", boxSizing: "border-box" },
  btn: (bg, color) => ({ display: "inline-flex", alignItems: "center", gap: 7, padding: "14px 20px", background: bg, color: color || "white", border: "none", borderRadius: 12, cursor: "pointer", fontWeight: 600, fontSize: 15, fontFamily: "'DM Sans', sans-serif", minHeight: 48 }),
  btnSm: (bg, color) => ({ display: "inline-flex", alignItems: "center", gap: 5, padding: "10px 14px", background: bg, color: color || "white", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 13, fontFamily: "'DM Sans', sans-serif", minHeight: 40 }),
};
const th = { padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 11, borderBottom: "1px solid #e5e7eb" };
const td = { padding: "8px 10px", color: "#1a1a2e", fontSize: 12 };

// ─── Shared Components ───
function Row({ label, value, last }) {
  return <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: last ? "none" : "1px solid #f3f4f6" }}><span style={{ color: M.muted, fontSize: 13 }}>{label}</span><span style={{ fontWeight: 500, fontSize: 13, textAlign: "right", maxWidth: "60%", wordBreak: "break-word" }}>{value}</span></div>;
}
function CatTab({ active, onClick, label }) {
  return <button onClick={onClick} style={{ padding: "5px 12px", borderRadius: 20, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", background: active ? M.dark : "#f3f4f6", color: active ? "white" : M.muted, fontFamily: M.font, whiteSpace: "nowrap", flexShrink: 0 }}>{label}</button>;
}
function Modal({ children, onClose, title }) {
  return (<div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
    <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
    <div style={{ position: "relative", background: "white", borderRadius: 20, width: "100%", maxWidth: 440, maxHeight: "85vh", overflow: "auto", padding: 20 }}>
      {title && <h3 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 700 }}>{title}</h3>}
      {children}
    </div>
  </div>);
}

// QtyNumpadModal — touch-friendly decimal quantity entry for is_decimal_unit
// products (e.g. fish/meat sold by 0.77 kg). Mirrors the POS app behavior:
// caps input at 2 decimals (Poreska spec mandates 2dp precision; our
// invoice_items.quantity DB column is decimal(10,3) so 2dp fits comfortably).
// Accepts both comma and dot decimal separators in the display, normalizes to
// dot internally before saving — XMLGenerator already formats Q with
// number_format($qty, 2, '.', '') for fiscal output.
function QtyNumpadModal({ product, onClose, onSave }) {
  const [display, setDisplay] = useState(String(product.quantity ?? "1").replace(".", ","));

  const append = (ch) => {
    setDisplay(prev => {
      // First digit when display is "0" replaces it (so users tapping 5 don't get "05")
      if (prev === "0") return ch;
      // Reject second decimal separator
      if ((ch === "," || ch === ".") && /[,.]/.test(prev)) return prev;
      // Cap at 2 decimals after separator
      const sepMatch = prev.match(/[,.]/);
      if (sepMatch) {
        const afterSep = prev.length - sepMatch.index - 1;
        if (afterSep >= 2 && ch !== "," && ch !== ".") return prev;
      }
      return prev + ch;
    });
  };
  const backspace = () => setDisplay(prev => prev.length <= 1 ? "0" : prev.slice(0, -1));
  const clear = () => setDisplay("0");

  const parseQty = () => {
    const normalized = display.replace(",", ".");
    const n = parseFloat(normalized);
    return isNaN(n) || n <= 0 ? 0 : n;
  };
  const handleSave = () => {
    const q = parseQty();
    if (q <= 0) return;
    onSave(q);
  };

  const valid = parseQty() > 0;

  // Numpad layout: 7 8 9 / 4 5 6 / 1 2 3 / , 0 ⌫
  const keys = [
    ["7", "8", "9"],
    ["4", "5", "6"],
    ["1", "2", "3"],
    [",", "0", "⌫"],
  ];

  return (
    <Modal onClose={onClose} title="Količina">
      <div style={{ marginBottom: 8, fontSize: 13, color: M.muted }}>
        {product.name}{product.unit ? ` (${product.unit})` : ""}
      </div>
      <div style={{ background: "#f9fafb", borderRadius: 10, padding: "18px 20px", textAlign: "right", fontSize: 36, fontWeight: 700, fontFamily: M.font, color: valid ? M.dark : "#9ca3af", marginBottom: 14, minHeight: 60 }}>
        {display}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {keys.flat().map(k => (
          <button
            key={k}
            onClick={() => k === "⌫" ? backspace() : append(k)}
            style={{ padding: "16px 0", fontSize: 22, fontWeight: 600, background: "white", border: `1px solid ${M.border}`, borderRadius: 10, cursor: "pointer", color: M.dark, fontFamily: M.font }}
          >{k}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button onClick={clear} style={{ ...M.btn("#f3f4f6", M.muted), flex: 1, justifyContent: "center" }}>Briši</button>
        <button onClick={handleSave} disabled={!valid} style={{ ...M.btn(M.green), flex: 2, justifyContent: "center", opacity: valid ? 1 : 0.5 }}>
          <Save size={15} />Sačuvaj
        </button>
      </div>
    </Modal>
  );
}

function Badge({ status }) {
  const colors = { fiscalized: { bg: "#dcfce7", c: M.green }, draft: { bg: "#fef3c7", c: "#92400e" }, storno: { bg: "#fef2f2", c: M.red }, failed: { bg: "#fef2f2", c: M.red } };
  const s = colors[status] || colors.draft;
  return <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: s.bg, color: s.c, whiteSpace: "nowrap", flexShrink: 0 }}>{status}</span>;
}
function DiscountForm({ current, onApply, onClose }) {
  const [type, setType] = useState(current.type === "none" ? "percentage" : current.type);
  const [value, setValue] = useState(current.value || "");
  return (<div>
    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
      <button onClick={() => setType("percentage")} style={M.btn(type === "percentage" ? M.dark : "#f3f4f6", type === "percentage" ? "white" : M.muted)}>%</button>
      <button onClick={() => setType("fixed")} style={M.btn(type === "fixed" ? M.dark : "#f3f4f6", type === "fixed" ? "white" : M.muted)}>€</button>
    </div>
    <input type="number" step="0.01" value={value} onChange={e => setValue(e.target.value)} placeholder={type === "percentage" ? "Procenat..." : "Iznos..."} style={M.input} autoFocus />
    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
      <button onClick={() => onApply({ type: "none", value: 0 })} style={{ ...M.btn("#fef2f2", M.red), flex: 1, justifyContent: "center" }}>Ukloni</button>
      <button onClick={() => onApply({ type, value: parseFloat(value) || 0 })} style={{ ...M.btn(M.green), flex: 1, justifyContent: "center" }}>Primijeni</button>
    </div>
  </div>);
}
function StornoForm({ onStorno, onClose }) {
  const [reason, setReason] = useState("");
  const reasons = [{ id: "STORNO_OP", l: "Greška operatera" }, { id: "STORNO_BUYER", l: "Zahtjev kupca" }, { id: "STORNO_RETURN", l: "Povrat robe" }];
  return (<div>
    <p style={{ fontSize: 13, color: M.muted, marginBottom: 10 }}>Izaberite razlog storniranja:</p>
    {reasons.map(r => (
      <label key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, marginBottom: 6, cursor: "pointer", background: reason === r.id ? "#f0fdf4" : "#f9fafb", border: `1px solid ${reason === r.id ? M.green : M.border}` }}>
        <input type="radio" name="reason" checked={reason === r.id} onChange={() => setReason(r.id)} style={{ accentColor: M.green, width: 18, height: 18 }} />
        <span style={{ fontSize: 14, fontWeight: 500 }}>{r.l}</span>
      </label>
    ))}
    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
      <button onClick={onClose} style={{ ...M.btn("#f3f4f6", M.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
      <button onClick={() => reason && onStorno(reason)} disabled={!reason} style={{ ...M.btn(M.red), flex: 1, justifyContent: "center", opacity: reason ? 1 : 0.5 }}>Storniraj</button>
    </div>
  </div>);
}
function EmailForm({ onSend, onClose }) {
  const [email, setEmail] = useState("");
  return (<div>
    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@primjer.com" style={M.input} autoFocus />
    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
      <button onClick={onClose} style={{ ...M.btn("#f3f4f6", M.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
      <button onClick={() => onSend(email)} style={{ ...M.btn(M.blue), flex: 1, justifyContent: "center" }}><Send size={14} />Pošalji</button>
    </div>
  </div>);
}

// ═══════════════════════════════════════════════════
// LOGIN — Mobile optimized
// ═══════════════════════════════════════════════════
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false); const [error, setError] = useState("");
  const handleSubmit = async () => {
    setLoading(true); setError("");
    const data = await api("/auth/login", { method: "POST", body: JSON.stringify({ email, password, app: "minipos" }) });
    setLoading(false);
    if (data.success) { localStorage.removeItem("minipos_tcr"); onLogin(data.data.token, data.data.operator); } else setError(data.message || "Greška");
  };
  return (
    <div style={{ position: "fixed", inset: 0, minHeight: "100dvh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #0a0a14 0%, #0d1525 40%, #0f3460 100%)", fontFamily: M.font, overflowY: "auto", paddingTop: "calc(24px + env(safe-area-inset-top))", paddingBottom: "calc(24px + env(safe-area-inset-bottom))", paddingLeft: "env(safe-area-inset-left)", paddingRight: "env(safe-area-inset-right)", boxSizing: "border-box" }}>
      <img src="/efis-logo.png" alt="EFIS.PRO" style={{ width: 160, marginBottom: 8 }} onError={e => { e.target.style.display = "none"; }} />
      <div style={{ fontSize: 11, color: "rgba(59,130,246,0.5)", letterSpacing: 3, marginBottom: 30, textTransform: "uppercase" }}>MINI POS</div>
      {error && <div style={{ width: 280, background: "#fef2f2", color: M.red, padding: 10, borderRadius: 10, fontSize: 13, marginBottom: 12, textAlign: "center" }}>{error}</div>}
      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" style={{ width: 280, padding: "12px 14px", marginBottom: 10, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "white", borderRadius: 10, fontSize: 15, fontFamily: M.font, outline: "none", boxSizing: "border-box" }} />
      <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Lozinka" onKeyDown={e => e.key === "Enter" && handleSubmit()} style={{ width: 280, padding: "12px 14px", marginBottom: 16, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "white", borderRadius: 10, fontSize: 15, fontFamily: M.font, outline: "none", boxSizing: "border-box" }} />
      <button onClick={handleSubmit} disabled={loading} style={{ width: 280, padding: "14px 0", background: M.blue, color: "white", border: "none", borderRadius: 10, fontSize: 16, fontWeight: 600, fontFamily: M.font, cursor: "pointer", textAlign: "center", boxSizing: "border-box" }}>{loading ? "..." : "Prijavi se"}</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// MAIN SHELL — Max 480px, bottom tabs
// ═══════════════════════════════════════════════════
function MiniPOSApp({ user, onLogout }) {
  const [view, setView] = useState("home");
  const [toast, setToast] = useState(null);
  const showToast = (message, type = "success") => { setToast({ message, type }); setTimeout(() => setToast(null), 4000); };
  const [showMore, setShowMore] = useState(false);
  const [online, setOnline] = useState(navigator.onLine !== false);
  const [syncing, setSyncing] = useState(false);

  // Online/offline detection + auto-sync
  const wasOfflineRef = useRef(false);
  const onlineRef = useRef(true);

  // Auto-select first TCR on fresh login if none stored
  useEffect(() => {
    const storedTcr = localStorage.getItem("minipos_tcr");
    if (!storedTcr) {
      (async () => {
        const cfgData = await api("/auth/company-config");
        if (cfgData.success) {
          const allTcrs = cfgData.data.all_tcrs || [];
          if (allTcrs.length > 0) {
            localStorage.setItem("minipos_tcr", allTcrs[0].tcr_code);
          }
        }
      })();
    }
  }, []);

  const syncDrafts = async () => {
    const drafts = getOfflineDrafts();
    if (drafts.length === 0) return;
    setSyncing(true);
    let synced = 0;
    for (const draft of drafts) {
      try {
        const d = await api("/invoices", { method: "POST", body: JSON.stringify(draft.payload) });
        if (d.success) { removeOfflineDraft(draft.id); synced++; }
      } catch(e) {}
    }
    setSyncing(false);
    const remaining = getOfflineDrafts().length;
    if (synced > 0) showToast(`Ponovo online! ${synced} draft račun${synced > 1 ? "a" : ""} sačuvan${synced > 1 ? "o" : ""} u bazi.`, "success");
    if (remaining > 0) showToast(`${remaining} draft nije uspjelo sinhronizovati.`, "error");
  };

  useEffect(() => {
    const goOnline = () => { setOnline(true); onlineRef.current = true; };
    const goOffline = () => { setOnline(false); onlineRef.current = false; wasOfflineRef.current = true; showToast("Offline mod - podaci iz keša", "error"); };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    // Polling: try fetching products every 8s to check connectivity
    const poll = setInterval(async () => {
      const token = localStorage.getItem("minipos_token");
      if (!token) return;
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 4000);
        const r = await fetch(`${API_BASE}/products`, {
          headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
          signal: controller.signal
        });
        clearTimeout(timeout);
        // If we get any response (even error JSON), server is reachable
        if (r.status !== 0) {
          if (!onlineRef.current || wasOfflineRef.current) {
            setOnline(true);
            onlineRef.current = true;
            if (wasOfflineRef.current) {
              wasOfflineRef.current = false;
              const drafts = getOfflineDrafts();
              if (drafts.length > 0) {
                showToast(`Ponovo online! Sinhronizujem ${drafts.length} draft${drafts.length > 1 ? "ova" : ""}...`, "success");
                setTimeout(syncDrafts, 1000);
              } else {
                showToast("Ponovo online!", "success");
              }
            }
          }
        }
      } catch(e) {
        // Network error = offline
        if (onlineRef.current) {
          setOnline(false);
          onlineRef.current = false;
          wasOfflineRef.current = true;
          showToast("Offline mod - podaci iz keša", "error");
        }
      }
    }, 8000);

    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); clearInterval(poll); };
  }, []);

  const tabs = [
    { id: "home", icon: Home, label: "Početna" },
    { id: "pos", icon: LayoutGrid, label: "Prodaja" },
    { id: "invoices", icon: FileText, label: "Računi" },
    { id: "blagajna", icon: Wallet, label: "Kasa" },
    { id: "more", icon: MoreVertical, label: "Više" },
  ];
  const moreItems = [
    { id: "virman", icon: Building, label: "Virman" },
    { id: "dashboard", icon: BarChart3, label: "Izvještaji" },
    { id: "settings", icon: Settings, label: "Podešavanja" },
  ];

  return (
    <div style={{ display: "flex", justifyContent: "center", height: "100dvh", background: "#e5e7eb", fontFamily: M.font, position: "fixed", inset: 0, overflow: "hidden", paddingTop: "env(safe-area-inset-top)" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />
      {(() => { if (!document.querySelector('meta[name="viewport"]')) { const m = document.createElement('meta'); m.name = 'viewport'; m.content = 'width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no'; document.head.appendChild(m); } else { document.querySelector('meta[name="viewport"]').content = 'width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no'; } return null; })()}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}.spin{animation:spin 1s linear infinite}@keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}@keyframes floatUp{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}@keyframes logoGlow{0%,100%{filter:drop-shadow(0 0 15px rgba(59,130,246,0.3))}50%{filter:drop-shadow(0 0 25px rgba(59,130,246,0.5))}}@keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}*{box-sizing:border-box;-webkit-tap-highlight-color:transparent;touch-action:manipulation}input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none}::-webkit-scrollbar{width:0;height:0}html{position:fixed;width:100%;height:100%;overflow:hidden;touch-action:manipulation}body{position:fixed;width:100%;height:100%;overflow:hidden;overscroll-behavior:none;-webkit-overflow-scrolling:auto;margin:0;touch-action:manipulation}`}</style>

      {/* Phone frame */}
      <div style={{ width: "100%", maxWidth: 480, height: "100%", display: "flex", flexDirection: "column", background: M.bg, position: "relative", overflow: "hidden", boxShadow: "0 0 40px rgba(0,0,0,0.1)" }}>

        {/* Safe area spacer for iOS notch */}
        {(() => {
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
          const hasNotch = isIOS && window.screen.height >= 812;
          const topPad = hasNotch ? 50 : (isIOS ? 20 : 0);
          const bottomPad = hasNotch ? 34 : 0;
          if (topPad > 0) return <div style={{ height: topPad, background: "#ffffff", flexShrink: 0 }} />;
          return null;
        })()}
        <style>{`*{-webkit-tap-highlight-color:transparent;-webkit-touch-callout:none;box-sizing:border-box}html,body,#root{height:100%;margin:0;padding:0}`}</style>

        {/* Offline indicator */}
        {(!online || syncing) && <div style={{ background: !online ? "#ef4444" : M.blue, color: "white", textAlign: "center", padding: "4px 12px", fontSize: 11, fontWeight: 600, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
          {syncing ? <><RefreshCw size={11} className="spin" />Sinhronizacija draft računa...</> : <><WifiOff size={11} />Offline — podaci iz keša</>}
        </div>}

        {/* Desktop notice - only visible on wide screens */}
        <div style={{ background: "#1e293b", color: "#94a3b8", textAlign: "center", padding: "6px 12px", fontSize: 11, display: "none" }} id="desktop-notice">
          <Smartphone size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />Ova aplikacija je dizajnirana za male ekrane
        </div>
        <style>{`@media(min-width:481px){#desktop-notice{display:block!important}}`}</style>

        {/* Content */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          {view === "home" && <HomeView user={user} onNavigate={v => setView(v)} />}
          {view === "pos" && <MiniPOSView user={user} showToast={showToast} online={online} />}
          {view === "invoices" && <InvoicesView user={user} showToast={showToast} />}
          {view === "blagajna" && <BlagajnaView user={user} showToast={showToast} />}
          {view === "virman" && <VirmanView user={user} showToast={showToast} online={online} />}
          {view === "dashboard" && <DashboardView user={user} showToast={showToast} />}
          {view === "settings" && <SettingsView user={user} showToast={showToast} />}
        </div>

        {/* Bottom tab bar */}
        <div style={{ background: "white", borderTop: `1px solid ${M.border}`, display: "flex", flexShrink: 0, position: "relative", paddingBottom: "max(8px, env(safe-area-inset-bottom))" }}>
          {tabs.map(t => {
            const active = t.id === "more" ? ["virman","dashboard","settings"].includes(view) : view === t.id;
            return (
              <button key={t.id} onClick={() => { if (t.id === "more") setShowMore(!showMore); else { setView(t.id); setShowMore(false); } }}
                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, background: "none", border: "none", cursor: "pointer", color: active ? M.blue : "#9ca3af", fontFamily: M.font, fontSize: 11, fontWeight: 600, padding: "10px 0", height: 62, WebkitTapHighlightColor: "transparent" }}>
                <t.icon size={26} strokeWidth={active ? 2.5 : 2} />
                <span>{t.label}</span>
              </button>
            );
          })}
          {showMore && <>
            <div onClick={() => setShowMore(false)} style={{ position: "fixed", inset: 0, zIndex: 800 }} />
            <div style={{ position: "absolute", bottom: 62, right: 4, background: "white", borderRadius: 14, boxShadow: "0 8px 30px rgba(0,0,0,0.15)", border: `1px solid ${M.border}`, zIndex: 850, minWidth: 180, padding: 6, animation: "slideUp 0.15s ease" }}>
              {moreItems.map(m => (
                <button key={m.id} onClick={() => { setView(m.id); setShowMore(false); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", border: "none", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 500, background: view === m.id ? "#eff6ff" : "transparent", color: view === m.id ? M.blue : M.dark, fontFamily: M.font }}>
                  <m.icon size={18} />{m.label}
                </button>
              ))}
              <div style={{ borderTop: `1px solid ${M.border}`, margin: "4px 0" }} />
              <button onClick={onLogout} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", border: "none", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 500, background: "transparent", color: M.red, fontFamily: M.font }}><LogOut size={18} />Odjavi se</button>
              <div style={{ padding: "4px 14px 6px", fontSize: 11, color: M.muted }}>{user?.name || ""}</div>
            </div>
          </>}
        </div>

        {toast && <div style={{ position: "absolute", bottom: 68, left: 12, right: 12, padding: "10px 14px", borderRadius: 12, background: toast.type === "success" ? M.green : toast.type === "error" ? M.red : M.dark, color: "white", fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 6, boxShadow: "0 4px 20px rgba(0,0,0,0.2)", zIndex: 9999, animation: "slideUp 0.3s ease", textAlign: "center", justifyContent: "center" }}>{toast.type === "success" ? <Check size={14} /> : <AlertCircle size={14} />}{toast.message}</div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// HOME — Compact landing
// ═══════════════════════════════════════════════════
function HomeView({ user, onNavigate }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const iv = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(iv); }, []);
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(160deg, #0a0a14 0%, #0f1d35 100%)", padding: "0 24px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", border: "1px solid rgba(59,130,246,0.05)", top: "50%", left: "50%", marginTop: -150, marginLeft: -150, animation: "spin 80s linear infinite", pointerEvents: "none" }} />
      <div style={{ animation: "floatUp 4s ease-in-out infinite, logoGlow 3s ease-in-out infinite", marginBottom: 8, pointerEvents: "none" }}>
        <img src="/efis-logo.png" alt="EFIS.PRO" style={{ width: "min(220px, 55vw)", height: "auto" }} onError={e => { e.target.style.display = "none"; }} />
      </div>
      <p style={{ color: "rgba(59,130,246,0.4)", fontSize: 10, letterSpacing: 3, textTransform: "uppercase", margin: "0 0 20px", pointerEvents: "none" }}>miniPOS • Crna Gora</p>
      <div style={{ color: "white", fontSize: 38, fontWeight: 200, fontFamily: M.mono, letterSpacing: 3, background: "linear-gradient(90deg, #60a5fa, #a78bfa, #60a5fa)", backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "shimmer 4s linear infinite", pointerEvents: "none" }}>
        {time.toLocaleTimeString("sr-Latn-ME", { hour: "2-digit", minute: "2-digit" })}
      </div>
      <div style={{ color: "rgba(255,255,255,0.25)", fontSize: 12, marginTop: 4, pointerEvents: "none" }}>{time.toLocaleDateString("sr-Latn-ME", { weekday: "short", day: "numeric", month: "short" })}</div>
      <div style={{ display: "flex", gap: 12, marginTop: 28, position: "relative", zIndex: 10 }}>
        {[{ id: "pos", icon: LayoutGrid, label: "Prodaja", c: "#3b82f6" }, { id: "invoices", icon: FileText, label: "Računi", c: "#d97706" }].map(b => (
          <button key={b.id} onClick={() => onNavigate(b.id)} style={{ padding: "16px 32px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 14, color: "rgba(255,255,255,0.7)", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 600, fontFamily: M.font, WebkitTapHighlightColor: "rgba(59,130,246,0.3)", touchAction: "manipulation" }}><b.icon size={18} />{b.label}</button>
        ))}
      </div>
      <div style={{ position: "absolute", bottom: 14, color: "rgba(255,255,255,0.3)", fontSize: 12, fontWeight: 500 }}>{user?.company_name || ""}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// MINI POS VIEW — Vertical split cart/products
// ═══════════════════════════════════════════════════
function MiniPOSView({ user, showToast, online }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("brzi");
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [fiscalizing, setFiscalizing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  const [invoiceDiscount, setInvoiceDiscount] = useState({ type: "none", value: 0 });
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [lastFiscalized, setLastFiscalized] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPayPanel, setShowPayPanel] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  // qtyModal holds the cart line ref when the user taps a decimal-unit
  // product's quantity button. Cleared on save/cancel. Mounted at the bottom
  // of this view alongside the other modals.
  const [qtyModal, setQtyModal] = useState(null);

  useEffect(() => { loadProducts(); loadCategories(); }, []);
  const loadProducts = async () => { setLoading(true); const d = await api("/products"); if (d.success) setProducts(d.data?.products || []); setLoading(false); };
  const loadCategories = async () => { const d = await api("/invoices/categories"); if (d.success) setCategories(d.data?.categories || []); };
  // is_decimal_unit propagates from product → cart line. parseInt() === 1 keeps
  // the same defensive check used in POS app (handles "1"/"0"/true/false from
  // both API and offline cache). Existing cart line always increments by +1
  // when re-tapping the product card; decimal entry uses the qty button +
  // numpad in the cart line itself.
  const addToCart = (p) => { setCart(prev => { const ex = prev.find(c => c.id === p.id); if (ex) return prev.map(c => c.id === p.id ? { ...c, quantity: parseFloat((c.quantity + 1).toFixed(2)) } : c); return [...prev, { id: p.id, name: p.name, price: parseFloat(p.price), quantity: 1, vat_rate: parseFloat(p.vat_rate ?? 21), unit: p.unit || "kom", is_decimal_unit: parseInt(p.is_decimal_unit) === 1 ? 1 : 0, discount_type: "none", discount_value: 0, custom_price: null }]; }); };
  const updateCartItem = (id, u) => setCart(prev => prev.map(c => c.id === id ? { ...c, ...u } : c));
  const removeFromCart = (id) => setCart(prev => prev.filter(c => c.id !== id));
  const clearCart = () => { setCart([]); setInvoiceDiscount({ type: "none", value: 0 }); setPaymentMethod("cash"); setShowPayPanel(false); };
  const fastMenuIds = useMemo(() => { try { return JSON.parse(localStorage.getItem("minipos_fast_menu") || "[]").map(Number); } catch(e) { return []; } }, [products]);
  const cartCalc = useMemo(() => {
    let netTotal = 0, totalItemDiscount = 0;
    cart.forEach(item => { const up = item.custom_price !== null ? item.custom_price : item.price; const lineNet = item.quantity * up; let disc = 0; if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100); else if (item.discount_type === "fixed") disc = item.discount_value; totalItemDiscount += disc; netTotal += lineNet - disc; });
    let invDisc = 0; if (invoiceDiscount.type === "percentage") invDisc = netTotal * (invoiceDiscount.value / 100); else if (invoiceDiscount.type === "fixed") invDisc = invoiceDiscount.value;
    const subtotal = Math.max(0, netTotal - invDisc);
    let vatAmount = 0; const vatGroups = {};
    cart.forEach(item => { const up = item.custom_price !== null ? item.custom_price : item.price; const ln = item.quantity * up; let d = 0; if (item.discount_type === "percentage") d = ln * (item.discount_value / 100); else if (item.discount_type === "fixed") d = item.discount_value || 0; const af = ln - d; const r = parseFloat(item.vat_rate ?? 21); const v = af * (r / 100); vatAmount += v; if (!vatGroups[r]) vatGroups[r] = { base: 0, vat: 0 }; vatGroups[r].base += af; vatGroups[r].vat += v; });
    if (invDisc > 0 && netTotal > 0) { const ratio = (netTotal - invDisc) / netTotal; vatAmount *= ratio; }
    return { netTotal, totalItemDiscount, invDisc, subtotal, vatAmount, total: subtotal + vatAmount, vatGroups };
  }, [cart, invoiceDiscount]);
  const buildPayload = (fiscNow) => ({ location_id: user?.location_id || 1, tcr_code: getTcr(), payment_method: paymentMethod, fiscalize_now: fiscNow, discount_type: invoiceDiscount.type, discount_value: invoiceDiscount.value, ...(paymentMethod === "split" && { split_cash: parseFloat(splitCash) || 0, split_card: parseFloat(splitCard) || 0 }), items: cart.map(i => ({ product_id: i.id, name: i.name, quantity: i.quantity, unit_price: i.custom_price !== null ? i.custom_price : i.price, vat_rate: i.vat_rate, unit: i.unit, discount_type: i.discount_type, discount_value: i.discount_value })) });
  const handleFiscalize = async () => {
    if (!online) { const cnt = saveOfflineDraft(buildPayload(false)); clearCart(); showToast(`Offline! Sačuvano lokalno kao draft (${cnt}). Fiskalizacija kad se povežete.`, "error"); setShowConfirm(false); return; }
    // Check internal day
    const idCheck = await api("/internalday/settings");
    if (idCheck.success && idCheck.data?.internal_day_enabled) {
      const idActive = await api(`/internalday/active/${getTcr()}`);
      if (!idActive.success || !idActive.data?.has_active_day) {
        showToast("Otvorite interni dan prije izdavanja racuna! (Kasa → Otvaranja)", "error");
        setShowConfirm(false); return;
      }
    }
    setShowConfirm(false); setFiscalizing(true); const d = await api("/invoices", { method: "POST", body: JSON.stringify(buildPayload(true)) }); setFiscalizing(false); if (d.success) { setLastFiscalized({ fic: d.data.fic, invoice: d.data.invoice, total: cartCalc.total, verification_url: d.data.verification_url }); clearCart(); showToast("Fiskalizovano!"); if (d.data?.invoice?.id) { printReceipt(d.data.invoice.id); } } else { if (d._offline) { const cnt = saveOfflineDraft(buildPayload(false)); clearCart(); showToast(`Nema mreže! Draft sačuvan lokalno (${cnt}). Sinhronizacija kad se povežete.`, "error"); } else showToast(d.message || "Greška", "error"); }
  };
  const handleSaveDraft = async () => {
    if (!online) { const cnt = saveOfflineDraft(buildPayload(false)); clearCart(); showToast(`Offline! Draft sačuvan lokalno (${cnt})`, "success"); return; }
    setSaving(true); const d = await api("/invoices", { method: "POST", body: JSON.stringify(buildPayload(false)) }); setSaving(false); if (d.success) { clearCart(); showToast("Draft sačuvan"); if (d.data?.invoice?.id) { printReceipt(d.data.invoice.id); } } else { if (d._offline) { const cnt = saveOfflineDraft(buildPayload(false)); clearCart(); showToast(`Nema mreže! Draft sačuvan lokalno (${cnt})`, "error"); } else showToast(d.message || "Greška", "error"); }
  };
  const filtered = useMemo(() => { let list = products; if (activeCategory === "brzi") list = products.filter(p => fastMenuIds.includes(Number(p.id))); else if (activeCategory !== "all") list = products.filter(p => String(p.category_id) === String(activeCategory)); if (search) { const q = search.toLowerCase(); list = list.filter(p => p.name.toLowerCase().includes(q)); } return list; }, [products, activeCategory, search, fastMenuIds]);

  if (lastFiscalized) return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, background: "white" }}>
      <div style={{ width: 52, height: 52, background: "#dcfce7", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}><Check size={26} color={M.green} /></div>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Fiskalizovano!</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: M.green, marginBottom: 6 }}>{fmt(lastFiscalized.total)} €</div>
      <div style={{ fontSize: 10, color: M.muted, fontFamily: M.mono, wordBreak: "break-all", textAlign: "center", maxWidth: 260, marginBottom: 8 }}>{lastFiscalized.fic}</div>
      <div style={{ fontSize: 12, color: M.muted, marginBottom: 20 }}>{lastFiscalized.invoice?.invoice_number}</div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={async () => { const id = lastFiscalized.invoice?.id; if (id) await printReceipt(id, true); }} style={M.btnSm("#f3f4f6", M.muted)}><Printer size={13} />Print</button>
        <button onClick={() => { const id = lastFiscalized.invoice?.id; if (id) openPdf(`/invoices/pdf/${id}?type=a4`); }} style={M.btnSm("#f3f4f6", M.muted)}><Download size={13} />PDF</button>
        <button onClick={() => setLastFiscalized(null)} style={M.btnSm(M.green, "white")}><Plus size={13} />Novi</button>
      </div>
    </div>
  );

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* TOP — Cart (dynamic: small when empty, grows with items, max 45%) */}
      <div style={{ flex: cart.length === 0 ? "0 0 auto" : "0 1 45%", maxHeight: cart.length === 0 ? "auto" : "45%", display: "flex", flexDirection: "column", background: "white", borderBottom: `2px solid ${M.border}` }}>
        <div style={{ flex: 1, overflow: "auto", padding: "2px 8px", WebkitOverflowScrolling: "touch" }}>
          {cart.length === 0 ? <div style={{ textAlign: "center", padding: "10px 16px", color: "#bbb", fontSize: 12 }}><ShoppingCart size={20} strokeWidth={1.5} style={{ verticalAlign: "middle", marginRight: 4 }} />Prazna korpa</div>
          : cart.map(item => { const up = item.custom_price !== null ? item.custom_price : item.price; let disc = 0; if (item.discount_type === "percentage") disc = (item.quantity * up) * (item.discount_value / 100); else if (item.discount_type === "fixed") disc = item.discount_value; const lineGross = ((item.quantity * up) - disc) * (1 + item.vat_rate / 100); return (
            <div key={item.id} style={{ padding: "4px 0", borderBottom: "1px solid #f5f5f5" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <div onClick={() => setEditingItem(editingItem === item.id ? null : item.id)} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}><div style={{ fontWeight: 600, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div><div style={{ fontSize: 10, color: "#999" }}>{fmt(up)} × {fmtQty(item.quantity)}{disc > 0 ? ` -${fmt(disc)}` : ""}</div></div>
                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {/* Minus: removes line when at minimum (1 for integer, 0.01 for decimal),
                      otherwise decrements by 1 (integer) or 0.01 (decimal). The toFixed(2)
                      guards against 0.1+0.2-style float drift. */}
                  <button onClick={() => {
                    const step = item.is_decimal_unit ? 0.01 : 1;
                    const minQ = step;
                    if (item.quantity <= minQ + 0.0001) { removeFromCart(item.id); return; }
                    updateCartItem(item.id, { quantity: parseFloat((item.quantity - step).toFixed(2)) });
                  }} style={{ width: 42, height: 42, border: `1px solid ${M.border}`, borderRadius: 10, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>{item.quantity <= (item.is_decimal_unit ? 0.01 : 1) + 0.0001 ? <Trash2 size={16} color={M.red} /> : <Minus size={16} />}</button>
                  {item.is_decimal_unit ? (
                    // Tappable qty for decimal-unit products → opens numpad. Dashed border
                    // hints that it's interactive; matches the POS app pattern.
                    <button onClick={() => setQtyModal(item)} style={{ fontSize: 13, fontWeight: 600, minWidth: 42, textAlign: "center", padding: "4px 6px", border: `1px dashed ${M.blue}`, borderRadius: 6, background: "white", cursor: "pointer", color: M.dark }} title="Unesi tačnu količinu">{fmtQty(item.quantity)}</button>
                  ) : (
                    <span style={{ fontSize: 13, fontWeight: 600, minWidth: 18, textAlign: "center" }}>{item.quantity}</span>
                  )}
                  <button onClick={() => updateCartItem(item.id, { quantity: parseFloat((item.quantity + (item.is_decimal_unit ? 0.01 : 1)).toFixed(2)) })} style={{ width: 42, height: 42, border: `1px solid ${M.border}`, borderRadius: 10, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={16} /></button>
                </div>
                <div style={{ fontWeight: 700, fontSize: 12, minWidth: 46, textAlign: "right" }}>{fmt(lineGross)}€</div>
              </div>
              {editingItem === item.id && <div style={{ padding: "4px 0", display: "flex", gap: 4, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 65px" }}><label style={{ fontSize: 9, color: M.muted }}>Cijena</label><input type="number" step="0.01" value={item.custom_price !== null ? item.custom_price : item.price} onChange={e => updateCartItem(item.id, { custom_price: parseFloat(e.target.value) || 0 })} style={{ ...M.input, padding: "4px 6px", fontSize: 12 }} /></div>
                {/* Inline qty edit. step + parse path branches on is_decimal_unit so accidental
                    "0.5" entry on integer products still snaps to int. Comma-decimal entry
                    (Montenegrin locale) normalized via String().replace before parseFloat. */}
                <div style={{ flex: "1 1 50px" }}><label style={{ fontSize: 9, color: M.muted }}>Kol.</label><input type="number" step={item.is_decimal_unit ? "0.01" : "1"} value={item.quantity} onChange={e => updateCartItem(item.id, { quantity: item.is_decimal_unit ? Math.max(0.01, parseFloat(String(e.target.value).replace(",", ".")) || 1) : Math.max(1, parseInt(e.target.value) || 1) })} style={{ ...M.input, padding: "4px 6px", fontSize: 12 }} /></div>
                <div style={{ flex: "1 1 85px" }}><label style={{ fontSize: 9, color: M.muted }}>Popust</label><div style={{ display: "flex", gap: 2 }}><select value={item.discount_type} onChange={e => updateCartItem(item.id, { discount_type: e.target.value, discount_value: 0 })} style={{ ...M.input, padding: "3px", fontSize: 11, flex: 1 }}><option value="none">-</option><option value="percentage">%</option><option value="fixed">€</option></select>{item.discount_type !== "none" && <input type="number" step="0.01" value={item.discount_value} onChange={e => updateCartItem(item.id, { discount_value: parseFloat(e.target.value) || 0 })} style={{ ...M.input, padding: "3px", fontSize: 11, width: 42 }} />}</div></div>
              </div>}
            </div>); })}
        </div>
        {cart.length > 0 && <div style={{ padding: "5px 10px", borderTop: `1px solid ${M.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ fontWeight: 700, fontSize: 16 }}>{fmt(cartCalc.total)} €</span><span style={{ background: "#f3f4f6", padding: "1px 6px", borderRadius: 4, fontSize: 11, fontWeight: 600, color: M.muted }}>{cart.length}</span></div>
          <div style={{ display: "flex", gap: 4 }}>
            <button onClick={clearCart} style={{ ...M.btnSm("#fef2f2", M.red), padding: "3px 7px" }}><Trash2 size={11} /></button>
            <button onClick={() => setShowPayPanel(true)} style={{ ...M.btnSm(M.green, "white"), padding: "3px 10px" }}><Zap size={12} />Plati</button>
          </div>
        </div>}
      </div>
      {/* BOTTOM — Products (fills remaining space, min 55%) */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "55%" }}>
        <div style={{ padding: "4px 8px", background: "white", display: "flex", gap: 4, borderBottom: `1px solid ${M.border}`, flexShrink: 0 }}>
          <div style={{ flex: 1, position: "relative" }}><Search size={13} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#bbb" }} /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretraži..." style={{ ...M.input, paddingLeft: 26, padding: "5px 8px 5px 26px", fontSize: 13, borderRadius: 8 }} /></div>
          <button onClick={loadProducts} style={{ padding: "7px 8px", background: "#f3f4f6", border: `1px solid ${M.border}`, borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><RefreshCw size={14} color={M.muted} /></button>
        </div>
        <div style={{ padding: "3px 8px", background: "white", display: "flex", gap: 4, overflowX: "auto", borderBottom: `1px solid ${M.border}`, flexShrink: 0 }}>
          <CatTab active={activeCategory === "brzi"} onClick={() => setActiveCategory("brzi")} label="⚡Brzi" />
          <CatTab active={activeCategory === "all"} onClick={() => setActiveCategory("all")} label="Sve" />
          {categories.map(c => <CatTab key={c.id} active={String(activeCategory) === String(c.id)} onClick={() => setActiveCategory(c.id)} label={c.name} />)}
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 5, background: M.bg }}>
          {loading ? <div style={{ textAlign: "center", padding: 24 }}><Loader2 size={22} className="spin" color="#bbb" /></div>
          : filtered.length === 0 ? <div style={{ textAlign: "center", padding: 24, color: "#bbb", fontSize: 12 }}><Package size={24} /><p style={{ marginTop: 4 }}>Nema proizvoda</p></div>
          : <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
            {filtered.map(p => { const inCart = cart.find(c => c.id === p.id); const gross = parseFloat(p.price) * (1 + parseFloat(p.vat_rate ?? 21) / 100); return (
              <button key={p.id} onClick={() => addToCart(p)} style={{ position: "relative", padding: "10px 6px", background: inCart ? "#f0fdf4" : "white", border: inCart ? `2px solid ${M.green}` : `1px solid ${M.border}`, borderRadius: 10, cursor: "pointer", textAlign: "center", fontFamily: M.font, height: 72, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                {inCart && <div style={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, background: M.green, borderRadius: "50%", color: "white", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>{fmtQty(inCart.quantity)}</div>}
                <div style={{ fontWeight: 600, fontSize: 11, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", marginBottom: 2, width: "100%", wordBreak: "break-word" }}>{p.name}</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: M.green, flexShrink: 0 }}>{fmt(gross)}€</div>
              </button>); })}
          </div>}
        </div>
      </div>
      {/* Payment panel */}
      {showPayPanel && <>
        <div onClick={() => setShowPayPanel(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 900 }} />
        <div style={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 950, padding: 16, pointerEvents: "none" }}>
        <div style={{ maxWidth: 440, width: "100%", background: "white", borderRadius: 20, padding: "16px 16px", maxHeight: "85vh", overflow: "auto", boxShadow: "0 8px 30px rgba(0,0,0,0.15)", pointerEvents: "auto" }}>
          <div style={{ width: 32, height: 3, background: "#ddd", borderRadius: 2, margin: "0 auto 10px" }} />
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${M.border}`, marginBottom: 10 }}><span style={{ fontWeight: 700, fontSize: 18 }}>Ukupno</span><span style={{ fontWeight: 700, fontSize: 22, color: M.green }}>{fmt(cartCalc.total)} €</span></div>
          <div style={{ fontSize: 12, color: M.muted, marginBottom: 8 }}>Neto: {fmt(cartCalc.subtotal)}€ + PDV: {fmt(cartCalc.vatAmount)}€{cartCalc.invDisc > 0 ? ` - Popust: ${fmt(cartCalc.invDisc)}€` : ""}</div>
          <button onClick={() => setShowDiscountModal(true)} style={{ ...M.btnSm(invoiceDiscount.type !== "none" ? "#fef3c7" : "#f3f4f6", invoiceDiscount.type !== "none" ? "#92400e" : M.muted), width: "100%", justifyContent: "center", marginBottom: 10 }}><Percent size={12} />{invoiceDiscount.type !== "none" ? `Popust: ${invoiceDiscount.value}${invoiceDiscount.type === "percentage" ? "%" : "€"}` : "Dodaj popust"}</button>
          <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
            {[{ id: "cash", l: "Gotovina", i: Banknote }, { id: "card", l: "Kartica", i: CreditCard }, { id: "split", l: "Podijeli", i: Wallet }].map(m => (
              <button key={m.id} onClick={() => setPaymentMethod(m.id)} style={{ padding: "10px 8px", border: "2px solid", borderColor: paymentMethod === m.id ? M.green : M.border, background: paymentMethod === m.id ? "#f0fdf4" : "white", color: paymentMethod === m.id ? M.green : M.muted, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: M.font, minHeight: 44 }}><m.i size={16} />{m.l}</button>))}
          </div>
          {paymentMethod === "split" && <div style={{ display: "flex", gap: 6, marginBottom: 10 }}><div style={{ flex: 1 }}><label style={{ fontSize: 11, color: M.muted }}>Gotovina</label><input type="number" step="0.01" value={splitCash} onChange={e => { setSplitCash(e.target.value); setSplitCard(fmt(cartCalc.total - (parseFloat(e.target.value) || 0))); }} style={{ ...M.input, padding: "6px 8px" }} /></div><div style={{ flex: 1 }}><label style={{ fontSize: 11, color: M.muted }}>Kartica</label><input type="number" step="0.01" value={splitCard} onChange={e => { setSplitCard(e.target.value); setSplitCash(fmt(cartCalc.total - (parseFloat(e.target.value) || 0))); }} style={{ ...M.input, padding: "6px 8px" }} /></div></div>}
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => { setShowPayPanel(false); setShowConfirm(true); }} disabled={fiscalizing} style={{ ...M.btn(M.green), flex: 2, justifyContent: "center", padding: 14, fontSize: 17, fontWeight: 700 }}><Zap size={20} />FISKALIZUJ</button>
            <button onClick={() => { setShowPayPanel(false); handleSaveDraft(); }} style={{ ...M.btn(M.blue), flex: 1, justifyContent: "center", padding: 14, fontSize: 14 }}><Archive size={16} />Draft</button>
          </div>
        </div>
        </div>
      </>}
      {showConfirm && <Modal onClose={() => setShowConfirm(false)} title="Potvrda"><div style={{ background: "#f9fafb", borderRadius: 10, padding: 14, marginBottom: 14, textAlign: "center" }}><div style={{ fontSize: 28, fontWeight: 700, color: M.green }}>{fmt(cartCalc.total)} €</div><div style={{ fontSize: 12, color: M.muted, marginTop: 4 }}>{cart.length} stavki • {paymentMethod === "cash" ? "Gotovina" : paymentMethod === "card" ? "Kartica" : "Split"}</div></div><div style={{ display: "flex", gap: 8 }}><button onClick={() => setShowConfirm(false)} style={{ ...M.btn("#f3f4f6", M.muted), flex: 1, justifyContent: "center" }}>Otkaži</button><button onClick={handleFiscalize} style={{ ...M.btn(M.green), flex: 1, justifyContent: "center", padding: 16, fontSize: 17 }}><Zap size={20} />Potvrdi</button></div></Modal>}
      {showDiscountModal && <Modal onClose={() => setShowDiscountModal(false)} title="Popust"><DiscountForm current={invoiceDiscount} onApply={d => { setInvoiceDiscount(d); setShowDiscountModal(false); }} onClose={() => setShowDiscountModal(false)} /></Modal>}
      {qtyModal && <QtyNumpadModal product={qtyModal} onClose={() => setQtyModal(null)} onSave={qty => { updateCartItem(qtyModal.id, { quantity: qty }); setQtyModal(null); }} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// INVOICES — Mobile card list (no tables)
// ═══════════════════════════════════════════════════
function InvoicesView({ user, showToast }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pg, setPg] = useState({ total: 0, page: 1, per_page: 20, total_pages: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(() => daysAgoLocal(7));
  const [dateTo, setDateTo] = useState(() => todayLocal());
  const [detail, setDetail] = useState(null);
  const [stornoModal, setStornoModal] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [invTab, setInvTab] = useState("racuni");
  // Korekcije
  const [corrSearch, setCorrSearch] = useState("");
  const [corrInvoice, setCorrInvoice] = useState(null);
  const [corrItems, setCorrItems] = useState([]);
  const [corrPayment, setCorrPayment] = useState("cash");
  const [corrBuyerName, setCorrBuyerName] = useState("");
  const [corrBuyerTin, setCorrBuyerTin] = useState("");
  const [corrReason, setCorrReason] = useState("CORRECTIVE");
  const [corrSubmitting, setCorrSubmitting] = useState(false);
  // Avansni
  const [advMode, setAdvMode] = useState("create");
  const [advItems, setAdvItems] = useState([{ name: "", quantity: 1, unit_price: 0, vat_rate: 21 }]);
  const [advPayment, setAdvPayment] = useState("cash");
  const [advBuyerName, setAdvBuyerName] = useState("");
  const [advBuyerTin, setAdvBuyerTin] = useState("");
  const [advSubmitting, setAdvSubmitting] = useState(false);
  const [advSearch, setAdvSearch] = useState("");
  const [advInvoice, setAdvInvoice] = useState(null);
  const [advFinalItems, setAdvFinalItems] = useState([{ name: "", quantity: 1, unit_price: 0, vat_rate: 21 }]);
  const [advFinalPayment, setAdvFinalPayment] = useState("cash");

  useEffect(() => { loadInvoices(); }, [pg.page, statusFilter, dateFrom, dateTo]);
  const loadInvoices = async () => { setLoading(true); const p = new URLSearchParams({ page: pg.page, per_page: 20 }); if (statusFilter) p.set("status", statusFilter); const tcr = getTcr(); if (tcr) p.set("tcr_code", tcr); if (dateFrom) p.set("date_from", dateFrom); if (dateTo) p.set("date_to", dateTo); const d = await api(`/invoices?${p}`); if (d.success) { setInvoices(d.data.invoices || []); setPg(prev => ({ ...prev, ...d.data.pagination })); } setLoading(false); };
  const viewInvoice = async (id) => { const d = await api(`/invoices/show/${id}`); if (d.success) setDetail(d.data); };
  const fiscalizeDraft = async (id) => { const d = await api(`/invoices/fiscalize/${id}`, { method: "POST" }); if (d.success) { showToast("Fiskalizovano!"); loadInvoices(); if (detail) viewInvoice(id); } else showToast(d.message, "error"); };
  const handleStorno = async (id, reason) => { const d = await api(`/invoices/storno/${id}`, { method: "POST", body: JSON.stringify({ storno_reason: reason, fiscalize_now: true }) }); if (d.success) { showToast("Storno uspješan!"); setStornoModal(null); setDetail(null); loadInvoices(); } else showToast(d.message, "error"); };
  const handleEmail = async (id, email) => { const d = await api(`/invoices/email/${id}`, { method: "POST", body: JSON.stringify({ email }) }); if (d.success) showToast("Email poslat"); else showToast(d.message || "Greška", "error"); setEmailModal(null); };

  const loadCorrInvoice = async () => {
    if (!corrSearch.trim()) return;
    const tcr = getTcr();
    let d = await api(`/invoices?search=${encodeURIComponent(corrSearch.trim())}&per_page=5${tcr ? "&tcr_code=" + tcr : ""}`);
    let found = d.success && d.data?.invoices?.length > 0 ? d.data.invoices.find(inv => inv.status === "fiscalized" && !inv.stornoed_by_invoice_id && !inv.corrected_by_invoice_id && inv.is_storno != 1) : null;
    if (!found && d.success && d.data?.invoices?.length > 0) found = d.data.invoices[0];
    if (found) {
      const det = await api(`/invoices/show/${found.id}`);
      if (det.success) {
        setCorrInvoice(det.data);
        setCorrPayment(det.data.invoice?.payment_method || "cash");
        setCorrBuyerName(det.data.invoice?.buyer_name || "");
        setCorrBuyerTin(det.data.invoice?.buyer_pib || "");
        setCorrItems((det.data.items || []).map(it => ({ name: it.item_name || it.name || "", quantity: Math.abs(parseFloat(it.quantity || 1)), unit_price: parseFloat(it.unit_price || 0), vat_rate: parseFloat(it.vat_rate ?? 21), unit: it.unit || "kom", discount_type: it.discount_type || "none", discount_value: parseFloat(it.discount_value || 0) })));
      }
    } else showToast("Račun nije pronađen", "error");
  };
  const submitCorrection = async () => {
    if (!corrInvoice || corrItems.length === 0) return;
    setCorrSubmitting(true);
    const d = await api(`/invoices/correct/${corrInvoice.invoice.id}`, { method: "POST", body: JSON.stringify({ items: corrItems, payment_method: corrPayment, buyer_name: corrBuyerName, buyer_tin: corrBuyerTin, correction_reason: corrReason }) });
    setCorrSubmitting(false);
    if (d.success) { showToast("Korekcija uspješna!"); setCorrInvoice(null); setCorrItems([]); setCorrSearch(""); loadInvoices(); }
    else showToast(d.message || "Greška", "error");
  };
  const submitAdvance = async () => {
    if (advItems.length === 0 || advItems.every(i => !i.name)) return;
    setAdvSubmitting(true);
    const d = await api("/invoices/advance", { method: "POST", body: JSON.stringify({ items: advItems, payment_method: advPayment, tcr_code: getTcr(), buyer_name: advBuyerName, buyer_tin: advBuyerTin }) });
    setAdvSubmitting(false);
    if (d.success) { showToast("Avansni račun kreiran!"); setAdvItems([{ name: "", quantity: 1, unit_price: 0, vat_rate: 21 }]); setAdvBuyerName(""); setAdvBuyerTin(""); loadInvoices(); }
    else showToast(d.message || "Greška", "error");
  };
  const loadAdvInvoice = async () => {
    if (!advSearch.trim()) return;
    const tcr = getTcr();
    let d = await api(`/invoices?search=${encodeURIComponent(advSearch.trim())}&per_page=5${tcr ? "&tcr_code=" + tcr : ""}`);
    let found = d.success ? (d.data?.invoices || []).find(inv => inv.is_advance == 1 && !inv.finalized_by_invoice_id) : null;
    if (found) {
      const det = await api(`/invoices/show/${found.id}`);
      if (det.success) { setAdvInvoice(det.data); setAdvFinalItems((det.data.items || []).map(it => ({ name: it.item_name || it.name || "", quantity: Math.abs(parseFloat(it.quantity || 1)), unit_price: parseFloat(it.unit_price || 0), vat_rate: parseFloat(it.vat_rate ?? 21), unit: it.unit || "kom" }))); }
    } else showToast("Avansni račun nije pronađen", "error");
  };
  const submitFinalizeAdvance = async () => {
    if (!advInvoice || advFinalItems.length === 0) return;
    setAdvSubmitting(true);
    const d = await api(`/invoices/finalizeAdvance/${advInvoice.invoice.id}`, { method: "POST", body: JSON.stringify({ items: advFinalItems, payment_method: advFinalPayment, buyer_name: advBuyerName, buyer_tin: advBuyerTin }) });
    setAdvSubmitting(false);
    if (d.success) { showToast(`Finalni račun kreiran!`); setAdvInvoice(null); setAdvSearch(""); setAdvFinalItems([{ name: "", quantity: 1, unit_price: 0, vat_rate: 21 }]); loadInvoices(); }
    else showToast(d.message || "Greška", "error");
  };

  // Detail view — mobile optimized
  if (detail) {
    const inv = detail.invoice; const items = detail.items || [];
    const isStorno = inv.is_storno == 1;
    const payLabel = { cash: "Gotovina", card: "Kartica", split: "Podijeljeno", bank_transfer: "Virman", other: "Ostalo" }[inv.payment_method] || inv.payment_method;
    return (
      <div style={{ height: "100%", overflow: "auto", background: M.bg, WebkitOverflowScrolling: "touch" }}>
        <div style={{ padding: "10px 14px", background: isStorno ? "#fef2f2" : "white", borderBottom: `1px solid ${M.border}`, display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => setDetail(null)} style={{ padding: 6, background: "#f3f4f6", border: "none", borderRadius: 8, cursor: "pointer" }}><ArrowLeft size={18} /></button>
          <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 700, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{isStorno && "⊖ "}{inv.invoice_number}</div><div style={{ fontSize: 11, color: M.muted }}>{fmtDateTime(inv.invoice_date)}</div></div>
          <Badge status={inv.status} />
        </div>
        <div style={{ padding: 12 }}>
          {isStorno && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 12, color: M.red, fontWeight: 600 }}>Storno/korektivni račun (negativne stavke)</div>}
          {/* Draft storno warning — original has storno that failed fiscalization */}
          {detail.storno_invoice && detail.storno_invoice.status === "draft" && !isStorno && (
            <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>Storno kreiran ali fiskalizacija nije uspjela!</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => fiscalizeDraft(detail.storno_invoice.id)} style={{ ...M.btnSm(M.green, "white"), flex: 1, justifyContent: "center" }}><Zap size={13} />Ponovo fiskalizuj storno</button>
                <button onClick={() => viewInvoice(detail.storno_invoice.id)} style={{ ...M.btnSm("#f3f4f6", M.muted), justifyContent: "center" }}><Eye size={13} />Vidi storno</button>
              </div>
            </div>
          )}
          {inv.is_advance == 1 && <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 12, color: "#1e40af", fontWeight: 600 }}>Avansni račun{inv.finalized_by_invoice_id ? " — ISKORIŠTEN" : ""}</div>}
          {inv.advance_iic && <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 12, color: "#15803d", fontWeight: 600 }}>Finalni račun (uključuje avansno plaćanje)</div>}
          {inv.is_corrective == 1 && <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 12, color: "#9333ea", fontWeight: 600 }}>Korektivni račun</div>}
          {inv.corrected_by_invoice_id && <div style={{ background: "#fefce8", border: "1px solid #fde68a", borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 12, color: "#92400e", fontWeight: 600 }}>Ovaj račun je korigovan</div>}
          {/* Action buttons - scrollable row */}
          <div style={{ display: "flex", gap: 6, marginBottom: 12, overflowX: "auto" }}>
            <button onClick={async () => { await printReceipt(inv.id, true); }} style={M.btnSm("#f3f4f6", M.muted)}><Printer size={12} />Print</button>
            <button onClick={() => openPdf(`/invoices/pdf/${inv.id}?type=a4`)} style={M.btnSm("#f3f4f6", M.muted)}><Download size={12} />PDF</button>
            <button onClick={() => setEmailModal(inv.id)} style={M.btnSm("#f3f4f6", M.muted)}><Mail size={12} />Email</button>
            {inv.status === "draft" && <button onClick={() => fiscalizeDraft(inv.id)} style={M.btnSm("#dcfce7", "#15803d")}><Zap size={12} />Fiskalizuj</button>}
            {detail.can_storno && <button onClick={() => setStornoModal(inv.id)} style={M.btnSm("#fef2f2", M.red)}><RotateCcw size={12} />Storno</button>}
          </div>
          {/* Fiscal info */}
          {inv.fic && <div style={{ ...M.card, padding: 14, marginBottom: 10, background: "#f0fdf4", borderColor: "#bbf7d0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 8 }}><Check size={14} color="#15803d" /><span style={{ fontWeight: 700, color: "#15803d", fontSize: 13 }}>Fiskalizovano</span></div>
            <Row label="FIC" value={<span style={{ fontFamily: M.mono, fontSize: 10, wordBreak: "break-all" }}>{inv.fic}</span>} />
            <Row label="IIC" value={<span style={{ fontFamily: M.mono, fontSize: 10, wordBreak: "break-all" }}>{inv.iic || "-"}</span>} last />
            {detail.verification_url && (() => {
              // Use the backend-built verification URL (mapr.tax.gov.me with all 8
              // spec params, Europe/Podgorica timezone). Identical to the QR on
              // printed receipts and PDFs — eliminates the timezone bug from the
              // old JS-built 4-param URL.
              const vurl = detail.verification_url;
              return <div style={{ textAlign: "center", marginTop: 10, paddingTop: 10, borderTop: "1px solid #bbf7d0" }}><img src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(vurl)}`} alt="QR" style={{ width: 100, height: 100 }} /><div style={{ fontSize: 10, color: "#15803d", marginTop: 4 }}>Verifikacioni QR kod</div></div>;
            })()}
          </div>}
          {/* Partner */}
          {(inv.buyer_name || inv.partner_name) && <div style={{ ...M.card, padding: 14, marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}><Building size={13} style={{ verticalAlign: -2 }} /> Kupac</div>
            <Row label="Naziv" value={inv.partner_name || inv.buyer_name} />
            {(inv.partner_tax_id || inv.buyer_pib) && <Row label="PIB" value={inv.partner_tax_id || inv.buyer_pib} last />}
          </div>}
          {/* Items */}
          <div style={{ ...M.card, overflow: "hidden", marginBottom: 10 }}>
            <div style={{ padding: "10px 14px", borderBottom: `1px solid ${M.border}`, fontWeight: 700, fontSize: 13 }}>Stavke ({items.length})</div>
            {items.map((it, i) => <div key={i} style={{ padding: "8px 14px", borderBottom: i < items.length - 1 ? "1px solid #f5f5f5" : "none", display: "flex", alignItems: "center" }}>
              <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 500 }}>{it.item_name || it.name}</div><div style={{ fontSize: 11, color: M.muted }}>{fmtQty(it.quantity)} × {fmt(it.unit_price)}€</div></div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{fmt(it.total_amount || it.quantity * it.unit_price)}€</div>
            </div>)}
          </div>
          {/* Totals */}
          <div style={{ ...M.card, padding: 14 }}>
            <Row label="Neto" value={`${fmt(inv.subtotal)} €`} />
            <Row label="PDV" value={`${fmt(inv.vat_amount)} €`} />
            {parseFloat(inv.discount_amount || 0) > 0 && <Row label="Popust" value={`-${fmt(inv.discount_amount)} €`} />}
            <Row label="Plaćanje" value={payLabel} />
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 10, borderTop: "1px solid #f3f4f6", marginTop: 6 }}><span style={{ fontWeight: 700, fontSize: 16 }}>Ukupno</span><span style={{ fontWeight: 700, fontSize: 20, color: isStorno ? M.red : M.green }}>{fmt(inv.total_amount)} €</span></div>
          </div>
        </div>
        {stornoModal && <Modal onClose={() => setStornoModal(null)} title="Storno"><StornoForm onStorno={r => handleStorno(stornoModal, r)} onClose={() => setStornoModal(null)} /></Modal>}
        {emailModal && <Modal onClose={() => setEmailModal(null)} title="Email"><EmailForm onSend={e => handleEmail(emailModal, e)} onClose={() => setEmailModal(null)} /></Modal>}
      </div>
    );
  }

  // List view — mobile cards instead of table
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "10px 12px", background: "white", borderBottom: `1px solid ${M.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Računi <span style={{ fontWeight: 400, fontSize: 12, color: M.muted }}>{pg.total}</span></span>
          <div style={{ display: "flex", gap: 4 }}>
            {[{ id: "racuni", l: "Računi" }, { id: "korekcije", l: "Korekcije" }, { id: "avansni", l: "Avansni" }].map(t => (
              <button key={t.id} onClick={() => setInvTab(t.id)} style={{ padding: "5px 10px", border: `1px solid ${invTab === t.id ? M.dark : M.border}`, borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", background: invTab === t.id ? M.dark : "white", color: invTab === t.id ? "white" : M.muted, fontFamily: M.font }}>{t.l}</button>
            ))}
          </div>
        </div>

        {/* ═══ KOREKCIJE TAB ═══ */}
        {invTab === "korekcije" && (
          <div style={{ padding: "8px 0" }}>
            <div style={{ background: "#fef3c7", borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 12, color: "#92400e" }}>Sistem kreira storno + novi ispravljeni račun.</div>
            {!corrInvoice ? (
              <div style={{ display: "flex", gap: 6 }}>
                <input value={corrSearch} onChange={e => setCorrSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && loadCorrInvoice()} placeholder="Broj računa..." style={{ ...M.input, flex: 1, fontSize: 13 }} />
                <button onClick={loadCorrInvoice} style={M.btn(M.blue, "white")}>Traži</button>
              </div>
            ) : (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div><div style={{ fontWeight: 700, fontSize: 14 }}>{corrInvoice.invoice?.invoice_number}</div><div style={{ fontSize: 11, color: M.muted }}>{fmt(corrInvoice.invoice?.total_amount)} €</div></div>
                  <button onClick={() => { setCorrInvoice(null); setCorrItems([]); }} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={16} color={M.muted} /></button>
                </div>
                <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                  {[{ v: "CORRECTIVE", l: "Kupac" }, { v: "ERROR_CORRECTIVE", l: "Greška" }].map(r => (
                    <button key={r.v} onClick={() => setCorrReason(r.v)} style={{ flex: 1, padding: "6px", border: `1px solid ${corrReason === r.v ? M.blue : M.border}`, borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", background: corrReason === r.v ? M.blue : "white", color: corrReason === r.v ? "white" : M.muted, fontFamily: M.font }}>{r.l}</button>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  {[{ v: "cash", l: "Got." }, { v: "card", l: "Kart." }, { v: "virman", l: "Virm." }].map(p => (
                    <button key={p.v} onClick={() => setCorrPayment(p.v)} style={{ flex: 1, padding: "6px", border: `1px solid ${corrPayment === p.v ? M.green : M.border}`, borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", background: corrPayment === p.v ? M.green : "white", color: corrPayment === p.v ? "white" : M.muted, fontFamily: M.font }}>{p.l}</button>
                  ))}
                </div>
                {corrItems.map((item, idx) => (
                  <div key={idx} style={{ display: "flex", gap: 4, marginBottom: 4, alignItems: "center" }}>
                    <input value={item.name} onChange={e => { const c = [...corrItems]; c[idx].name = e.target.value; setCorrItems(c); }} style={{ ...M.input, flex: 2, padding: "6px 8px", fontSize: 12 }} />
                    <input type="number" value={item.quantity} onChange={e => { const c = [...corrItems]; c[idx].quantity = parseFloat(e.target.value) || 0; setCorrItems(c); }} style={{ ...M.input, width: 50, padding: "6px", fontSize: 12 }} />
                    <input type="number" step="0.01" value={item.unit_price} onChange={e => { const c = [...corrItems]; c[idx].unit_price = parseFloat(e.target.value) || 0; setCorrItems(c); }} style={{ ...M.input, width: 65, padding: "6px", fontSize: 12 }} />
                    <button onClick={() => setCorrItems(corrItems.filter((_, i) => i !== idx))} style={{ background: "none", border: "none", cursor: "pointer", color: M.red, padding: 2 }}><Trash2 size={14} /></button>
                  </div>
                ))}
                <div style={{ background: "#f9fafb", borderRadius: 8, padding: 10, margin: "8px 0" }}>
                  {(() => { let t = 0; corrItems.forEach(i => { t += i.quantity * i.unit_price * (1 + (i.vat_rate ?? 21) / 100); }); return <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}><span>Ukupno:</span><span style={{ color: M.green }}>{fmt(t)} €</span></div>; })()}
                </div>
                <button onClick={submitCorrection} disabled={corrSubmitting} style={{ ...M.btn(M.blue, "white"), width: "100%", justifyContent: "center", padding: 12 }}>
                  {corrSubmitting ? <Loader2 size={15} className="spin" /> : <CheckCircle size={15} />}<span style={{ marginLeft: 6 }}>Potvrdi korekciju</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* ═══ AVANSNI TAB ═══ */}
        {invTab === "avansni" && (
          <div style={{ padding: "8px 0" }}>
            <div style={{ background: "#eff6ff", borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 12, color: "#1e40af" }}>Avansni račun za prepay. Finaliziraj kad isporučiš.</div>
            <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
              {[{ id: "create", l: "Kreiraj" }, { id: "finalize", l: "Finaliziraj" }].map(t => (
                <button key={t.id} onClick={() => { setAdvMode(t.id); setAdvInvoice(null); }} style={{ flex: 1, padding: "7px", border: `1px solid ${advMode === t.id ? M.blue : M.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", background: advMode === t.id ? M.blue : "white", color: advMode === t.id ? "white" : M.muted, fontFamily: M.font }}>{t.l}</button>
              ))}
            </div>
            {advMode === "create" && (
              <div>
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  <input value={advBuyerName} onChange={e => setAdvBuyerName(e.target.value)} placeholder="Kupac" style={{ ...M.input, flex: 1, fontSize: 13 }} />
                  <input value={advBuyerTin} onChange={e => setAdvBuyerTin(e.target.value)} placeholder="PIB" style={{ ...M.input, flex: 1, fontSize: 13 }} />
                </div>
                <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                  {[{ v: "cash", l: "Got." }, { v: "card", l: "Kart." }, { v: "bank_transfer", l: "Virman" }].map(p => (
                    <button key={p.v} onClick={() => setAdvPayment(p.v)} style={{ flex: 1, padding: "6px", border: `1px solid ${advPayment === p.v ? M.green : M.border}`, borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", background: advPayment === p.v ? M.green : "white", color: advPayment === p.v ? "white" : M.muted, fontFamily: M.font }}>{p.l}</button>
                  ))}
                </div>
                {advItems.map((item, idx) => (
                  <div key={idx} style={{ display: "flex", gap: 4, marginBottom: 4, alignItems: "center" }}>
                    <input value={item.name} onChange={e => { const c = [...advItems]; c[idx].name = e.target.value; setAdvItems(c); }} placeholder="Opis" style={{ ...M.input, flex: 2, padding: "6px 8px", fontSize: 12 }} />
                    <input type="number" value={item.quantity} onChange={e => { const c = [...advItems]; c[idx].quantity = parseFloat(e.target.value) || 0; setAdvItems(c); }} style={{ ...M.input, width: 45, padding: "6px", fontSize: 12 }} />
                    <input type="number" step="0.01" value={item.unit_price} onChange={e => { const c = [...advItems]; c[idx].unit_price = parseFloat(e.target.value) || 0; setAdvItems(c); }} style={{ ...M.input, width: 60, padding: "6px", fontSize: 12 }} />
                    <button onClick={() => setAdvItems(advItems.filter((_, i) => i !== idx))} style={{ background: "none", border: "none", cursor: "pointer", color: M.red, padding: 2 }}><Trash2 size={14} /></button>
                  </div>
                ))}
                <button onClick={() => setAdvItems([...advItems, { name: "", quantity: 1, unit_price: 0, vat_rate: 21 }])} style={{ ...M.btnSm("#f3f4f6", M.muted), marginBottom: 8, fontSize: 11 }}>+ Stavka</button>
                <div style={{ background: "#f9fafb", borderRadius: 8, padding: 10, marginBottom: 8 }}>
                  {(() => { let t = 0; advItems.forEach(i => { t += i.quantity * i.unit_price * (1 + (i.vat_rate ?? 21) / 100); }); return <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}><span>Avans:</span><span style={{ color: M.green }}>{fmt(t)} €</span></div>; })()}
                </div>
                <button onClick={submitAdvance} disabled={advSubmitting} style={{ ...M.btn(M.blue, "white"), width: "100%", justifyContent: "center", padding: 12 }}>
                  {advSubmitting ? <Loader2 size={15} className="spin" /> : <DollarSign size={15} />}<span style={{ marginLeft: 6 }}>Kreiraj avans</span>
                </button>
              </div>
            )}
            {advMode === "finalize" && (
              <div>
                {!advInvoice ? (
                  <div style={{ display: "flex", gap: 6 }}>
                    <input value={advSearch} onChange={e => setAdvSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && loadAdvInvoice()} placeholder="Broj avansnog računa..." style={{ ...M.input, flex: 1, fontSize: 13 }} />
                    <button onClick={loadAdvInvoice} style={M.btn(M.blue, "white")}>Traži</button>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <div><div style={{ fontWeight: 700, fontSize: 14 }}>Avans: {advInvoice.invoice?.invoice_number}</div><div style={{ fontSize: 12, color: M.green, fontWeight: 600 }}>{fmt(advInvoice.invoice?.total_amount)} €</div></div>
                      <button onClick={() => { setAdvInvoice(null); setAdvSearch(""); }} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={16} color={M.muted} /></button>
                    </div>
                    <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                      {[{ v: "cash", l: "Got." }, { v: "card", l: "Kart." }, { v: "bank_transfer", l: "Virman" }].map(p => (
                        <button key={p.v} onClick={() => setAdvFinalPayment(p.v)} style={{ flex: 1, padding: "6px", border: `1px solid ${advFinalPayment === p.v ? M.green : M.border}`, borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", background: advFinalPayment === p.v ? M.green : "white", color: advFinalPayment === p.v ? "white" : M.muted, fontFamily: M.font }}>{p.l}</button>
                      ))}
                    </div>
                    {advFinalItems.map((item, idx) => (
                      <div key={idx} style={{ display: "flex", gap: 4, marginBottom: 4, alignItems: "center" }}>
                        <input value={item.name} onChange={e => { const c = [...advFinalItems]; c[idx].name = e.target.value; setAdvFinalItems(c); }} style={{ ...M.input, flex: 2, padding: "6px 8px", fontSize: 12 }} />
                        <input type="number" value={item.quantity} onChange={e => { const c = [...advFinalItems]; c[idx].quantity = parseFloat(e.target.value) || 0; setAdvFinalItems(c); }} style={{ ...M.input, width: 45, padding: "6px", fontSize: 12 }} />
                        <input type="number" step="0.01" value={item.unit_price} onChange={e => { const c = [...advFinalItems]; c[idx].unit_price = parseFloat(e.target.value) || 0; setAdvFinalItems(c); }} style={{ ...M.input, width: 60, padding: "6px", fontSize: 12 }} />
                        <button onClick={() => setAdvFinalItems(advFinalItems.filter((_, i) => i !== idx))} style={{ background: "none", border: "none", cursor: "pointer", color: M.red, padding: 2 }}><Trash2 size={14} /></button>
                      </div>
                    ))}
                    <div style={{ background: "#f9fafb", borderRadius: 8, padding: 10, margin: "8px 0" }}>
                      {(() => { let t = 0; advFinalItems.forEach(i => { t += i.quantity * i.unit_price * (1 + (i.vat_rate ?? 21) / 100); }); const adv = parseFloat(advInvoice.invoice?.total_amount || 0); return <>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: M.muted }}><span>Ukupno:</span><span>{fmt(t)} €</span></div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#1e40af" }}><span>Avans:</span><span>-{fmt(adv)} €</span></div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, borderTop: "1px solid #e5e7eb", paddingTop: 4, marginTop: 4 }}><span>Ostatak:</span><span style={{ color: M.green }}>{fmt(Math.max(0, t - adv))} €</span></div>
                      </>; })()}
                    </div>
                    <button onClick={submitFinalizeAdvance} disabled={advSubmitting} style={{ ...M.btn(M.blue, "white"), width: "100%", justifyContent: "center", padding: 12 }}>
                      {advSubmitting ? <Loader2 size={15} className="spin" /> : <CheckCircle size={15} />}<span style={{ marginLeft: 6 }}>Finalni račun</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══ RACUNI LIST (only when on racuni tab) ═══ */}
        {invTab === "racuni" && (<>
        <div style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPg(p => ({ ...p, page: 1 })); }} style={{ ...M.input, padding: "5px 8px", fontSize: 12, width: "auto", flex: 1 }} />
          <span style={{ fontSize: 11, color: M.muted }}>—</span>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPg(p => ({ ...p, page: 1 })); }} style={{ ...M.input, padding: "5px 8px", fontSize: 12, width: "auto", flex: 1 }} />
          {[{ l: "7d", d: 7 }, { l: "30d", d: 30 }].map(p => (
            <button key={p.l} onClick={() => { setDateFrom(daysAgoLocal(p.d)); setDateTo(todayLocal()); setPg(pr => ({ ...pr, page: 1 })); }} style={{ padding: "4px 10px", border: `1px solid ${M.border}`, borderRadius: 6, fontSize: 11, cursor: "pointer", background: "white", fontFamily: M.font, color: M.muted }}>{p.l}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 4, overflowX: "auto" }}>
          {[{ v: "", l: "Sve" }, { v: "fiscalized", l: "Fiskalizovani" }, { v: "draft", l: "Draft" }, { v: "storno", l: "Storno" }, { v: "corrective", l: "Korig." }, { v: "advance", l: "Avans" }].map(s => (
            <button key={s.v} onClick={() => { setStatusFilter(s.v); setPg(p => ({ ...p, page: 1 })); }} style={{ padding: "7px 14px", border: "none", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer", background: statusFilter === s.v ? M.dark : "#f3f4f6", color: statusFilter === s.v ? "white" : M.muted, fontFamily: M.font, whiteSpace: "nowrap" }}>{s.l}</button>
          ))}
        </div>
        </>)}
      </div>
      {invTab === "racuni" && <div style={{ flex: 1, overflow: "auto", padding: 10 }}>
        {loading ? <div style={{ textAlign: "center", padding: 40 }}><Loader2 size={24} className="spin" color="#bbb" /></div>
        : invoices.length === 0 ? <div style={{ textAlign: "center", padding: 40, color: "#bbb" }}><FileText size={30} /><p style={{ marginTop: 6, fontSize: 13 }}>Nema računa</p></div>
        : invoices.map(inv => {
          const isStorno = inv.is_storno == 1 || inv.status === "storno";
          const wasStornoed = inv.stornoed_by_invoice_id || inv.stornoed_at;
          return (
            <div key={inv.id} onClick={() => viewInvoice(inv.id)} style={{ ...M.card, padding: "12px 14px", marginBottom: 8, cursor: "pointer", background: isStorno ? "#fef2f2" : wasStornoed ? "#fff7ed" : "white", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: M.mono, fontSize: 13, fontWeight: 700, color: isStorno ? M.red : M.dark }}>{isStorno && "⊖ "}#{shortInvNum(inv.invoice_number)}</span>
                  {inv.is_advance == 1 && <span style={{ fontSize: 9, color: "#1e40af", fontWeight: 700, background: "#eff6ff", padding: "1px 4px", borderRadius: 3 }}>AVANS</span>}
                  {inv.finalized_by_invoice_id && <span style={{ fontSize: 9, color: "#15803d", fontWeight: 700, background: "#f0fdf4", padding: "1px 4px", borderRadius: 3 }}>ISKORIŠTEN</span>}
                  {inv.is_corrective == 1 && <span style={{ fontSize: 9, color: "#9333ea", fontWeight: 700, background: "#faf5ff", padding: "1px 4px", borderRadius: 3 }}>KOREKC.</span>}
                  {inv.corrected_by_invoice_id && <span style={{ fontSize: 9, color: "#92400e", fontWeight: 700, background: "#fefce8", padding: "1px 4px", borderRadius: 3 }}>KORIGOVANO</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}><Badge status={isStorno ? "storno" : inv.status} /><span style={{ fontSize: 11, color: M.muted }}>{fmtDate(inv.invoice_date)} • {inv.payment_method === "cash" ? "Got." : inv.payment_method === "card" ? "Kart." : inv.payment_method === "bank_transfer" ? "Virman" : inv.payment_method}</span></div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, color: isStorno ? M.red : M.dark, flexShrink: 0 }}>{fmt(inv.total_amount)}€</div>
              <ChevronRight size={16} color="#ccc" style={{ flexShrink: 0 }} />
            </div>
          );
        })}
        {pg.total_pages > 1 && <div style={{ display: "flex", justifyContent: "center", gap: 6, padding: "10px 0" }}>
          <button disabled={pg.page <= 1} onClick={() => setPg(p => ({ ...p, page: p.page - 1 }))} style={{ ...M.btnSm(pg.page <= 1 ? "#f3f4f6" : "white", pg.page <= 1 ? "#ccc" : M.dark), border: `1px solid ${M.border}` }}>← Preth.</button>
          <span style={{ padding: "6px 10px", fontSize: 12, color: M.muted }}>{pg.page}/{pg.total_pages}</span>
          <button disabled={pg.page >= pg.total_pages} onClick={() => setPg(p => ({ ...p, page: p.page + 1 }))} style={{ ...M.btnSm(pg.page >= pg.total_pages ? "#f3f4f6" : "white", pg.page >= pg.total_pages ? "#ccc" : M.dark), border: `1px solid ${M.border}` }}>Sljed. →</button>
        </div>}
        </div>}
      {stornoModal && <Modal onClose={() => setStornoModal(null)} title="Storno"><StornoForm onStorno={r => handleStorno(stornoModal, r)} onClose={() => setStornoModal(null)} /></Modal>}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// BLAGAJNA — Mobile cards
// ═══════════════════════════════════════════════════
function BlagajnaView({ user, showToast }) {
  const [depositAmt, setDepositAmt] = useState("");
  const [actualCash, setActualCash] = useState("");
  const [notes, setNotes] = useState("");
  const [active, setActive] = useState(null);
  const [depositExists, setDepositExists] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [unclosedSession, setUnclosedSession] = useState(null);
  const [internalEnabled, setInternalEnabled] = useState(false);
  const [internalDay, setInternalDay] = useState(null);
  const [internalSales, setInternalSales] = useState(null);
  const [blagajnaTab, setBlagajnaTab] = useState("depozit");
  const [showCloseAny, setShowCloseAny] = useState(false);
  const [closeAnyDate, setCloseAnyDate] = useState("");
  const [closeAnyCash, setCloseAnyCash] = useState("");
  const [internalHistory, setInternalHistory] = useState([]);
  const [withdrawAmt, setWithdrawAmt] = useState("");
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawals, setWithdrawals] = useState([]);
  const [todayWithdrawn, setTodayWithdrawn] = useState(0);
  const [continueMode, setContinueMode] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem("minipos_continue_mode") || "null"); if (v && v.date === todayLocal() && v.tcr === (localStorage.getItem("minipos_tcr") || "")) return true; } catch(e) {}
    return false;
  });
  const [todaySales, setTodaySales] = useState(null);
  const [closingInfo, setClosingInfo] = useState(null);
  const [dayInvoices, setDayInvoices] = useState([]);
  const [showCloseByInvoice, setShowCloseByInvoice] = useState(false);
  const [histFrom, setHistFrom] = useState(() => daysAgoLocal(14));
  const [histTo, setHistTo] = useState(() => todayLocal());
  const tcr = getTcr();

  useEffect(() => { load(); }, []);
  const load = async () => {
    if (!tcr) { setLoading(false); return; }
    try {
      const [dep, sess, hist, unclosed, idSettings, idStatus, idHist, wdHist] = await Promise.all([
        api(`/deposits/check/${tcr}`), api(`/cashregister/active/${tcr}`), api(`/cashregister?tcr_code=${tcr}`),
        api(`/cashregister/unclosed/${tcr}`), api("/internalday/settings"), api(`/internalday/status/${tcr}`),
        api(`/internalday/history/${tcr}`), api(`/deposits/withdrawals/${tcr}`)
      ]);
      if (dep.success) setDepositExists(dep.data.has_deposit);
      if (sess.success) setActive(sess.data.has_active_session ? sess.data : null);
      if (hist.success) setSessions(hist.data.sessions || []);
      if (unclosed.success && unclosed.data.has_unclosed) setUnclosedSession(unclosed.data); else setUnclosedSession(null);
      if (idSettings.success) setInternalEnabled(!!idSettings.data?.internal_day_enabled);
      if (idStatus.success) { setInternalDay(idStatus.data?.day || null); setInternalSales(idStatus.data?.sales || null); }
      if (idHist?.success) setInternalHistory(idHist.data?.days || []);
      if (wdHist?.success) { setWithdrawals(wdHist.data?.withdrawals || []); setTodayWithdrawn(wdHist.data?.today_total || 0); }
      // If continue-mode is persisted for today, re-fetch today's sales so the
      // "Nastavak rada" panel re-renders after a remount (todaySales state
      // resets to null on remount; without this it would stay hidden).
      try {
        const cm = JSON.parse(localStorage.getItem("minipos_continue_mode") || "null");
        if (cm && cm.date === todayLocal() && cm.tcr === tcr) {
          const ts = await api(`/cashregister/todaySales/${tcr}`);
          if (ts.success) setTodaySales(ts.data.sales || ts.data);
        }
      } catch(e) {}
    } catch(e) {}
    setLoading(false);
  };

  const doDeposit = async () => {
    if (!depositAmt) return;
    setSubmitting(true);
    const d = await api("/deposits/register", { method: "POST", body: JSON.stringify({ tcr_code: tcr, deposit_amount: parseFloat(depositAmt) || 0 }) });
    setSubmitting(false);
    if (d.success) { showToast("Depozit registrovan!"); setDepositAmt(""); load(); setTimeout(async () => { try { const sess = await api(`/cashregister/active/${tcr}`); if (sess.success && sess.data?.session?.id) { if (isSunmi) { const s = sess.data.session; await thermalPrint("/print/register", { type: "opening", company_name: user?.company_name || "", date: fmtDateTime(s.opened_at), operator_name: s.operator_name || user?.full_name || user?.name || "", tcr_code: tcr, deposit_amount: s.opening_cash || 0, fic: s.fic || s.deposit_fic || d.data?.fic || "" }); } else { const token = localStorage.getItem("minipos_token"); const pw = parseInt(localStorage.getItem("minipos_paper_width") || "58"); const wp = pw <= 58 ? "&width=32" : ""; const r = await fetch(`${API_BASE}/cashregister/openingPdf/${sess.data.session.id}?type=text${wp}`, { headers: { Authorization: `Bearer ${token}` } }); if (r.ok) { const text = await r.text(); thermalPrint("/print/text", { text }); } } } } catch(e) {} }, 500); } else showToast(d.message, "error");
  };
  const doClose = async (targetDate) => {
    setSubmitting(true);
    const d = await api("/cashregister/close", { method: "POST", body: JSON.stringify({ tcr_code: tcr, actual_cash: parseFloat(actualCash) || 0, notes, target_date: targetDate || "today" }) });
    setSubmitting(false);
    if (d.success) { setClosingInfo(d.data); setActualCash(""); setNotes(""); setContinueMode(false); localStorage.removeItem("minipos_continue_mode"); load(); showToast("Blagajna zatvorena!"); const sessId = d.data?.session?.id; if (sessId) { setTimeout(async () => { try { if (isSunmi) { const s = d.data.session; await thermalPrint("/print/register", { type: "closing", company_name: user?.company_name || "", date: fmtDateTime(s.opened_at), opened_at: fmtDateTime(s.opened_at), closed_at: fmtDateTime(s.closed_at), operator_name: s.operator_name || user?.full_name || "", tcr_code: tcr, deposit_amount: s.opening_cash || 0, opening_cash: s.opening_cash, cash_sales: s.cash_sales, card_sales: s.card_sales, total_sales: s.total_sales, transaction_count: s.transaction_count, expected_cash: s.expected_cash, actual_cash: s.actual_cash, difference: s.difference }); } else { const token = localStorage.getItem("minipos_token"); const r = await fetch(`${API_BASE}/cashregister/closingPdf/${sessId}?type=text`, { headers: { Authorization: `Bearer ${token}` } }); if (r.ok) { const text = await r.text(); thermalPrint("/print/text", { text }); } } } catch(e) {} }, 300); } } else showToast(d.message, "error");
  };
  const doOpenInternalDay = async () => {
    setSubmitting(true);
    const d = await api("/internalday/open", { method: "POST", body: JSON.stringify({ tcr_code: tcr }) });
    setSubmitting(false);
    if (d.success) { showToast("Interni dan otvoren!"); load(); } else showToast(d.message, "error");
  };
  const doCloseInternalDay = async () => {
    setSubmitting(true);
    const d = await api("/internalday/close", { method: "POST", body: JSON.stringify({ tcr_code: tcr }) });
    setSubmitting(false);
    if (d.success) { showToast("Interni dan zatvoren!"); load(); const dayId = d.data?.day?.id; if (dayId) { setTimeout(async () => { try { if (isSunmi) { const day = d.data.day; await thermalPrint("/print/register", { type: "closing", company_name: user?.company_name || "", date: fmtDateTime(day.opened_at || day.date), opened_at: fmtDateTime(day.opened_at || day.date), closed_at: fmtDateTime(day.closed_at || new Date().toISOString()), operator_name: day.operator_name || user?.full_name || "", tcr_code: tcr, deposit_amount: day.opening_cash || 0, opening_cash: day.opening_cash || 0, cash_sales: day.cash_sales || 0, card_sales: day.card_sales || 0, total_sales: day.total_sales || 0, transaction_count: day.transaction_count || 0, expected_cash: day.expected_cash || 0, actual_cash: day.actual_cash || 0, difference: day.difference || 0 }); } else { const token = localStorage.getItem("minipos_token"); const r = await fetch(`${API_BASE}/internalday/closingText/${dayId}`, { headers: { Authorization: `Bearer ${token}` } }); if (r.ok) { const text = await r.text(); thermalPrint("/print/text", { text }); } } } catch(e) {} }, 300); } } else showToast(d.message, "error");
  };
  const doWithdraw = async () => {
    if (!withdrawAmt || parseFloat(withdrawAmt) <= 0) { showToast("Unesite iznos", "error"); return; }
    setSubmitting(true);
    const d = await api("/deposits/withdraw", { method: "POST", body: JSON.stringify({ tcr_code: tcr, amount: parseFloat(withdrawAmt), reason: withdrawReason }) });
    setSubmitting(false);
    if (d.success) { showToast("Podizanje uspješno!"); setWithdrawAmt(""); setWithdrawReason(""); load(); const wid = d.data?.withdraw_id; if (wid) { setTimeout(async () => { try { if (isSunmi) { const w = 32; const lr = (l, r) => l + " ".repeat(Math.max(1, w - l.length - r.length)) + r; let text = "=".repeat(w) + "\n  PODIZANJE GOTOVINE\n" + "=".repeat(w) + "\n"; text += (user?.company_name || "") + "\nTCR: " + tcr + "\n" + "-".repeat(w) + "\n"; text += lr("Iznos:", (parseFloat(withdrawAmt) || 0).toFixed(2) + " EUR") + "\n"; if (withdrawReason) text += "Razlog: " + withdrawReason + "\n"; if (d.data?.fic) text += "-".repeat(w) + "\nIKOF/FIC:\n" + d.data.fic + "\n"; text += "=".repeat(w) + "\n\n\n"; await sunmiPrintText(text); } else { const token = localStorage.getItem("minipos_token"); const r = await fetch(`${API_BASE}/cashregister/withdrawalText/${wid}`, { headers: { Authorization: `Bearer ${token}` } }); if (r.ok) { const text = await r.text(); thermalPrint("/print/text", { text }); } } } catch(e) {} }, 300); } } else showToast(d.message || "Greška", "error");
  };
  const doCloseByInvoice = async (invId) => {
    setSubmitting(true);
    const d = await api("/internalday/closeByInvoice", { method: "POST", body: JSON.stringify({ tcr_code: tcr, invoice_id: invId }) });
    setSubmitting(false);
    if (d.success) { showToast("Dan zatvoren po racunu!"); setShowCloseByInvoice(false); load(); } else showToast(d.message, "error");
  };
  const loadDayInvoices = async () => {
    const d = await api(`/internalday/invoices/${tcr}`);
    if (d.success) { setDayInvoices(d.data?.invoices || []); setShowCloseByInvoice(true); }
  };

  // Date range helper
  const DateRange = () => (
    <div style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
      <input type="date" value={histFrom} onChange={e => setHistFrom(e.target.value)} style={{ ...M.input, padding: "4px 6px", fontSize: 11, flex: 1, minWidth: 100 }} />
      <span style={{ fontSize: 10, color: M.muted }}>—</span>
      <input type="date" value={histTo} onChange={e => setHistTo(e.target.value)} style={{ ...M.input, padding: "4px 6px", fontSize: 11, flex: 1, minWidth: 100 }} />
      {[{ l: "7d", d: 7 }, { l: "14d", d: 14 }, { l: "30d", d: 30 }].map(p => (
        <button key={p.l} onClick={() => { setHistFrom(daysAgoLocal(p.d)); setHistTo(todayLocal()); }} style={{ padding: "3px 8px", border: `1px solid ${M.border}`, borderRadius: 4, fontSize: 10, cursor: "pointer", background: "white", fontFamily: M.font, color: M.muted }}>{p.l}</button>
      ))}
    </div>
  );

  if (loading) return <div style={{ textAlign: "center", padding: 40 }}><Loader2 size={22} className="spin" color="#bbb" /></div>;
  if (!tcr) return <div style={{ textAlign: "center", padding: 40, color: M.muted }}><AlertCircle size={28} /><p style={{ marginTop: 8, fontSize: 13 }}>Odaberite TCR u podešavanjima</p></div>;

  return (
    <div style={{ height: "100%", overflow: "auto", padding: "12px", paddingLeft: "max(12px, env(safe-area-inset-left, 12px))", paddingRight: "max(12px, env(safe-area-inset-right, 12px))", background: M.bg, WebkitOverflowScrolling: "touch" }}>
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Blagajna <span style={{ fontWeight: 400, fontSize: 11, color: M.muted }}>TCR: {tcr}</span></div>

      {/* Closing report */}
      {closingInfo && (
        <div style={{ ...M.card, padding: 14, marginBottom: 10, borderColor: "#bbf7d0", background: "#f0fdf4" }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: M.green }}>Izvještaj zatvaranja</div>
          <Row label="Gotovina" value={`${fmt(closingInfo.cash_sales)} €`} />
          <Row label="Kartica" value={`${fmt(closingInfo.card_sales)} €`} />
          <Row label="Ukupan promet" value={`${fmt(closingInfo.total_sales)} €`} />
          <Row label="Transakcije" value={closingInfo.transaction_count || 0} />
          <Row label="Očekivana got." value={`${fmt(closingInfo.expected_cash)} €`} />
          <Row label="Stvarna got." value={`${fmt(closingInfo.actual_cash)} €`} />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, paddingTop: 8, borderTop: `1px solid ${M.border}` }}><span style={{ fontWeight: 700, fontSize: 13 }}>Razlika</span><span style={{ fontWeight: 700, fontSize: 13, color: parseFloat(closingInfo.difference || 0) === 0 ? M.green : M.red }}>{fmt(closingInfo.difference)} €</span></div>
          <button onClick={() => setClosingInfo(null)} style={{ ...M.btnSm("#f3f4f6", M.muted), width: "100%", justifyContent: "center", marginTop: 8 }}>OK</button>
        </div>
      )}

      {/* ═══ INTERNAL DAY ON: tabs ═══ */}
      {internalEnabled && (
        <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
          {[{ id: "depozit", l: "Depozit" }, { id: "podizanje", l: "Podizanje" }, { id: "otvaranja", l: "Otvaranja" }, { id: "zatvaranja", l: "Zatvaranja" }].map(t => (
            <button key={t.id} onClick={() => setBlagajnaTab(t.id)} style={{ padding: "8px 14px", border: "none", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", background: blagajnaTab === t.id ? M.dark : "#f3f4f6", color: blagajnaTab === t.id ? "white" : M.muted, fontFamily: M.font }}>{t.l}</button>
          ))}
        </div>
      )}

      {/* Internal day alert */}
      {internalEnabled && !internalDay && blagajnaTab === "depozit" && (
        <div style={{ ...M.card, padding: 12, marginBottom: 10, borderColor: "#fde68a", background: "#fffbeb" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>Interni dan nije otvoren! Otvorite u tabu Otvaranja.</div>
        </div>
      )}
      {internalEnabled && internalDay && blagajnaTab === "depozit" && (
        <div style={{ ...M.card, padding: 12, marginBottom: 10, borderColor: "#bbf7d0", background: "#f0fdf4" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d" }}>Interni dan otvoren od {internalDay.opened_at?.slice(11, 16)} · Promet: {fmt(internalSales?.total_sales || 0)} €</div>
        </div>
      )}

      {/* ═══ DEPOZIT TAB (Internal Day ON) ═══ */}
      {internalEnabled && blagajnaTab === "depozit" && (
        <div>
          {!depositExists ? (
            <div style={{ ...M.card, padding: 14, marginBottom: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Registruj depozit</div>
              <div style={{ display: "flex", gap: 6 }}>
                <input type="number" step="0.01" value={depositAmt} onChange={e => setDepositAmt(e.target.value)} placeholder="Iznos €" style={{ ...M.input, padding: "8px 10px", flex: 1 }} />
                <button onClick={doDeposit} disabled={submitting} style={M.btn(M.green)}>{submitting ? <Loader2 size={14} className="spin" /> : "Depozit"}</button>
              </div>
            </div>
          ) : (
            <div style={{ ...M.card, padding: 12, marginBottom: 10, background: "#f0fdf4" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#15803d" }}>Depozit registrovan za danas</div>
            </div>
          )}
          <div style={{ fontSize: 14, fontWeight: 700, color: M.muted, marginBottom: 8 }}>Istorija depozita</div>
          <DateRange />
          {sessions.filter(s => s.opened_at?.slice(0,10) >= histFrom && s.opened_at?.slice(0,10) <= histTo).map(s => (
            <div key={s.id} style={{ ...M.card, padding: "8px 12px", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div><div style={{ fontWeight: 600, fontSize: 12 }}>{fmtDate(s.opened_at)}</div><div style={{ fontSize: 10, color: M.muted }}>Depozit: {fmt(s.opening_cash || 0)} €</div></div>
              <button onClick={async () => { const token = localStorage.getItem("minipos_token"); const pw = parseInt(localStorage.getItem("minipos_paper_width") || "58"); const wp = pw <= 58 ? "&width=32" : ""; const r = await fetch(`${API_BASE}/cashregister/openingPdf/${s.id}?type=text${wp}`, { headers: { Authorization: `Bearer ${token}` } }); if (r.ok) { const text = await r.text(); if (isSunmi) { await sunmiPrintText(text); } else { await thermalPrint("/print/text", { text }); } } }} style={M.btnSm("#f3f4f6", M.muted)}><Printer size={11} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ═══ OTVARANJA TAB ═══ */}
      {internalEnabled && blagajnaTab === "otvaranja" && (
        <div>
          {!internalDay ? (
            <div style={{ ...M.card, padding: 14, marginBottom: 10 }}>
              <p style={{ fontWeight: 600, margin: "0 0 10px", fontSize: 13 }}>Otvorite interni dan</p>
              <button onClick={doOpenInternalDay} disabled={submitting} style={{ ...M.btn(M.green, "white"), width: "100%", justifyContent: "center", padding: 12 }}>{submitting ? <Loader2 size={14} className="spin" /> : "Otvori interni dan"}</button>
            </div>
          ) : (
            <div style={{ ...M.card, padding: 14, marginBottom: 10, background: "#f0fdf4" }}>
              <Row label="Od" value={internalDay.opened_at?.slice(0, 16).replace("T", " ")} />
              <Row label="Promet" value={`${fmt(internalSales?.total_sales || 0)} €`} />
              <Row label="Računi" value={internalSales?.transaction_count || 0} last />
            </div>
          )}
          <div style={{ fontSize: 14, fontWeight: 700, color: M.muted, marginBottom: 8 }}>Istorija otvaranja</div>
          <DateRange />
          {internalHistory.filter(d => d.opened_at?.slice(0,10) >= histFrom && d.opened_at?.slice(0,10) <= histTo).slice(0, 30).map(d => (
            <div key={d.id} style={{ ...M.card, padding: "8px 12px", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{d.opened_at?.slice(0, 10)}</span>
                  <span style={{ fontSize: 11, color: d.status === "open" ? M.green : M.muted, marginLeft: 8 }}>{d.status === "open" ? "Otvoren" : "Zatvoren"}</span>
                </div>
                <div style={{ fontSize: 10, color: M.muted }}>{d.opened_at?.slice(11, 16)}{d.closed_at ? ` - ${d.closed_at?.slice(11, 16)}` : ""} · {d.transaction_count || 0} rač.</div>
              </div>
              <button onClick={async () => { await thermalPrint("/print/register", { type: "opening", company_name: user?.company_name || "", date: fmtDateTime(d.opened_at), operator_name: d.operator_name || user?.full_name || "", tcr_code: tcr, deposit_amount: 0, fic: "" }); }} style={M.btnSm("#f3f4f6", M.muted)}><Printer size={11} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ═══ ZATVARANJA TAB ═══ */}
      {internalEnabled && blagajnaTab === "zatvaranja" && (
        <div>
          {internalDay ? (
            <div style={{ ...M.card, padding: 14, marginBottom: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Zatvori interni dan</div>
              <button onClick={doCloseInternalDay} disabled={submitting} style={{ ...M.btn(M.red, "white"), width: "100%", justifyContent: "center", padding: 10 }}>{submitting ? <Loader2 size={14} className="spin" /> : "Zatvori interni dan"}</button>
            </div>
          ) : (
            <div style={{ ...M.card, padding: 12, marginBottom: 10, background: "#f9fafb" }}>
              <div style={{ fontSize: 12, color: M.muted }}>Nema otvorenog internog dana.</div>
            </div>
          )}
          {/* Close by invoice (forgotten day) */}
          <button onClick={loadDayInvoices} style={{ ...M.btnSm("#f59e0b", "white"), width: "100%", justifyContent: "center", padding: 8, marginBottom: 10, fontSize: 11 }}><Clock size={12} />Zatvori po računu (zaboravljen dan)</button>
          {showCloseByInvoice && (
            <Modal onClose={() => setShowCloseByInvoice(false)} title="Zatvori po računu">
              {dayInvoices.length === 0 ? <div style={{ textAlign: "center", padding: 16, color: M.muted, fontSize: 12 }}>Nema računa za zatvaranje</div>
              : dayInvoices.map(inv => (
                <div key={inv.id} onClick={() => doCloseByInvoice(inv.id)} style={{ padding: "10px 12px", borderRadius: 8, border: `1px solid ${M.border}`, marginBottom: 4, cursor: "pointer", background: "#f9fafb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div><div style={{ fontWeight: 600, fontSize: 12 }}>#{inv.invoice_number}</div><div style={{ fontSize: 10, color: M.muted }}>{fmtDateTime(inv.issue_date)}</div></div>
                  <span style={{ fontWeight: 700, fontSize: 12 }}>{fmt(inv.total_amount)} €</span>
                </div>
              ))}
            </Modal>
          )}
          <div style={{ fontSize: 14, fontWeight: 700, color: M.muted, marginBottom: 8 }}>Istorija zatvaranja</div>
          <DateRange />
          {internalHistory.filter(d => d.status === "closed" && d.closed_at?.slice(0,10) >= histFrom && d.closed_at?.slice(0,10) <= histTo).map(d => (
            <div key={d.id} style={{ ...M.card, padding: "8px 12px", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{d.closed_at?.slice(0, 10)}</span>
                  <span style={{ fontWeight: 700, fontSize: 12, color: M.green, marginLeft: 8 }}>{fmt(d.total_sales)} €</span>
                </div>
                <div style={{ fontSize: 10, color: M.muted }}>{d.opened_at?.slice(11, 16)} - {d.closed_at?.slice(11, 16)} · {d.transaction_count} rač. · Got: {fmt(d.cash_sales)}€ · Kart: {fmt(d.card_sales)}€</div>
              </div>
              <button onClick={async () => { await thermalPrint("/print/register", { type: "closing", company_name: user?.company_name || "", date: fmtDateTime(d.opened_at), opened_at: fmtDateTime(d.opened_at), closed_at: fmtDateTime(d.closed_at), operator_name: d.operator_name || user?.full_name || "", tcr_code: tcr, deposit_amount: 0, opening_cash: 0, cash_sales: d.cash_sales || 0, card_sales: d.card_sales || 0, total_sales: d.total_sales || 0, transaction_count: d.transaction_count || 0, expected_cash: d.expected_cash || 0, actual_cash: d.actual_cash || 0, difference: d.difference || 0 }); }} style={M.btnSm("#f3f4f6", M.muted)}><Printer size={11} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ═══ PODIZANJE TAB (Internal Day ON) — duplicated content so it renders when internalEnabled=true ═══ */}
      {internalEnabled && blagajnaTab === "podizanje" && (
        <div>
          {depositExists ? (
            <div style={{ ...M.card, padding: 14, marginBottom: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Podizanje gotovine</div>
              {todayWithdrawn > 0 && (
                <div style={{ background: "#fef3c7", borderRadius: 8, padding: 8, marginBottom: 8, fontSize: 12, color: "#92400e" }}>
                  Danas podignuto: <strong>{fmt(todayWithdrawn)} €</strong>
                </div>
              )}
              <input type="number" step="0.01" value={withdrawAmt} onChange={e => setWithdrawAmt(e.target.value)} placeholder="Iznos (€)" style={{ ...M.input, padding: "8px 10px", marginBottom: 6 }} />
              <input value={withdrawReason} onChange={e => setWithdrawReason(e.target.value)} placeholder="Razlog (opciono)" style={{ ...M.input, padding: "8px 10px", marginBottom: 8 }} />
              <button onClick={doWithdraw} disabled={submitting} style={{ ...M.btn(M.orange, "white"), width: "100%", justifyContent: "center", padding: 10 }}>
                {submitting ? <Loader2 size={14} className="spin" /> : <DollarSign size={14} />}<span style={{ marginLeft: 6 }}>Podizanje</span>
              </button>
            </div>
          ) : (
            <div style={{ ...M.card, padding: 14, marginBottom: 10, background: "#fef3c7", borderColor: "#fde68a" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>Depozit nije registrovan. Prvo registrujte depozit.</div>
            </div>
          )}
          <div style={{ fontSize: 14, fontWeight: 700, color: M.muted, marginBottom: 8 }}>Istorija podizanja</div>
          <DateRange />
          {withdrawals.filter(w => w.issue_date?.slice(0,10) >= histFrom && w.issue_date?.slice(0,10) <= histTo).length === 0
            ? <div style={{ textAlign: "center", padding: 16, color: M.muted, fontSize: 12 }}>Nema podizanja</div>
            : withdrawals.filter(w => w.issue_date?.slice(0,10) >= histFrom && w.issue_date?.slice(0,10) <= histTo).map((w, i) => (
            <div key={i} style={{ ...M.card, padding: "8px 12px", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div><div style={{ fontWeight: 600, fontSize: 12, color: M.orange }}>-{fmt(w.deposit_amount)} €</div><div style={{ fontSize: 10, color: M.muted }}>{fmtDateTime(w.issue_date)}{w.fic ? ` · FIC: ${w.fic.slice(0,12)}...` : ""}</div></div>
              <button onClick={async () => { if (isSunmi) { const ww = 32; const lr = (l, r) => l + " ".repeat(Math.max(1, ww - l.length - r.length)) + r; let text = "=".repeat(ww) + "\n  PODIZANJE GOTOVINE\n" + "=".repeat(ww) + "\n"; text += (user?.company_name || "") + "\nTCR: " + tcr + "\n" + "-".repeat(ww) + "\n"; text += lr("Iznos:", fmt(w.deposit_amount) + " EUR") + "\n"; text += lr("Datum:", fmtDateTime(w.issue_date)) + "\n"; if (w.fic) text += "-".repeat(ww) + "\nIKOF/FIC:\n" + w.fic + "\n"; text += "=".repeat(ww) + "\n\n\n"; await sunmiPrintText(text); } else { const token = localStorage.getItem("minipos_token"); const r = await fetch(`${API_BASE}/cashregister/withdrawalText/${w.id}`, { headers: { Authorization: `Bearer ${token}` } }); if (r.ok) { const text = await r.text(); await thermalPrint("/print/text", { text }); } } }} style={M.btnSm("#f3f4f6", M.muted)}><Printer size={11} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ═══ NON-INTERNAL MODE ═══ */}
      {!internalEnabled && <>
        <div style={{ display: "flex", gap: 4, marginBottom: 10, overflowX: "auto" }}>
          {[{ id: "depozit", l: "Depozit" }, { id: "podizanje", l: "Podizanje" }, { id: "otvaranja", l: "Otvaranja" }, { id: "zatvaranja", l: "Zatvaranja" }].map(t => (
            <button key={t.id} onClick={() => setBlagajnaTab(t.id)} style={{ padding: "8px 14px", border: "none", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", background: blagajnaTab === t.id ? M.dark : "#f3f4f6", color: blagajnaTab === t.id ? "white" : M.muted, fontFamily: M.font, flexShrink: 0 }}>{t.l}</button>
          ))}
        </div>

        {blagajnaTab === "depozit" && <>
          {/* Unclosed session warning */}
          {unclosedSession && !active && (
            <div style={{ ...M.card, padding: 14, marginBottom: 10, borderColor: "#fecaca", background: "#fef2f2" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: M.red, marginBottom: 6 }}>Nezatvorena sesija ({fmtDate(unclosedSession.session?.opened_at)})</div>
              <div style={{ display: "flex", gap: 6 }}>
                <input type="number" step="0.01" value={actualCash} onChange={e => setActualCash(e.target.value)} placeholder="Gotovina €" style={{ ...M.input, padding: "8px 10px", flex: 1 }} />
                <button onClick={() => doClose("oldest")} disabled={submitting} style={M.btn(M.red)}>{submitting ? <Loader2 size={14} className="spin" /> : "Zatvori"}</button>
              </div>
            </div>
          )}

          {/* Deposit form */}
          {!depositExists && !active && (
            <div style={{ ...M.card, padding: 14, marginBottom: 10, borderColor: "#fde68a", background: "#fffbeb" }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: "#92400e" }}>Registruj depozit</div>
              <div style={{ display: "flex", gap: 6 }}>
                <input type="number" step="0.01" value={depositAmt} onChange={e => setDepositAmt(e.target.value)} placeholder="Iznos €" style={{ ...M.input, padding: "8px 10px", flex: 1 }} />
                <button onClick={doDeposit} disabled={submitting} style={M.btn(M.green)}>{submitting ? <Loader2 size={14} className="spin" /> : "Depozit"}</button>
              </div>
            </div>
          )}

          {/* Active session */}
          {active && (
            <div style={{ ...M.card, padding: 14, marginBottom: 10, borderColor: "#bbf7d0", background: "#f0fdf4" }}>
              <Row label="Otvorena" value={fmtDateTime(active.session?.opened_at || active.opened_at)} />
              <Row label="Depozit" value={`${fmt(active.session?.opening_cash || active.opening_cash || active.initial_deposit || 0)} €`} />
              <Row label="Gotovina" value={`${fmt(active.sales?.cash_sales || active.cash_sales || 0)} €`} />
              <Row label="Kartica" value={`${fmt(active.sales?.card_sales || active.card_sales || 0)} €`} />
              <Row label="Promet" value={`${fmt(active.sales?.total_sales || active.total_sales || 0)} €`} />
              <Row label="Transakcije" value={active.sales?.transaction_count || active.transaction_count || 0} />
              {todayWithdrawn > 0 && <Row label="Podignuto" value={`-${fmt(todayWithdrawn)} €`} />}
              <Row label="Očekivana got." value={`${fmt(active.expected_cash || 0)} €`} last />
              <div style={{ marginTop: 10 }}>
                <div style={{ display: "flex", gap: 6 }}>
                  <input type="number" step="0.01" value={actualCash} onChange={e => setActualCash(e.target.value)} placeholder="Stvarna gotovina €" style={{ ...M.input, padding: "8px 10px", flex: 1 }} />
                  <button onClick={() => doClose("today")} disabled={submitting} style={M.btn(M.red)}>{submitting ? <Loader2 size={14} className="spin" /> : "Zatvori"}</button>
                </div>
                <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Napomena" style={{ ...M.input, padding: "8px 10px", marginTop: 6 }} />
              </div>
            </div>
          )}

          {/* Closed for today — continue mode */}
          {!unclosedSession && !active && depositExists && !continueMode && (
            <div style={{ ...M.card, padding: 14, marginBottom: 10, background: "#f0fdf4", borderColor: "#bbf7d0", textAlign: "center" }}>
              <CheckCircle size={28} color={M.green} />
              <p style={{ fontWeight: 600, fontSize: 13, margin: "8px 0" }}>Blagajna zatvorena za danas</p>
              <button onClick={async () => { const d = await api(`/cashregister/todaySales/${tcr}`); if (d.success) { setTodaySales(d.data.sales || d.data); setContinueMode(true); localStorage.setItem("minipos_continue_mode", JSON.stringify({ date: todayLocal(), tcr })); } else showToast("Greška", "error"); }} style={{ ...M.btnSm(M.blue), justifyContent: "center" }}><RefreshCw size={13} />Nastavi opet</button>
            </div>
          )}

          {/* Continue mode — show today sales + close again */}
          {continueMode && todaySales && (
            <div style={{ ...M.card, padding: 14, marginBottom: 10, background: "#eff6ff", borderColor: "#bfdbfe" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: M.blue, marginBottom: 8 }}>Nastavak rada</div>
              <Row label="Gotovina" value={`${fmt(todaySales.cash_sales || 0)} €`} />
              <Row label="Kartica" value={`${fmt(todaySales.card_sales || 0)} €`} />
              <Row label="Promet" value={`${fmt(todaySales.total_sales || 0)} €`} />
              <Row label="Računi" value={todaySales.transaction_count || 0} last />
              <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
                <input type="number" step="0.01" value={actualCash} onChange={e => setActualCash(e.target.value)} placeholder="Stvarna gotovina €" style={{ ...M.input, padding: "8px 10px", flex: 1 }} />
                <button onClick={() => doClose("today")} disabled={submitting} style={M.btn(M.red)}>{submitting ? <Loader2 size={14} className="spin" /> : "Zatvori"}</button>
              </div>
            </div>
          )}

          {/* Close any day */}
          <button onClick={() => setShowCloseAny(true)} style={{ ...M.btn("#f59e0b"), width: "100%", justifyContent: "center", padding: 9, fontSize: 12, marginBottom: 10 }}><Clock size={13} /><span style={{ marginLeft: 6 }}>Zatvori za bilo koji dan</span></button>
        </>}

        {/* ═══ PODIZANJE TAB (non-internal) ═══ */}
        {blagajnaTab === "podizanje" && (
          <div>
            {depositExists ? (
              <div style={{ ...M.card, padding: 14, marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Podizanje gotovine</div>
                {todayWithdrawn > 0 && (
                  <div style={{ background: "#fef3c7", borderRadius: 8, padding: 8, marginBottom: 8, fontSize: 12, color: "#92400e" }}>
                    Danas podignuto: <strong>{fmt(todayWithdrawn)} €</strong>
                  </div>
                )}
                <input type="number" step="0.01" value={withdrawAmt} onChange={e => setWithdrawAmt(e.target.value)} placeholder="Iznos (€)" style={{ ...M.input, padding: "8px 10px", marginBottom: 6 }} />
                <input value={withdrawReason} onChange={e => setWithdrawReason(e.target.value)} placeholder="Razlog (opciono)" style={{ ...M.input, padding: "8px 10px", marginBottom: 8 }} />
                <button onClick={doWithdraw} disabled={submitting} style={{ ...M.btn(M.orange, "white"), width: "100%", justifyContent: "center", padding: 10 }}>
                  {submitting ? <Loader2 size={14} className="spin" /> : <DollarSign size={14} />}<span style={{ marginLeft: 6 }}>Podizanje</span>
                </button>
              </div>
            ) : (
              <div style={{ ...M.card, padding: 14, marginBottom: 10, background: "#fef3c7", borderColor: "#fde68a" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>Depozit nije registrovan. Prvo registrujte depozit.</div>
              </div>
            )}
            <div style={{ fontSize: 14, fontWeight: 700, color: M.muted, marginBottom: 8 }}>Istorija podizanja</div>
            <DateRange />
            {withdrawals.filter(w => w.issue_date?.slice(0,10) >= histFrom && w.issue_date?.slice(0,10) <= histTo).length === 0
              ? <div style={{ textAlign: "center", padding: 16, color: M.muted, fontSize: 12 }}>Nema podizanja</div>
              : withdrawals.filter(w => w.issue_date?.slice(0,10) >= histFrom && w.issue_date?.slice(0,10) <= histTo).map((w, i) => (
              <div key={i} style={{ ...M.card, padding: "8px 12px", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div><div style={{ fontWeight: 600, fontSize: 12, color: M.orange }}>-{fmt(w.deposit_amount)} €</div><div style={{ fontSize: 10, color: M.muted }}>{fmtDateTime(w.issue_date)}{w.fic ? ` · FIC: ${w.fic.slice(0,12)}...` : ""}</div></div>
                <button onClick={async () => { if (isSunmi) { const ww = 32; const lr = (l, r) => l + " ".repeat(Math.max(1, ww - l.length - r.length)) + r; let text = "=".repeat(ww) + "\n  PODIZANJE GOTOVINE\n" + "=".repeat(ww) + "\n"; text += (user?.company_name || "") + "\nTCR: " + tcr + "\n" + "-".repeat(ww) + "\n"; text += lr("Iznos:", fmt(w.deposit_amount) + " EUR") + "\n"; text += lr("Datum:", fmtDateTime(w.issue_date)) + "\n"; if (w.fic) text += "-".repeat(ww) + "\nIKOF/FIC:\n" + w.fic + "\n"; text += "=".repeat(ww) + "\n\n\n"; await sunmiPrintText(text); } else { const token = localStorage.getItem("minipos_token"); const r = await fetch(`${API_BASE}/cashregister/withdrawalText/${w.id}`, { headers: { Authorization: `Bearer ${token}` } }); if (r.ok) { const text = await r.text(); await thermalPrint("/print/text", { text }); } } }} style={M.btnSm("#f3f4f6", M.muted)}><Printer size={11} /></button>
              </div>
            ))}
          </div>
        )}

        {/* ═══ OTVARANJA TAB (non-internal) ═══ */}
        {blagajnaTab === "otvaranja" && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: M.muted, marginBottom: 8 }}>Istorija otvaranja</div>
            <DateRange />
            {sessions.filter(s => s.opened_at?.slice(0,10) >= histFrom && s.opened_at?.slice(0,10) <= histTo).length === 0
              ? <div style={{ textAlign: "center", padding: 16, color: M.muted, fontSize: 12 }}>Nema otvaranja</div>
              : sessions.filter(s => s.opened_at?.slice(0,10) >= histFrom && s.opened_at?.slice(0,10) <= histTo).map(s => (
              <div key={s.id} style={{ ...M.card, padding: "8px 12px", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div><div style={{ fontWeight: 600, fontSize: 12 }}>{fmtDate(s.opened_at)}</div><div style={{ fontSize: 10, color: M.muted }}>Depozit: {fmt(s.opening_cash || 0)} €</div></div>
                <button onClick={async () => { const token = localStorage.getItem("minipos_token"); const pw = parseInt(localStorage.getItem("minipos_paper_width") || "58"); const wp = pw <= 58 ? "&width=32" : ""; const r = await fetch(`${API_BASE}/cashregister/openingPdf/${s.id}?type=text${wp}`, { headers: { Authorization: `Bearer ${token}` } }); if (r.ok) { const text = await r.text(); if (isSunmi) { await sunmiPrintText(text); } else { await thermalPrint("/print/text", { text }); } } }} style={M.btnSm("#f3f4f6", M.muted)}><Printer size={11} /></button>
              </div>
            ))}
          </div>
        )}

        {/* ═══ ZATVARANJA TAB (non-internal) ═══ */}
        {blagajnaTab === "zatvaranja" && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: M.muted, marginBottom: 8 }}>Istorija zatvaranja</div>
            <DateRange />
            {sessions.filter(s => s.closed_at && s.closed_at?.slice(0,10) >= histFrom && s.closed_at?.slice(0,10) <= histTo).length === 0
              ? <div style={{ textAlign: "center", padding: 16, color: M.muted, fontSize: 12 }}>Nema zatvaranja</div>
              : sessions.filter(s => s.closed_at && s.closed_at?.slice(0,10) >= histFrom && s.closed_at?.slice(0,10) <= histTo).map(s => (
              <div key={s.id} style={{ ...M.card, padding: "8px 12px", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{fmtDate(s.closed_at)}</div>
                  <div style={{ fontSize: 10, color: M.muted }}>Promet: {fmt(s.total_sales || 0)}€ · Razlika: <span style={{ color: parseFloat(s.difference || 0) === 0 ? M.green : M.red }}>{fmt(s.difference || 0)}€</span></div>
                </div>
                <button onClick={async () => { await thermalPrint("/print/register", { type: "closing", company_name: user?.company_name || "", date: fmtDateTime(s.opened_at), opened_at: fmtDateTime(s.opened_at), closed_at: fmtDateTime(s.closed_at), operator_name: s.operator_name || "", tcr_code: tcr, deposit_amount: s.opening_cash || 0, opening_cash: s.opening_cash, cash_sales: s.cash_sales, card_sales: s.card_sales, total_sales: s.total_sales, transaction_count: s.transaction_count, expected_cash: s.expected_cash, actual_cash: s.actual_cash, difference: s.difference }); }} style={M.btnSm("#f3f4f6", M.muted)}><Printer size={11} /></button>
              </div>
            ))}
            <button onClick={() => setShowCloseAny(true)} style={{ ...M.btn("#f59e0b"), width: "100%", justifyContent: "center", padding: 9, fontSize: 12, marginTop: 10 }}><Clock size={13} /><span style={{ marginLeft: 6 }}>Zatvori za bilo koji dan</span></button>
          </div>
        )}
      </>}

      {/* Close Any Day Modal */}
      {showCloseAny && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => setShowCloseAny(false)}>
          <div style={{ background: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 420, maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Zatvori za odabrani dan</div>
            <input type="date" value={closeAnyDate} onChange={e => setCloseAnyDate(e.target.value)} style={{ ...M.input, marginBottom: 10 }} />
            <input type="number" step="0.01" value={closeAnyCash} onChange={e => setCloseAnyCash(e.target.value)} placeholder="Stvarna gotovina €" style={{ ...M.input, marginBottom: 14 }} />
            <button disabled={submitting || !closeAnyDate} onClick={async () => {
              setSubmitting(true);
              const d = await api("/cashregister/closeAnyDate", { method: "POST", body: JSON.stringify({ tcr_code: tcr, target_date: closeAnyDate, actual_cash: parseFloat(closeAnyCash) || 0 }) });
              setSubmitting(false);
              if (d.success) { showToast("Zatvoreno!"); setShowCloseAny(false); setCloseAnyDate(""); setCloseAnyCash(""); load(); } else showToast(d.message, "error");
            }} style={{ ...M.btn("#f59e0b"), width: "100%", justifyContent: "center", padding: 12 }}>{submitting ? "..." : "Zatvori"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
// ═══ PRESJEK STANJA RENDER ═══
// Z-style snapshot: cash flow, turnover (cash/non-cash/total), invoice type
// blocks (storno/avans/offline/order), per-rate VAT, payment methods.
// Backend returns the data already aggregated — this component is purely
// presentational (mobile-tuned: tighter padding, smaller fonts than POS).
function PresjekRender({ data, vatByRate }) {
  if (!data) return null;
  const f = (n) => parseFloat(n || 0).toFixed(2);
  const cf = data.cash_flow || {};
  const tc = data.turnover_cash || {};
  const tn = data.turnover_noncash || {};
  const tt = data.turnover_total || {};
  const it = data.invoice_types || {};
  const p = data.payments || {};

  const sectionStyle = { background: "#f9fafb", borderRadius: 8, padding: 10, marginBottom: 8, border: "1px solid #e5e7eb" };
  const titleStyle = { fontWeight: 700, fontSize: 11, color: "#92400e", marginBottom: 6, textAlign: "center", paddingBottom: 4, borderBottom: "1px dashed #d1d5db", textTransform: "uppercase", letterSpacing: 0.3 };
  const lineStyle = { display: "flex", justifyContent: "space-between", padding: "2px 0", fontSize: 12 };
  const Line = ({ label, value, bold }) => (
    <div style={{ ...lineStyle, fontWeight: bold ? 700 : 400 }}>
      <span style={{ color: bold ? "#111" : "#374151" }}>{label}</span>
      <span style={{ color: bold ? "#059669" : "#111", fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );

  const rates = [21, 7, 0, 15];
  const byRate = {};
  (vatByRate || []).forEach(vr => { byRate[Math.round(Number(vr.vat_rate))] = vr; });

  let totalNet = 0, totalVat = 0, totalGross = 0;
  (vatByRate || []).forEach(vr => {
    totalNet += Number(vr.net_base || 0);
    totalVat += Number(vr.vat_total || 0);
    totalGross += Number(vr.gross_total || 0);
  });

  return (
    <div>
      {/* Cash flow */}
      <div style={sectionStyle}>
        <Line label="Gotovina u ENU:" value={f(cf.cash_in_enu)} bold />
        <Line label="Depozit:" value={f(cf.deposit)} />
        <Line label="Podignuta gotovina:" value={f(cf.withdraw)} />
        <Line label="Naplaćeno novčanice:" value={f(cf.collected_banknote)} />
        <Line label="Naplaćeno got. ostalo:" value={f(cf.collected_other_cash)} />
        <Line label="Naplaćeno bezgotovina:" value={f(cf.collected_noncash)} />
        <Line label="Naplaćeno ukupno:" value={f(cf.collected_total)} bold />
      </div>

      {/* Gotovinski promet */}
      <div style={sectionStyle}>
        <div style={titleStyle}>Gotovinski promet</div>
        <Line label="Cijena:" value={f(tc.price)} />
        <Line label="Popust:" value={f(tc.discount)} />
        <Line label="Za uplatu:" value={f(tc.to_pay)} bold />
      </div>

      {/* Bezgotovinski promet */}
      <div style={sectionStyle}>
        <div style={titleStyle}>Bezgotovinski promet</div>
        <Line label="Cijena:" value={f(tn.price)} />
        <Line label="Popust:" value={f(tn.discount)} />
        <Line label="Za uplatu:" value={f(tn.to_pay)} bold />
      </div>

      {/* Ukupan promet */}
      <div style={sectionStyle}>
        <div style={titleStyle}>Ukupan promet</div>
        <Line label="Cijena:" value={f(tt.price)} />
        <Line label="Popust:" value={f(tt.discount)} />
        <Line label="Za uplatu:" value={f(tt.to_pay)} bold />
      </div>

      {/* Invoice type blocks */}
      {[
        { key: "storno", title: "Korektivni računi" },
        { key: "avans", title: "Avansni računi" },
        { key: "offline", title: "Offline računi" },
        { key: "order", title: "Order računi" },
      ].map(b => {
        const blk = it[b.key] || { count: 0, revenue: 0, tax: 0 };
        return (
          <div key={b.key} style={sectionStyle}>
            <div style={titleStyle}>{b.title}</div>
            <Line label="Broj:" value={String(blk.count)} />
            <Line label="Promet:" value={f(blk.revenue)} />
            <Line label="Porez:" value={f(blk.tax)} />
          </div>
        );
      })}

      {/* Per-rate VAT blocks */}
      {rates.map(r => {
        const vr = byRate[r] || { net_base: 0, vat_total: 0, gross_total: 0 };
        return (
          <div key={r} style={sectionStyle}>
            <div style={titleStyle}>Poreska stopa - {r.toFixed(2)}%</div>
            <Line label="Osnovica:" value={f(vr.net_base)} />
            <Line label="Iznos poreza:" value={f(vr.vat_total)} />
            <Line label="Promet:" value={f(vr.gross_total)} />
          </div>
        );
      })}

      {/* Ukupan porez */}
      <div style={sectionStyle}>
        <div style={titleStyle}>Ukupan porez</div>
        <Line label="Osnovica:" value={f(totalNet)} />
        <Line label="Iznos poreza:" value={f(totalVat)} />
        <Line label="Promet:" value={f(totalGross)} bold />
      </div>

      {/* Plaćanja */}
      <div style={sectionStyle}>
        <div style={titleStyle}>Plaćanja</div>
        <Line label="Novčanice i kovanice:" value={f(p.banknote)} />
        <Line label="Kreditna kartica:" value={f(p.creditcard)} />
        <Line label="Order:" value={f(p.order)} />
        <Line label="Ostalo (gotovina):" value={f(p.cash_other)} />
        <Line label="Virman:" value={f(p.virman)} />
        <Line label="Biznis kred. kartica:" value={f(p.business_card)} />
        <Line label="Kartica prodavca:" value={f(p.vendor_card)} />
        <Line label="Order (avans):" value={f(p.order_advance)} />
        <Line label="Faktoring:" value={f(p.factoring)} />
        <Line label="Avans:" value={f(p.avans)} />
        <Line label="Vaučer:" value={f(p.voucher)} />
        <Line label="Bakšiš:" value={f(p.tip)} />
      </div>
    </div>
  );
}

function DashboardView({ user, showToast }) {
  const [stats, setStats] = useState(null); const [monthStats, setMonthStats] = useState(null); const [yearStats, setYearStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAmounts, setShowAmounts] = useState(false);
  const [reportTab, setReportTab] = useState("pregled");
  const [internalEnabled, setInternalEnabled] = useState(false);
  const [internalHistory, setInternalHistory] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  // Detailed report state
  const [detReportType, setDetReportType] = useState("daily");
  const [detDate, setDetDate] = useState(() => todayLocal());
  const [detDateFrom, setDetDateFrom] = useState(() => daysAgoLocal(7));
  const [detDateTo, setDetDateTo] = useState(() => todayLocal());
  const [detMonth, setDetMonth] = useState(() => monthLocal());
  const [detYear, setDetYear] = useState(() => String(new Date().getFullYear()));
  const [detReport, setDetReport] = useState(null);
  const [detLoading, setDetLoading] = useState(false);
  // Inspector state
  const [inspType, setInspType] = useState("presjek");
  const [inspFrom, setInspFrom] = useState(() => todayLocal());
  const [inspTo, setInspTo] = useState(() => todayLocal());
  const [inspReport, setInspReport] = useState(null);
  const [inspLoading, setInspLoading] = useState(false);
  const [ikofFields, setIkofFields] = useState({ ikofPib: "", ikofDate: "", ikofInvNum: "", ikofBu: "", ikofTcr: "", ikofSoft: "", ikofAmount: "" });
  const [ikofResult, setIkofResult] = useState(null);
  const tcr = getTcr();

  useEffect(() => { loadReports(); }, []);
  const loadReports = async () => {
    const tp = tcr ? `&tcr_code=${tcr}` : "";
    try {
      const [t, m, y, idSet, idHist, invs] = await Promise.all([
        api(`/invoices/stats?period=today${tp}`), api(`/invoices/stats?period=month${tp}`), api(`/invoices/stats?period=year${tp}`),
        api("/internalday/settings"), api(`/internalday/history/${tcr || "all"}`), api(`/invoices?per_page=10${tcr ? "&tcr_code=" + tcr : ""}`)
      ]);
      if (t.success) setStats(t.data?.stats); if (m.success) setMonthStats(m.data?.stats); if (y.success) setYearStats(y.data?.stats);
      if (idSet.success) setInternalEnabled(!!idSet.data?.internal_day_enabled);
      if (idHist.success) setInternalHistory(idHist.data?.days || []);
      if (invs.success) setRecentInvoices(invs.data?.invoices || []);
    } catch(e) {}
    setLoading(false);
  };

  const printReport = async (type, params) => {
    const token = localStorage.getItem("minipos_token");
    const tp = tcr ? `&tcr_code=${tcr}` : "";
    const wp = isSunmi ? "&width=32" : "";
    
    if (type === "inspector") {
      // Inspector reports have a text endpoint
      const url = `${API_BASE}/invoices/inspectorReportText?${params}${tp}${wp}`;
      try {
        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const text = await r.text();
          const ok = await thermalPrint("/print/text", { text });
          if (ok) showToast("Štampano!"); else systemPrintText(text);
        }
      } catch(e) { showToast("Greška pri štampi", "error"); }
    } else {
      // Detailed reports — fetch JSON and format text locally
      const url = `${API_BASE}/invoices/detailedReport?${params}${tp}`;
      try {
        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
        if (r.ok) {
          const data = await r.json();
          if (data.success && data.data) {
            const d = data.data;
            const s = d.summary || {};
            const w = isSunmi ? 32 : 48;
            const lr = (l, r) => l + " ".repeat(Math.max(1, w - l.length - r.length)) + r;
            const ct = (t) => " ".repeat(Math.max(0, Math.floor((w - t.length) / 2))) + t;
            let text = ct(d.type === "daily" ? "DNEVNI IZVJESTAJ" : d.type === "monthly" ? "MJESECNI IZVJESTAJ" : "GODISNJI IZVJESTAJ") + "\n";
            text += ct("Period: " + (d.label || "")) + "\n";
            text += "=".repeat(w) + "\n";
            text += lr("Racuni:", (s.total_invoices || 0) + "") + "\n";
            text += lr("Gotovina:", fmt(s.cash_revenue || 0) + " EUR") + "\n";
            text += lr("Kartica:", fmt(s.card_revenue || 0) + " EUR") + "\n";
            if (parseFloat(s.other_revenue || 0) > 0) text += lr("Ostalo:", fmt(s.other_revenue) + " EUR") + "\n";
            text += lr("PDV:", fmt(s.total_vat || 0) + " EUR") + "\n";
            text += lr("Bez PDV:", fmt(s.total_without_vat || 0) + " EUR") + "\n";
            text += "=".repeat(w) + "\n";
            text += lr("UKUPNO:", fmt(s.total_revenue || 0) + " EUR") + "\n";
            text += "=".repeat(w) + "\n";
            if (d.storno) text += lr("Storno:", (d.storno.cnt || 0) + " (" + fmt(d.storno.amount || 0) + " EUR)") + "\n";
            text += "\n" + ct("EFIS.PRO") + "\n\n\n";
            const ok = await thermalPrint("/print/text", { text });
            if (ok) showToast("Štampano!"); else systemPrintText(text);
          }
        }
      } catch(e) { showToast("Greška pri štampi", "error"); }
    }
  };

  const exportCsv = async (data, filename) => {
    const isNative = !!(window.Capacitor?.isNativePlatform?.());
    if (isNative) {
      const csvContent = "\ufeff" + data;
      // Try native plugin first (Android)
      const sp = window.Capacitor?.Plugins?.SunmiPrinter;
      if (sp && sp.saveBase64File) {
        const bytes = new TextEncoder().encode(csvContent);
        let binary = "";
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const b64 = btoa(binary);
        const result = await saveFileToDevice(b64, filename, "text/csv");
        if (result.success) { showToast("CSV sacuvan: " + (result.location || "Downloads/" + filename)); return; }
      }
      // Fallback for iOS: use Capacitor Filesystem plugin
      const Fs = window.Capacitor?.Plugins?.Filesystem;
      if (Fs) {
        try {
          const bytes = new TextEncoder().encode(csvContent);
          let binary = "";
          for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
          const b64 = btoa(binary);
          await Fs.writeFile({ path: filename, data: b64, directory: "DOCUMENTS", recursive: true });
          showToast("CSV sacuvan: " + filename);
          return;
        } catch(e) { /* fall through */ }
      }
      // Last fallback: blob URL
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      showToast("CSV: " + filename);
    } else {
      const blob = new Blob(["\ufeff" + data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    }
  };

  const exportDetailed = () => {
    if (!detReport) return;
    const s = detReport.summary;
    let csv = `Izvještaj;${detReport.type === "daily" ? "Dnevni" : detReport.type === "monthly" ? "Mjesečni" : "Godišnji"}\nPeriod;${detReport.label}\n\n`;
    csv += `Računa;${s.total_invoices || 0}\nGotovina;${fmt(s.cash_revenue || 0)}\nKartica;${fmt(s.card_revenue || 0)}\n`;
    if (parseFloat(s.other_revenue || 0) > 0) csv += `Ostalo;${fmt(s.other_revenue)}\n`;
    csv += `Bez PDV;${fmt(s.total_without_vat || 0)}\nPDV;${fmt(s.total_vat || 0)}\nUKUPNO;${fmt(s.total_revenue || 0)}\n`;
    const items = detReport.days || detReport.months || [];
    if (items.length > 0) {
      csv += "\nDatum;Računi;Gotovina;Kartica;Ukupno\n";
      items.forEach(i => { csv += `${i.day || i.month};${i.invoices};${fmt(i.cash)};${fmt(i.card)};${fmt(i.revenue)}\n`; });
    }
    exportCsv(csv, `izvjestaj_${detReport.type}_${detReport.label}.csv`);
  };

  const exportInspector = () => {
    if (!inspReport) return;
    // q() wraps any string in CSV-quoted form (escapes embedded quotes).
    const q = v => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    let csv = "";

    // ── Company header block ──────────────────────────────────────
    if (inspReport.company_info) {
      const ci = inspReport.company_info;
      csv += "Naziv;" + q(ci.business_name) + "\n";
      csv += "Adresa;" + q((ci.address || "") + (ci.city ? ", " + ci.city : "")) + "\n";
      csv += "PIB;" + q(ci.pib) + "\n";
    }
    csv += "Izvjestaj;" + q(inspReport.title) + "\n";
    csv += "TCR;" + q(inspReport.tcr_code) + "\n";
    if (inspReport.business_unit_codes && inspReport.business_unit_codes.length > 0) {
      csv += "Poslovna jedinica;" + q(inspReport.business_unit_codes.join(", ")) + "\n";
    }
    if (inspReport.company_info?.operator_code) {
      csv += "Operater izvjestaja;" + q(inspReport.company_info.operator_code) + "\n";
    }
    csv += "Period;" + q(inspReport.period) + "\n";
    if (inspReport.company_info?.generated_at) {
      csv += "Stampano;" + q(inspReport.company_info.generated_at) + "\n";
    }
    csv += "\n";

    // ── Presjek stanja blocks ─────────────────────────────────────
    if (inspReport.presjek) {
      const pj = inspReport.presjek;
      const cf = pj.cash_flow || {};
      csv += "Gotovina u ENU;" + fmt(cf.cash_in_enu || 0) + "\n";
      csv += "Depozit;" + fmt(cf.deposit || 0) + "\n";
      csv += "Podignuta gotovina;" + fmt(cf.withdraw || 0) + "\n";
      csv += "Naplaceno novcanice;" + fmt(cf.collected_banknote || 0) + "\n";
      csv += "Naplaceno got. ostalo;" + fmt(cf.collected_other_cash || 0) + "\n";
      csv += "Naplaceno bezgotovina;" + fmt(cf.collected_noncash || 0) + "\n";
      csv += "Naplaceno ukupno;" + fmt(cf.collected_total || 0) + "\n\n";

      const tc = pj.turnover_cash || {}, tn = pj.turnover_noncash || {}, tt = pj.turnover_total || {};
      csv += "GOTOVINSKI PROMET\n";
      csv += "Cijena;" + fmt(tc.price || 0) + "\nPopust;" + fmt(tc.discount || 0) + "\nZa uplatu;" + fmt(tc.to_pay || 0) + "\n\n";
      csv += "BEZGOTOVINSKI PROMET\n";
      csv += "Cijena;" + fmt(tn.price || 0) + "\nPopust;" + fmt(tn.discount || 0) + "\nZa uplatu;" + fmt(tn.to_pay || 0) + "\n\n";
      csv += "UKUPAN PROMET\n";
      csv += "Cijena;" + fmt(tt.price || 0) + "\nPopust;" + fmt(tt.discount || 0) + "\nZa uplatu;" + fmt(tt.to_pay || 0) + "\n\n";

      const it = pj.invoice_types || {};
      [['storno','Korektivni racuni'],['avans','Avansni racuni'],['offline','Offline racuni'],['order','Order racuni']].forEach(([k,t]) => {
        const blk = it[k] || { count: 0, revenue: 0, tax: 0 };
        csv += t.toUpperCase() + "\n";
        csv += "Broj;" + (blk.count || 0) + "\nPromet;" + fmt(blk.revenue || 0) + "\nPorez;" + fmt(blk.tax || 0) + "\n\n";
      });

      const p = pj.payments || {};
      csv += "PLACANJA\n";
      csv += "Novcanice i kovanice;" + fmt(p.banknote || 0) + "\n";
      csv += "Kreditna kartica;" + fmt(p.creditcard || 0) + "\n";
      csv += "Order;" + fmt(p.order || 0) + "\n";
      csv += "Ostalo (gotovina);" + fmt(p.cash_other || 0) + "\n";
      csv += "Virman;" + fmt(p.virman || 0) + "\n";
      csv += "Biznis kred. kartica;" + fmt(p.business_card || 0) + "\n";
      csv += "Kartica prodavca;" + fmt(p.vendor_card || 0) + "\n";
      csv += "Order (avans);" + fmt(p.order_advance || 0) + "\n";
      csv += "Faktoring;" + fmt(p.factoring || 0) + "\n";
      csv += "Avans;" + fmt(p.avans || 0) + "\n";
      csv += "Vaucer;" + fmt(p.voucher || 0) + "\n";
      csv += "Baksis;" + fmt(p.tip || 0) + "\n\n";
    }

    // ── Standard summary block (only when there's no presjek) ─────
    if (inspReport.summary && !inspReport.presjek) {
      const s = inspReport.summary;
      csv += "Ukupno racuna;" + (s.total_invoices || 0) + "\n";
      csv += "Gotovina;" + fmt(s.cash_revenue || 0) + "\n";
      csv += "Kartica;" + fmt(s.card_revenue || 0) + "\n";
      if (parseFloat(s.other_revenue || 0) > 0) csv += "Ostalo;" + fmt(s.other_revenue) + "\n";
      csv += "Bez PDV;" + fmt(s.total_without_vat || 0) + "\n";
      csv += "PDV;" + fmt(s.total_vat || 0) + "\n";
      csv += "UKUPNO;" + fmt(s.total_revenue || 0) + "\n\n";
    }

    // ── PDV po stopama ────────────────────────────────────────────
    if (inspReport.vat_by_rate && inspReport.vat_by_rate.length > 0) {
      csv += "PDV PO STOPAMA\n";
      csv += "Stopa;Br. stavki;Osnovica;PDV iznos;Bruto\n";
      inspReport.vat_by_rate.forEach(vr => {
        csv += (vr.vat_rate || 0) + "%;" + (vr.item_count || 0) + ";" + fmt(vr.net_base || 0) + ";" + fmt(vr.vat_total || 0) + ";" + fmt(vr.gross_total || 0) + "\n";
      });
      csv += "\n";
    }

    // ── Invoice list (for journal / offline / corrective) ─────────
    if (inspReport.invoices && inspReport.invoices.length > 0) {
      const hasItems = inspReport.invoices.some(inv => inv.items && inv.items.length > 0);
      if (hasItems) {
        csv += "Broj racuna;Datum;Iznos;Nacin placanja;FIC;Operater;PJ;TCR;Storno;Stavka;Kol.;Cijena;Ukupno;PDV%;PDV iznos\n";
        inspReport.invoices.forEach(inv => {
          csv += q(inv.invoice_number) + ";" + q(inv.invoice_date) + ";" + fmt(inv.total_amount || 0) + ";" + q(inv.payment_method) + ";" + q(inv.fic) + ";" + q(inv.operator_code) + ";" + q(inv.business_unit_code) + ";" + q(inv.tcr_code) + ";" + (inv.is_storno ? "DA" : "") + ";;;;;;\n";
          if (inv.items) inv.items.forEach(item => {
            csv += ";;;;;;;;;" + q(item.name) + ";" + item.quantity + ";" + fmt(item.unit_price || 0) + ";" + fmt(item.total_price || 0) + ";" + (item.vat_rate || 0) + "%;" + fmt(item.vat_amount || 0) + "\n";
          });
        });
      } else {
        csv += "Broj racuna;Datum;Iznos;Nacin placanja;FIC;Operater;PJ;TCR;Storno\n";
        inspReport.invoices.forEach(inv => {
          csv += q(inv.invoice_number) + ";" + q(inv.invoice_date) + ";" + fmt(inv.total_amount || 0) + ";" + q(inv.payment_method) + ";" + q(inv.fic) + ";" + q(inv.operator_code) + ";" + q(inv.business_unit_code) + ";" + q(inv.tcr_code) + ";" + (inv.is_storno ? "DA" : "") + "\n";
        });
      }
    }

    // ── Daily subtotals (periodic / journal) ──────────────────────
    if (inspReport.daily_subtotals && inspReport.daily_subtotals.length > 0) {
      csv += "\nDatum;Broj racuna;Promet\n";
      inspReport.daily_subtotals.forEach(ds => { csv += q(ds.day) + ";" + (ds.count || 0) + ";" + fmt(ds.revenue || 0) + "\n"; });
    }

    exportCsv(csv, `inspektor_${inspType}_${inspFrom}_${inspTo}.csv`);
  };

  if (loading) return <div style={{ textAlign: "center", padding: 40 }}><Loader2 size={22} className="spin" color="#bbb" /></div>;

  const MiniStat = ({ l, v, c: clr, i: Icon, hidden }) => (
    <div style={{ ...M.card, padding: 12, display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 34, height: 34, borderRadius: 9, background: clr + "15", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={16} color={clr} /></div>
      <div><div style={{ fontSize: 11, color: M.muted }}>{l}</div><div style={{ fontSize: 16, fontWeight: 700 }}>{hidden ? "••••••" : v}</div></div>
    </div>
  );

  return (
    <div style={{ height: "100%", overflow: "auto", padding: "12px", paddingLeft: "max(12px, env(safe-area-inset-left, 12px))", paddingRight: "max(12px, env(safe-area-inset-right, 12px))", WebkitOverflowScrolling: "touch" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>Izvještaji</div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setShowAmounts(!showAmounts)} style={{ padding: "5px 10px", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", background: showAmounts ? M.dark : "#f3f4f6", color: showAmounts ? "white" : M.muted }}>{showAmounts ? "Sakrij" : "Prikaži"}</button>
          <button onClick={loadReports} style={{ padding: "5px 10px", border: "none", borderRadius: 6, background: "#f3f4f6", cursor: "pointer" }}><RefreshCw size={12} color={M.muted} /></button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
        {[{ id: "pregled", l: "Pregled" }, { id: "detaljni", l: "Detaljni" }, ...(internalEnabled ? [{ id: "interni", l: "Interni" }] : []), { id: "inspektor", l: "Inspektor" }].map(t => (
          <button key={t.id} onClick={() => setReportTab(t.id)} style={{ padding: "8px 14px", border: "none", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", background: reportTab === t.id ? M.dark : "#f3f4f6", color: reportTab === t.id ? "white" : M.muted, fontFamily: M.font }}>{t.l}</button>
        ))}
      </div>

      {reportTab === "pregled" && <>
        <div style={{ fontSize: 11, fontWeight: 600, color: M.muted, marginBottom: 6, textTransform: "uppercase" }}>Danas</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
          <MiniStat l="Racuni" v={stats?.total_invoices || 0} c={M.blue} i={Receipt} />
          <MiniStat l="Promet" v={`${fmt(stats?.total_revenue || 0)}€`} c={M.green} i={TrendingUp} hidden={!showAmounts} />
          <MiniStat l="Gotovina" v={`${fmt(stats?.cash_revenue || 0)}€`} c={M.orange} i={Banknote} hidden={!showAmounts} />
          <MiniStat l="Kartica" v={`${fmt(stats?.card_revenue || 0)}€`} c="#7c3aed" i={CreditCard} hidden={!showAmounts} />
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: M.muted, marginBottom: 6, textTransform: "uppercase" }}>Ovaj mjesec</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
          <MiniStat l="Racuni" v={monthStats?.total_invoices || 0} c={M.blue} i={Receipt} />
          <MiniStat l="Promet" v={`${fmt(monthStats?.total_revenue || 0)}€`} c={M.green} i={TrendingUp} hidden={!showAmounts} />
          <MiniStat l="Gotovina" v={`${fmt(monthStats?.cash_revenue || 0)}€`} c={M.orange} i={Banknote} hidden={!showAmounts} />
          <MiniStat l="Kartica" v={`${fmt(monthStats?.card_revenue || 0)}€`} c="#7c3aed" i={CreditCard} hidden={!showAmounts} />
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: M.muted, marginBottom: 6, textTransform: "uppercase" }}>Ova godina</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <MiniStat l="Racuni" v={yearStats?.total_invoices || 0} c={M.blue} i={Receipt} />
          <MiniStat l="Promet" v={`${fmt(yearStats?.total_revenue || 0)}€`} c={M.green} i={TrendingUp} hidden={!showAmounts} />
          <MiniStat l="Gotovina" v={`${fmt(yearStats?.cash_revenue || 0)}€`} c={M.orange} i={Banknote} hidden={!showAmounts} />
          <MiniStat l="Kartica" v={`${fmt(yearStats?.card_revenue || 0)}€`} c="#7c3aed" i={CreditCard} hidden={!showAmounts} />
        </div>
      </>}

      {/* ═══ DETALJNI IZVJESTAJI ═══ */}
      {reportTab === "detaljni" && (
        <div>
          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
            {[{ id: "daily", l: "Dnevni" }, { id: "monthly", l: "Mjes." }, { id: "yearly", l: "God." }].map(t => (
              <button key={t.id} onClick={() => { setDetReportType(t.id); setDetReport(null); }} style={{ flex: 1, padding: "6px", border: `1px solid ${detReportType === t.id ? M.blue : M.border}`, borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", background: detReportType === t.id ? M.blue : "white", color: detReportType === t.id ? "white" : M.muted, fontFamily: M.font }}>{t.l}</button>
            ))}
          </div>
          <div style={{ ...M.card, padding: 12, marginBottom: 10 }}>
            {detReportType === "daily" && (
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <input type="date" value={detDateFrom} onChange={e => setDetDateFrom(e.target.value)} style={{ ...M.input, padding: "6px 8px", fontSize: 12, flex: 1 }} />
                <span style={{ color: M.muted, fontSize: 11 }}>—</span>
                <input type="date" value={detDateTo} onChange={e => setDetDateTo(e.target.value)} style={{ ...M.input, padding: "6px 8px", fontSize: 12, flex: 1 }} />
              </div>
            )}
            {detReportType === "monthly" && <input type="month" value={detMonth} onChange={e => setDetMonth(e.target.value)} style={{ ...M.input, padding: "6px 8px", fontSize: 12 }} />}
            {detReportType === "yearly" && <select value={detYear} onChange={e => setDetYear(e.target.value)} style={{ ...M.input, padding: "6px 8px", fontSize: 12 }}>{Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}</select>}
            <button onClick={async () => {
              setDetLoading(true);
              const tp = tcr ? `&tcr_code=${tcr}` : "";
              let url = `/invoices/detailedReport?type=${detReportType}${tp}`;
              if (detReportType === "daily") url += `&date_from=${detDateFrom}&date_to=${detDateTo}`;
              else if (detReportType === "monthly") url += `&month=${detMonth}`;
              else url += `&year=${detYear}`;
              const d = await api(url);
              setDetLoading(false);
              if (d.success) setDetReport(d.data); else showToast(d.message || "Greška", "error");
            }} disabled={detLoading} style={{ ...M.btn(M.blue, "white"), width: "100%", justifyContent: "center", padding: 10, marginTop: 8, fontSize: 13 }}>
              {detLoading ? <Loader2 size={14} className="spin" /> : <BarChart3 size={14} />}<span style={{ marginLeft: 6 }}>Generiši</span>
            </button>
          </div>
          {detReport && (() => {
            const s = detReport.summary;
            return (
              <div style={{ ...M.card, padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{detReport.label}</div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => printReport("detailed", `type=${detReportType}${detReportType === "daily" ? `&date_from=${detDateFrom}&date_to=${detDateTo}` : detReportType === "monthly" ? `&month=${detMonth}` : `&year=${detYear}`}`)} style={M.btnSm("#f3f4f6", M.muted)}><Printer size={12} /></button>
                    <button onClick={exportDetailed} style={M.btnSm("#f3f4f6", M.muted)}><Download size={12} /></button>
                  </div>
                </div>
                <Row label="Računa" value={s.total_invoices || 0} />
                <Row label="Gotovina" value={showAmounts ? `${fmt(s.cash_revenue)} €` : "••••••"} />
                <Row label="Kartica" value={showAmounts ? `${fmt(s.card_revenue)} €` : "••••••"} />
                {parseFloat(s.other_revenue || 0) > 0 && <Row label="Ostalo" value={showAmounts ? `${fmt(s.other_revenue)} €` : "••••••"} />}
                <Row label="Bez PDV" value={showAmounts ? `${fmt(s.total_without_vat)} €` : "••••••"} />
                <Row label="PDV" value={showAmounts ? `${fmt(s.total_vat)} €` : "••••••"} />
                <div style={{ borderTop: "2px solid #111", marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontWeight: 800, fontSize: 15 }}>UKUPNO</span>
                  <span style={{ fontWeight: 800, fontSize: 15, color: M.green }}>{showAmounts ? `${fmt(s.total_revenue)} €` : "••••••"}</span>
                </div>
                {detReport.storno && parseInt(detReport.storno.cnt) > 0 && (
                  <div style={{ background: "#fef2f2", borderRadius: 6, padding: 8, marginTop: 8, fontSize: 11, color: "#991b1b" }}>Stornirano: <strong>{detReport.storno.cnt}</strong> ({fmt(detReport.storno.amount)} €)</div>
                )}
                {(detReport.days?.length > 0 || detReport.months?.length > 0) && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: M.muted, marginBottom: 6 }}>{detReport.months ? "Po mjesecima" : "Po danima"}</div>
                    {(detReport.days || detReport.months || []).map((item, idx) => (
                      <div key={idx} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f3f4f6", fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>{item.day || item.month}</span>
                        <div style={{ textAlign: "right" }}>
                          <span style={{ color: M.muted }}>{item.invoices} rač. </span>
                          <span style={{ fontWeight: 700 }}>{showAmounts ? `${fmt(item.revenue)} €` : "••"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ═══ INTERNI DANI ═══ */}
      {reportTab === "interni" && (
        <div>
          {internalHistory.filter(d => d.status === "closed" && d.is_official).length === 0
            ? <div style={{ textAlign: "center", padding: 30, color: M.muted }}><Clock size={28} /><p style={{ fontSize: 12, marginTop: 8 }}>Nema podataka</p></div>
            : internalHistory.filter(d => d.status === "closed" && d.is_official).slice(0, 30).map(d => (
            <div key={d.id} style={{ ...M.card, padding: 12, marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{d.opened_at?.slice(0, 10)}</span>
                  <span style={{ fontWeight: 700, color: M.green }}>{showAmounts ? `${fmt(d.total_sales)} €` : "••••••"}</span>
                </div>
                <div style={{ fontSize: 11, color: M.muted }}>{d.opened_at?.slice(11, 16)} - {d.closed_at?.slice(11, 16)} · {d.transaction_count} rac. · Got: {showAmounts ? `${fmt(d.cash_sales)}€` : "••"}</div>
              </div>
              <button onClick={async () => { await thermalPrint("/print/register", { type: "closing", company_name: user?.company_name || "", date: fmtDateTime(d.opened_at), opened_at: fmtDateTime(d.opened_at), closed_at: fmtDateTime(d.closed_at), operator_name: d.operator_name || "", tcr_code: tcr, deposit_amount: 0, opening_cash: 0, cash_sales: d.cash_sales || 0, card_sales: d.card_sales || 0, total_sales: d.total_sales || 0, transaction_count: d.transaction_count || 0, expected_cash: d.expected_cash || 0, actual_cash: d.actual_cash || 0, difference: d.difference || 0 }); }} style={{ ...M.btnSm("#f3f4f6", M.muted), marginLeft: 6 }}><Printer size={11} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ═══ PORESKI INSPEKTOR ═══ */}
      {reportTab === "inspektor" && (
        <div>
          <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
            {[{ id: "presjek", l: "Presjek" }, { id: "status", l: "Trenutno" }, { id: "daily", l: "Dnevni" }, { id: "periodic", l: "Period" }, { id: "journal", l: "Žurnal" }, { id: "offline", l: "Offline" }, { id: "corrective", l: "Korektiv." }, { id: "ikof", l: "IKOF" }].map(t => (
              <button key={t.id} onClick={() => { setInspType(t.id); setInspReport(null); }} style={{ padding: "7px 10px", border: `1px solid ${inspType === t.id ? M.blue : M.border}`, borderRadius: 5, fontSize: 10, fontWeight: 600, cursor: "pointer", background: inspType === t.id ? M.blue : "white", color: inspType === t.id ? "white" : M.muted, fontFamily: M.font }}>{t.l}</button>
            ))}
          </div>

          {/* IKOF Recreation */}
          {inspType === "ikof" ? (
            <div style={{ ...M.card, padding: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>IKOF rekreacija</div>
              <div style={{ fontSize: 11, color: M.muted, marginBottom: 10 }}>Unesite parametre računa za provjeru IKOF-a</div>
              {[{ k: "ikofPib", l: "PIB", p: "PIB obveznika" }, { k: "ikofDate", l: "Datum", p: "2026-03-14T10:30:00+01:00" }, { k: "ikofInvNum", l: "Broj računa", p: "1/2026/ne548er229" }, { k: "ikofBu", l: "Kod PJ", p: "xx123xx456" }, { k: "ikofTcr", l: "Kod ENU", p: "ne548er229" }, { k: "ikofSoft", l: "Kod softvera", p: "ss123ss456" }, { k: "ikofAmount", l: "Iznos", p: "43.56" }].map(f => (
                <div key={f.k} style={{ marginBottom: 6 }}>
                  <label style={{ fontSize: 11, fontWeight: 600 }}>{f.l}</label>
                  <input value={ikofFields[f.k] || ""} onChange={e => setIkofFields(prev => ({ ...prev, [f.k]: e.target.value }))} placeholder={f.p} style={{ ...M.input, padding: "6px 8px", fontSize: 12, marginTop: 2 }} />
                </div>
              ))}
              <button onClick={async () => {
                const d = await api("/invoices/recreateIkof", { method: "POST", body: JSON.stringify({ pib: ikofFields.ikofPib, date: ikofFields.ikofDate, inv_num: ikofFields.ikofInvNum, bu_code: ikofFields.ikofBu, tcr_code: ikofFields.ikofTcr, soft_code: ikofFields.ikofSoft, total: ikofFields.ikofAmount }) });
                if (d.success) setIkofResult(d.data); else showToast(d.message || "Greška", "error");
              }} style={{ ...M.btn(M.dark, "white"), width: "100%", justifyContent: "center", padding: 10, marginTop: 8, fontSize: 13 }}>Generiši IKOF</button>
              {ikofResult && (
                <div style={{ marginTop: 10, background: "#f0fdf4", borderRadius: 8, padding: 12, border: "1px solid #bbf7d0" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#15803d", marginBottom: 4 }}>IKOF:</div>
                  <div style={{ fontFamily: M.mono, fontSize: 11, wordBreak: "break-all" }}>{ikofResult.iic}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#15803d", marginTop: 8, marginBottom: 2 }}>Potpis:</div>
                  <div style={{ fontFamily: M.mono, fontSize: 9, wordBreak: "break-all", color: M.muted }}>{ikofResult.iic_signature}</div>
                </div>
              )}
            </div>
          ) : <>

          {/* Date pickers for non-status types */}
          {inspType !== "status" && (
            <div style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center" }}>
              <input type="date" value={inspFrom} onChange={e => setInspFrom(e.target.value)} style={{ ...M.input, padding: "6px 8px", fontSize: 12, flex: 1 }} />
              <span style={{ color: M.muted, fontSize: 11 }}>—</span>
              <input type="date" value={inspTo} onChange={e => setInspTo(e.target.value)} style={{ ...M.input, padding: "6px 8px", fontSize: 12, flex: 1 }} />
            </div>
          )}
          <button onClick={async () => {
            setInspLoading(true);
            const tp = tcr ? `&tcr_code=${tcr}` : "";
            const d = await api(`/invoices/inspectorReport?type=${inspType}&date_from=${inspFrom}&date_to=${inspTo}${tp}`);
            setInspLoading(false);
            if (d.success) setInspReport(d.data); else showToast(d.message || "Greška", "error");
          }} disabled={inspLoading} style={{ ...M.btn(M.blue, "white"), width: "100%", justifyContent: "center", padding: 10, marginBottom: 10, fontSize: 13 }}>
            {inspLoading ? <Loader2 size={14} className="spin" /> : <BarChart3 size={14} />}<span style={{ marginLeft: 6 }}>Generiši</span>
          </button>

          {/* Report results */}
          {inspReport && (
            <div style={{ ...M.card, padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{inspReport.title}</div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => printReport("inspector", `type=${inspType}&date_from=${inspFrom}&date_to=${inspTo}`)} style={M.btnSm("#f3f4f6", M.muted)}><Printer size={12} /></button>
                  <button onClick={exportInspector} style={M.btnSm("#f3f4f6", M.muted)}><Download size={12} /></button>
                </div>
              </div>

              {/* ═══ Company / TCR / BU / Operator header ═══
                  Spec ch. 5.8 line 637 mandates ENU/BU/Operator codes on every
                  inspector report. Backend returns this in inspReport.company_info. */}
              {inspReport.company_info ? (
                <div style={{ background: "#fef3c7", borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 11, border: "1px solid #fde68a" }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "#92400e", marginBottom: 3 }}>{inspReport.company_info.business_name}</div>
                  {inspReport.company_info.address && <div style={{ color: "#78350f" }}>{inspReport.company_info.address}{inspReport.company_info.city ? `, ${inspReport.company_info.city}` : ""}</div>}
                  {inspReport.company_info.pib && <div style={{ color: "#78350f" }}>PIB: {inspReport.company_info.pib}</div>}
                  <div style={{ marginTop: 5, paddingTop: 5, borderTop: "1px solid #fde68a", color: "#78350f", display: "flex", flexWrap: "wrap", gap: "2px 10px" }}>
                    <span>TCR: <strong>{inspReport.tcr_code}</strong></span>
                    {inspReport.business_unit_codes && inspReport.business_unit_codes.length > 0 && <span>PJ: <strong>{inspReport.business_unit_codes.join(", ")}</strong></span>}
                    {inspReport.company_info.operator_code && <span>Op: <strong>{inspReport.company_info.operator_code}</strong></span>}
                  </div>
                  <div style={{ color: "#78350f", marginTop: 3 }}>Period: {inspReport.period}{inspReport.company_info.generated_at ? ` · Štampano: ${inspReport.company_info.generated_at}` : ""}</div>
                </div>
              ) : (
                <div style={{ fontSize: 11, color: M.muted, marginBottom: 8 }}>{inspReport.period}</div>
              )}

              {/* Summary block — only for non-presjek reports (presjek has its
                  own dedicated render below with much richer breakdown). */}
              {inspReport.summary && !inspReport.presjek && <>
                <Row label="Računa" value={inspReport.summary.total_invoices || 0} />
                <Row label="Gotovina" value={`${fmt(inspReport.summary.cash_revenue || 0)} €`} />
                <Row label="Kartica" value={`${fmt(inspReport.summary.card_revenue || 0)} €`} />
                {parseFloat(inspReport.summary.other_revenue || 0) > 0 && <Row label="Ostalo" value={`${fmt(inspReport.summary.other_revenue)} €`} />}

                {/* PDV po stopama — matches POS detail level for inspector audit */}
                {inspReport.vat_by_rate && inspReport.vat_by_rate.length > 0 && (
                  <div style={{ borderTop: "1px dashed #d1d5db", marginTop: 6, paddingTop: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: M.muted, marginBottom: 3, letterSpacing: 0.3 }}>PDV PO STOPAMA</div>
                    {inspReport.vat_by_rate.map((vr, idx) => (
                      <div key={idx} style={{ marginBottom: 3 }}>
                        <Row label={`${vr.vat_rate}% osnovica`} value={`${fmt(vr.net_base)} €`} />
                        <Row label={`${vr.vat_rate}% PDV`} value={`${fmt(vr.vat_total)} €`} />
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ borderTop: "1px dashed #d1d5db", marginTop: 6, paddingTop: 6 }}>
                  <Row label="Bez PDV" value={`${fmt(inspReport.summary.total_without_vat || 0)} €`} />
                  <Row label="PDV" value={`${fmt(inspReport.summary.total_vat || 0)} €`} />
                </div>
                <div style={{ borderTop: "2px solid #111", marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontWeight: 800 }}>UKUPNO</span>
                  <span style={{ fontWeight: 800, color: M.green }}>{fmt(inspReport.summary.total_revenue || 0)} €</span>
                </div>
              </>}

              {/* Z-style Presjek stanja — full cash flow + turnover + invoice
                  type blocks + per-rate VAT + 12-payment-method breakdown */}
              {inspReport.presjek && <PresjekRender data={inspReport.presjek} vatByRate={inspReport.vat_by_rate || []} />}

              {/* Invoice list with items (for journal/offline/corrective) */}
              {inspReport.invoices && inspReport.invoices.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: M.muted, marginBottom: 4 }}>Računi ({inspReport.invoices.length})</div>
                  {inspReport.invoices.map((inv, idx) => (
                    <div key={idx} style={{ ...M.card, padding: 10, marginBottom: 6 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 11, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.invoice_number}{inv.is_storno == 1 ? <span style={{ marginLeft: 4, color: M.red, fontWeight: 700 }}>STORNO</span> : null}</span>
                        <span style={{ fontWeight: 700, fontSize: 12, color: inv.is_storno == 1 ? M.red : M.green, flexShrink: 0 }}>{fmt(inv.total_amount)} €</span>
                      </div>
                      <div style={{ fontSize: 10, color: M.muted }}>{fmtDateTime(inv.invoice_date)} · {inv.payment_method}{inv.fic ? ` · FIC: ${inv.fic.slice(0,12)}...` : ""}</div>
                      {(inv.operator_code || inv.business_unit_code || (inv.tcr_code && !tcr)) && (
                        <div style={{ fontSize: 10, color: M.muted, marginTop: 2 }}>
                          {inv.operator_code && <span>OP: <strong>{inv.operator_code}</strong></span>}
                          {inv.business_unit_code && <span style={{ marginLeft: 6 }}>PJ: <strong>{inv.business_unit_code}</strong></span>}
                          {inv.tcr_code && !tcr && <span style={{ marginLeft: 6 }}>TCR: <strong>{inv.tcr_code}</strong></span>}
                        </div>
                      )}
                      {inv.items && inv.items.length > 0 && (
                        <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid #f3f4f6" }}>
                          {inv.items.map((it, ii) => (
                            <div key={ii} style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: M.muted }}>
                              <span>{it.name} × {fmtQty(it.quantity)}</span>
                              <span>{fmt(it.total_price || it.total_amount)} €</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {inspReport.daily_subtotals?.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: M.muted, marginBottom: 4 }}>Po danima</div>
                  {inspReport.daily_subtotals.map((ds, idx) => (
                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #f3f4f6", fontSize: 11 }}>
                      <span style={{ fontWeight: 600 }}>{ds.day}</span>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ color: M.muted }}>{ds.count} rač. </span>
                        <span style={{ fontWeight: 700 }}>{fmt(ds.revenue)} €</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </>}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// SETTINGS — Mobile scrollable
// ═══════════════════════════════════════════════════
// ═══ PRINTER SETTINGS COMPONENT ═══
function PrinterSettingsTab({ showToast }) {
  const [printerType, setPrinterType] = useState(() => localStorage.getItem("minipos_printer_type") || (isSunmi && !_isGeneralPrinter ? "sunmi" : "system"));
  const [printerAddr, setPrinterAddr] = useState(() => localStorage.getItem("minipos_printer_addr") || "");
  const [paperWidth, setPaperWidth] = useState(() => localStorage.getItem("minipos_paper_width") || "58");
  const [serverStatus, setServerStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [sunmiAvail, setSunmiAvail] = useState(false);
  const [usbDevices, setUsbDevices] = useState([]);
  const [btDevices, setBtDevices] = useState([]);
  const [btAddress, setBtAddress] = useState(() => localStorage.getItem("minipos_bt_address") || "");
  const [tcpHost, setTcpHost] = useState(() => localStorage.getItem("minipos_tcp_host") || "");
  const [tcpPort, setTcpPort] = useState(() => localStorage.getItem("minipos_tcp_port") || "9100");

  useEffect(() => {
    if (isSunmi) {
      (async () => {
        try {
          const sp = window.Capacitor?.Plugins?.SunmiPrinter;
          if (sp) {
            const a = await sp.isAvailable();
            setSunmiAvail(a.available);
            if (a.isGeneral) {
              // General plugin — load config and device lists
              try { const cfg = await sp.getPrinterConfig(); setBtAddress(cfg.btAddress || ""); setTcpHost(cfg.tcpHost || ""); setTcpPort(String(cfg.tcpPort || 9100)); } catch(e) {}
              try { const r = await sp.listUsbDevices(); setUsbDevices(r.devices || []); } catch(e) {}
              try { const r = await sp.listBluetoothDevices(); setBtDevices(r.devices || []); } catch(e) {}
            }
          }
        } catch(e) {}
      })();
    }
  }, []);

  const refreshDevices = async () => {
    const sp = window.Capacitor?.Plugins?.SunmiPrinter;
    if (!sp) return;
    try { const r = await sp.listUsbDevices(); setUsbDevices(r.devices || []); } catch(e) {}
    try { const r = await sp.listBluetoothDevices(); setBtDevices(r.devices || []); } catch(e) {}
    showToast("Osvježeno!");
  };

  const savePrinter = async () => {
    localStorage.setItem("minipos_printer_type", printerType);
    localStorage.setItem("minipos_printer_addr", printerAddr);
    localStorage.setItem("minipos_paper_width", paperWidth);
    localStorage.setItem("minipos_bt_address", btAddress);
    localStorage.setItem("minipos_tcp_host", tcpHost);
    localStorage.setItem("minipos_tcp_port", tcpPort);
    // Save to native plugin if general
    if (_isGeneralPrinter) {
      try {
        const sp = window.Capacitor?.Plugins?.SunmiPrinter;
        if (sp) await sp.savePrinterConfig({ type: printerType, btAddress, tcpHost, tcpPort: parseInt(tcpPort) || 9100 });
      } catch(e) {}
    }
    showToast("Sačuvano!");
  };

  const testPrint = async () => {
    setTesting(true);
    const w = parseInt(paperWidth) <= 58 ? 32 : 48;
    const sep = "=".repeat(w);
    const text = "\n" + sep + "\n  EFIS.PRO - Test Print\n" + sep + "\nTip: " + printerType + "\nSirina: " + paperWidth + "mm (" + w + " char)\n" + new Date().toLocaleString("sr-Latn-ME") + "\n" + sep + "\n\n\n";

    if (printerType === "sunmi") {
      try { await sunmiPrintText(text); showToast("Štampano!"); } catch (e) { showToast("Greška: " + e.message, "error"); }
    } else if (printerType === "system") {
      systemPrintText(text);
    } else if (printerType === "network") {
      if (!printerAddr) { showToast("Unesite adresu", "error"); setTesting(false); return; }
      try {
        const r = await fetch(`${printerAddr}/print/text`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
        if (r.ok) showToast("Poslano!"); else showToast("Greška", "error");
      } catch(e) { showToast("Server nedostupan", "error"); }
    } else if (_isGeneralPrinter && (printerType === "usb" || printerType === "bluetooth" || printerType === "tcp")) {
      try {
        const sp = window.Capacitor?.Plugins?.SunmiPrinter;
        await sp.printText({ text });
        showToast("Štampano!");
      } catch (e) { showToast(e.message || "Greška", "error"); }
    }
    setTesting(false);
  };

  const isGeneral = _isGeneralPrinter;
  const printerTypes = [];
  if (isSunmi && !isGeneral) printerTypes.push({ id: "sunmi", label: "Sunmi", color: M.green });
  if (isGeneral) printerTypes.push({ id: "usb", label: "USB", color: M.blue });
  if (isGeneral) printerTypes.push({ id: "bluetooth", label: "Bluetooth", color: M.blue });
  if (isGeneral) printerTypes.push({ id: "tcp", label: "TCP/IP", color: M.blue });
  printerTypes.push({ id: "network", label: "Mrežni", color: M.blue });
  printerTypes.push({ id: "system", label: "Sistemski", color: M.blue });

  return (
    <div>
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${M.border}`, padding: 18, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Način štampanja</div>
        {isSunmi && !isGeneral && <div style={{ fontSize: 12, color: sunmiAvail ? M.green : M.red, marginBottom: 8, fontWeight: 600 }}>
          {sunmiAvail ? "✓ Sunmi ugrađeni printer detektovan" : "✗ Sunmi printer nije dostupan"}
        </div>}
        {isGeneral && <div style={{ fontSize: 12, color: M.blue, marginBottom: 8, fontWeight: 600 }}>Android generalni printer — izaberite tip konekcije</div>}
        <div style={{ display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap" }}>
          {printerTypes.map(t => (
            <button key={t.id} onClick={() => setPrinterType(t.id)} style={{ flex: 1, minWidth: 70, padding: "10px", borderRadius: 8, border: `2px solid ${printerType === t.id ? t.color : M.border}`, background: printerType === t.id ? (t.color === M.green ? "#f0fdf4" : "#eff6ff") : "white", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: M.font, color: printerType === t.id ? t.color : M.muted }}>{t.label}</button>
          ))}
        </div>

        {printerType === "usb" && isGeneral && <>
          <div style={{ padding: 10, background: "#f0f9ff", borderRadius: 8, marginBottom: 10, fontSize: 11, color: "#1d4ed8" }}>Povežite USB printer putem OTG kabla.</div>
          <button onClick={refreshDevices} style={{ ...M.btnSm("#f3f4f6", M.muted), marginBottom: 8, fontSize: 11 }}>Osvježi uredjaje</button>
          {usbDevices.length > 0 ? usbDevices.map((d, i) => (
            <div key={i} style={{ padding: 8, border: `1px solid ${M.border}`, borderRadius: 6, marginBottom: 3, fontSize: 11 }}>
              <div style={{ fontWeight: 600 }}>{d.productName || d.name}</div>
              <div style={{ color: M.muted, fontSize: 10 }}>VID:{d.vendorId} PID:{d.productId} {d.isPrinter ? "· Printer ✓" : ""}</div>
            </div>
          )) : <div style={{ color: M.muted, fontSize: 11, padding: 8 }}>Nema USB uredjaja</div>}
        </>}

        {printerType === "bluetooth" && isGeneral && <>
          <div style={{ padding: 10, background: "#fef3c7", borderRadius: 8, marginBottom: 10, fontSize: 11, color: "#92400e" }}>Uparite printer u Android Bluetooth postavkama.</div>
          <button onClick={refreshDevices} style={{ ...M.btnSm("#f3f4f6", M.muted), marginBottom: 8, fontSize: 11 }}>Osvježi</button>
          {btDevices.length > 0 ? btDevices.map((d, i) => (
            <button key={i} onClick={() => setBtAddress(d.address)} style={{ display: "block", width: "100%", padding: 8, border: `2px solid ${btAddress === d.address ? M.green : M.border}`, borderRadius: 6, marginBottom: 3, fontSize: 11, textAlign: "left", cursor: "pointer", background: btAddress === d.address ? "#f0fdf4" : "white", fontFamily: M.font }}>
              <span style={{ fontWeight: 600 }}>{d.name}</span>
              <span style={{ color: M.muted, marginLeft: 6, fontSize: 10 }}>{d.address}</span>
            </button>
          )) : <div style={{ color: M.muted, fontSize: 11, padding: 8 }}>Nema uparenih uredjaja</div>}
        </>}

        {printerType === "tcp" && isGeneral && <>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>IP adresa</div>
          <input value={tcpHost} onChange={e => setTcpHost(e.target.value)} placeholder="192.168.1.100" style={{ ...M.input, fontFamily: M.mono, marginBottom: 6, fontSize: 12 }} />
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>Port</div>
          <input value={tcpPort} onChange={e => setTcpPort(e.target.value)} placeholder="9100" style={{ ...M.input, fontFamily: M.mono, marginBottom: 10, fontSize: 12, width: 100 }} />
        </>}

        {printerType === "network" && <>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Server adresa</div>
          <input value={printerAddr} onChange={e => setPrinterAddr(e.target.value)} placeholder="http://192.168.1.100:9100" style={{ ...M.input, fontFamily: M.mono, marginBottom: 14 }} />
        </>}

        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Širina papira</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          {["57", "58", "76", "80"].map(w => (
            <button key={w} onClick={() => setPaperWidth(w)} style={{ padding: "8px 16px", borderRadius: 7, border: `2px solid ${paperWidth === w ? M.green : M.border}`, background: paperWidth === w ? M.green + "15" : "white", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: M.mono, color: paperWidth === w ? M.green : M.muted }}>{w}mm</button>
          ))}
        </div>
        <button onClick={savePrinter} style={{ ...M.btn(M.blue), width: "100%", justifyContent: "center", padding: 11 }}>Sačuvaj</button>
      </div>
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${M.border}`, padding: 18 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Testiranje</div>
        <button onClick={testPrint} disabled={testing} style={{ ...M.btn(M.green), width: "100%", justifyContent: "center", padding: 10, fontSize: 13 }}>{testing ? "..." : "Test štampa"}</button>
      </div>
    </div>
  );
}

// ═══ KASA SETTINGS COMPONENT ═══
function KasaSettingsTab({ showToast }) {
  const [idEnabled, setIdEnabled] = useState(false);
  const [liveReport, setLiveReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const tcr = getTcr();
  useEffect(() => { api("/internalday/settings").then(r => { if (r.success) setIdEnabled(!!r.data?.internal_day_enabled); }); }, []);
  const fetchLive = async () => {
    if (!tcr) return showToast("Izaberite TCR", "error");
    setLoadingReport(true);
    const d = await api(`/internalday/liveReport/${tcr}`);
    setLoadingReport(false);
    if (d.success) setLiveReport(d.data); else showToast(d.message, "error");
  };
  return (
    <div>
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${M.border}`, padding: 20, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div><div style={{ fontWeight: 700, fontSize: 15 }}>Interni pocetak/kraj dana</div><div style={{ fontSize: 12, color: M.muted }}>Za objekte koji rade preko ponoci</div></div>
          <button onClick={async () => { const nv = !idEnabled; setIdEnabled(nv); await api("/internalday/settings", { method: "POST", body: JSON.stringify({ internal_day_enabled: nv ? 1 : 0 }) }); showToast(nv ? "Ukljuceno" : "Iskljuceno"); }} style={{ width: 48, height: 26, borderRadius: 13, border: "none", cursor: "pointer", background: idEnabled ? M.green : "#d1d5db", position: "relative" }}>
            <div style={{ width: 20, height: 20, borderRadius: 10, background: "white", position: "absolute", top: 3, left: idEnabled ? 25 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
          </button>
        </div>
      </div>
      <div style={{ background: "white", borderRadius: 12, border: `1px solid ${M.border}`, padding: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10 }}>Trenutno stanje kase</div>
        <button onClick={fetchLive} disabled={loadingReport} style={{ ...M.btn(M.blue), width: "100%", justifyContent: "center", padding: 12, fontSize: 14 }}>{loadingReport ? "..." : "Prikaži stanje"}</button>
        {liveReport && <div style={{ marginTop: 14, background: "#f9fafb", borderRadius: 8, padding: 14, fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>TCR: {tcr} · {liveReport.date}</div>
          <Row label="Depozit" value={`${fmt(liveReport.deposit)} €`} />
          {liveReport.total_withdrawn > 0 && <Row label="Podignuto" value={`-${fmt(liveReport.total_withdrawn)} €`} />}
          <Row label="Gotovina" value={`${fmt(liveReport.today_sales?.cash_sales || 0)} €`} />
          <Row label="Kartica" value={`${fmt(liveReport.today_sales?.card_sales || 0)} €`} />
          <Row label="Ukupan promet" value={`${fmt(liveReport.today_sales?.total_sales || 0)} €`} />
          <Row label="Računi" value={liveReport.today_sales?.transaction_count || 0} />
          <div style={{ borderTop: "2px solid #111", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 800, fontSize: 14 }}>Očekivana gotovina</span>
            <span style={{ fontWeight: 800, fontSize: 14, color: M.green }}>{fmt(liveReport.expected_cash)} €</span>
          </div>
          {liveReport.internal_day && (
            <div style={{ marginTop: 8, padding: 8, background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#15803d" }}>Interni dan od {liveReport.internal_day.opened_at?.slice(11, 16)}</div>
              <div style={{ fontSize: 12 }}>Promet: {fmt(liveReport.internal_day.sales?.total_sales || 0)} €</div>
            </div>
          )}
        </div>}
      </div>
    </div>
  );
}

function SettingsView({ user, showToast, onTablesToggle }) {
  const [tcrs, setTcrs] = useState([]);
  const [selectedTcr, setSelectedTcr] = useState(getTcr());
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("general");
  const [fastMenu, setFastMenu] = useState(() => { try { return JSON.parse(localStorage.getItem("minipos_fast_menu") || "[]").map(Number); } catch(e) { return []; } });
  const [editProd, setEditProd] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [editPartner, setEditPartner] = useState(null);
  const [partners, setPartners] = useState([]);
  const [partnerDetail, setPartnerDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  // Tables settings
  const [tablesEnabled, setTablesEnabled] = useState(false);
  const [rTables, setRTables] = useState([]);
  const [editTable, setEditTable] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    const [cfgData, prodData, catData, partData, tablesSettings, tablesData] = await Promise.all([
      api("/auth/company-config"),
      api("/products"),
      api("/products/categories"),
      api("/partners"),
      api("/tables/settings"),
      api("/tables"),
    ]);
    if (cfgData.success) {
      const allTcrs = cfgData.data.all_tcrs || [];
      setTcrs(allTcrs);
      const storedTcr = localStorage.getItem("minipos_tcr");
      const validTcr = allTcrs.find(t => t.tcr_code === storedTcr);
      if (validTcr) {
        setSelectedTcr(validTcr.tcr_code);
      } else if (allTcrs.length > 0) {
        setSelectedTcr(allTcrs[0].tcr_code);
        localStorage.setItem("minipos_tcr", allTcrs[0].tcr_code);
      } else {
        setSelectedTcr("");
        localStorage.removeItem("minipos_tcr");
      }
    }
    if (prodData.success) setProducts(prodData.data?.products || []);
    if (catData.success) setCategories(catData.data?.categories || []);
    if (partData.success) setPartners(partData.data?.partners || []);
    if (tablesSettings.success) setTablesEnabled(!!tablesSettings.data?.tables_enabled);
    if (tablesData.success) setRTables(tablesData.data?.tables || []);
    setLoading(false);
  };

  const handleTcrChange = (tcr) => { setSelectedTcr(tcr); localStorage.setItem("minipos_tcr", tcr); showToast("TCR: " + tcr); };
  const toggleFastMenu = (pid) => { const numPid = Number(pid); setFastMenu(prev => { const n = prev.includes(numPid) ? prev.filter(id => id !== numPid) : [...prev, numPid]; localStorage.setItem("minipos_fast_menu", JSON.stringify(n)); return n; }); };
  const moveFastItem = (pid, dir) => { setFastMenu(prev => { const idx = prev.indexOf(pid); if (idx === -1) return prev; const n = [...prev]; const si = dir === "up" ? idx - 1 : idx + 1; if (si < 0 || si >= n.length) return prev; [n[idx], n[si]] = [n[si], n[idx]]; localStorage.setItem("minipos_fast_menu", JSON.stringify(n)); return n; }); };

  // Product CRUD
  const saveProduct = async () => {
    if (!editProd.name || !editProd.price) return showToast("Naziv i cijena su obavezni", "error");
    setSaving(true);
    const isNew = !editProd.id;
    const url = isNew ? "/products" : `/products/update/${editProd.id}`;
    const payload = { ...editProd, track_stock: editProd.track_stock ? 1 : 0 };
    const d = await api(url, { method: "POST", body: JSON.stringify(payload) });
    setSaving(false);
    if (d.success) { showToast(isNew ? "Proizvod kreiran!" : "Proizvod ažuriran!"); setEditProd(null); loadAll(); }
    else showToast(d.message || "Greška", "error");
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Deaktivirati ovaj proizvod?")) return;
    const d = await api(`/products/delete/${id}`, { method: "POST" });
    if (d.success) { showToast("Proizvod deaktiviran"); loadAll(); } else showToast(d.message, "error");
  };

  // Category CRUD
  const saveCategory = async () => {
    if (!editCat.name) return showToast("Naziv kategorije je obavezan", "error");
    setSaving(true);
    const isNew = !editCat.id;
    const url = isNew ? "/products/categories" : `/products/updateCategory/${editCat.id}`;
    const d = await api(url, { method: "POST", body: JSON.stringify(editCat) });
    setSaving(false);
    if (d.success) { showToast(isNew ? "Kategorija kreirana!" : "Kategorija ažurirana!"); setEditCat(null); loadAll(); }
    else showToast(d.message, "error");
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Obrisati kategoriju?")) return;
    const d = await api(`/products/deleteCategory/${id}`, { method: "POST" });
    if (d.success) { showToast("Kategorija obrisana"); loadAll(); } else showToast(d.message, "error");
  };

  // Partner CRUD
  const savePartner = async () => {
    if (!editPartner.name) return showToast("Naziv partnera je obavezan", "error");
    setSaving(true);
    const isNew = !editPartner.id;
    const url = isNew ? "/partners" : `/partners/update/${editPartner.id}`;
    const d = await api(url, { method: "POST", body: JSON.stringify(editPartner) });
    setSaving(false);
    if (d.success) { showToast(isNew ? "Partner kreiran!" : "Partner ažuriran!"); setEditPartner(null); loadAll(); }
    else showToast(d.message || "Greška", "error");
  };
  const deletePartner = async (id) => {
    if (!window.confirm("Obrisati partnera?")) return;
    const d = await api(`/partners/delete/${id}`, { method: "POST" });
    if (d.success) { showToast("Partner obrisan"); loadAll(); } else showToast(d.message, "error");
  };
  const loadPartnerDetail = async (partner) => {
    const d = await api(`/partners/stats/${partner.id}`);
    if (d.success) setPartnerDetail({ ...partner, stats: d.data });
    else setPartnerDetail({ ...partner, stats: null });
  };

  if (loading) return <div style={{ padding: 50, textAlign: "center" }}><Loader2 size={28} className="spin" /></div>;

  const inputRow = (label, field, type = "text", placeholder = "") => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: M.dark, display: "block", marginBottom: 4 }}>{label}</label>
      <input type={type} step={type === "number" ? "0.01" : undefined} value={editProd?.[field] ?? ""} onChange={e => setEditProd(prev => ({ ...prev, [field]: type === "number" ? e.target.value : e.target.value }))} placeholder={placeholder} style={M.input} />
    </div>
  );

  return (
    <div style={{ height: "100%", overflow: "auto", padding: "16px 16px", paddingLeft: "max(16px, env(safe-area-inset-left, 16px))", paddingRight: "max(16px, env(safe-area-inset-right, 16px))", WebkitOverflowScrolling: "touch" }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 14px" }}>Podešavanja</h2>
      <div style={{ display: "flex", gap: 5, marginBottom: 16, flexWrap: "wrap" }}>
        {[{ id: "general", l: "TCR Kasa" }, { id: "kasa", l: "💰 Kasa" }, { id: "products", l: "Proizvodi" }, { id: "categories", l: "Kategorije" }, { id: "partners", l: "Partneri" }, { id: "fastmenu", l: "⚡ Brzi Meni" }, { id: "printer", l: "🖨 Printer" }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "8px 16px", border: "1px solid", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: M.font, borderColor: tab === t.id ? M.dark : M.border, background: tab === t.id ? M.dark : "white", color: tab === t.id ? "white" : M.muted }}>{t.l}</button>
        ))}
      </div>

      {/* ═══ TCR TAB ═══ */}
      {tab === "general" && (
        <div style={{ maxWidth: 560 }}>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${M.border}`, padding: 22 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 14px" }}>TCR Kod (Elektronska Kasa)</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {tcrs.map(t => (
                <label key={t.tcr_code} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 8, cursor: "pointer", background: selectedTcr === t.tcr_code ? "#f0fdf4" : "#f9fafb", border: `2px solid ${selectedTcr === t.tcr_code ? M.green : M.border}` }}>
                  <input type="radio" name="tcr" checked={selectedTcr === t.tcr_code} onChange={() => handleTcrChange(t.tcr_code)} style={{ accentColor: M.green, width: 18, height: 18 }} />
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontFamily: M.mono, fontSize: 15 }}>{t.tcr_code}</div>{t.location_name && <div style={{ fontSize: 13, color: M.muted }}>{t.location_name}</div>}</div>
                  {selectedTcr === t.tcr_code && <CheckCircle size={20} color={M.green} />}
                </label>
              ))}
              {tcrs.length === 0 && <div style={{ padding: 20, textAlign: "center", color: M.muted }}>Nema TCR kodova. Dodajte ih u web aplikaciji.</div>}
            </div>
          </div>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${M.border}`, padding: 22, marginTop: 16 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 14px" }}>Info</h3>
            <Row label="Operater" value={user?.name || ""} /><Row label="Kompanija" value={user?.company_name || ""} /><Row label="Email" value={user?.email || ""} last />
          </div>
        </div>
      )}

      {/* ═══ PRODUCTS TAB ═══ */}
      {/* ═══ KASA SETTINGS TAB ═══ */}
      {tab === "kasa" && <KasaSettingsTab showToast={showToast} />}

      {tab === "printer" && <PrinterSettingsTab showToast={showToast} />}

      {tab === "products" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{products.length} proizvoda</span>
            {/* New-product defaults: is_decimal_unit=0 (integer-only). Cashier can toggle in modal. */}
            <button onClick={() => setEditProd({ name: "", price: "", vat_rate: 21, unit: "kom", is_decimal_unit: 0, category_id: "", sku: "", barcode: "", description: "", track_stock: true, stock_quantity: 0 })} style={{ ...M.btnSm(M.green, "white"), padding: "6px 12px" }}><PlusCircle size={13} />Novi</button>
          </div>
          {products.map(p => {
            const cat = categories.find(c => String(c.id) === String(p.category_id));
            const gross = parseFloat(p.price) * (1 + parseFloat(p.vat_rate ?? 21) / 100);
            const isDec = parseInt(p.is_decimal_unit) === 1;
            return (
              <div key={p.id} style={{ ...M.card, padding: "10px 12px", marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}{isDec && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: M.blue, background: "#eff6ff", padding: "1px 5px", borderRadius: 3, border: `1px solid ${M.blue}` }}>DEC</span>}</div>
                  <div style={{ fontSize: 11, color: M.muted }}>{fmt(p.price)}€ + {p.vat_rate}% = <strong>{fmt(gross)}€</strong> {cat ? `• ${cat.name}` : ""}{p.track_stock == 1 ? ` • Stanje: ${p.stock_quantity ?? 0}` : ""}</div>
                </div>
                <button onClick={() => toggleFastMenu(p.id)} style={{ width: 28, height: 28, border: `1px solid ${fastMenu.includes(p.id) ? M.green : M.border}`, borderRadius: 6, background: fastMenu.includes(p.id) ? "#f0fdf4" : "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{fastMenu.includes(p.id) ? <Check size={12} color="#15803d" /> : <Plus size={12} color={M.muted} />}</button>
                {/* Edit-populate: read is_decimal_unit from API response (0/1 int). */}
                <button onClick={() => setEditProd({ id: p.id, name: p.name, price: p.price, vat_rate: p.vat_rate, unit: p.unit, is_decimal_unit: parseInt(p.is_decimal_unit) === 1 ? 1 : 0, category_id: p.category_id || "", sku: p.sku || "", barcode: p.barcode || "", description: p.description || "", stock_quantity: p.stock_quantity || 0, track_stock: p.track_stock == 1 })} style={{ width: 28, height: 28, border: `1px solid ${M.border}`, borderRadius: 6, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Edit3 size={12} color={M.muted} /></button>
                <button onClick={() => deleteProduct(p.id)} style={{ width: 28, height: 28, border: "1px solid #fecaca", borderRadius: 6, background: "#fef2f2", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Trash2 size={12} color={M.red} /></button>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ CATEGORIES TAB ═══ */}
      {tab === "categories" && (
        <div style={{ maxWidth: 560 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{categories.length} kategorija</span>
            <button onClick={() => setEditCat({ name: "", description: "", sort_order: 0 })} style={M.btn(M.green)}><PlusCircle size={15} />Nova kategorija</button>
          </div>
          {categories.map(c => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "white", borderRadius: 8, border: `1px solid ${M.border}`, marginBottom: 6 }}>
              <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</div>{c.description && <div style={{ fontSize: 13, color: M.muted }}>{c.description}</div>}</div>
              <button onClick={() => setEditCat({ id: c.id, name: c.name, description: c.description || "", sort_order: c.sort_order || 0, is_active: c.is_active })} style={M.btnSm("#f3f4f6", M.muted)}><Edit3 size={12} />Uredi</button>
              <button onClick={() => deleteCategory(c.id)} style={M.btnSm("#fef2f2", M.red)}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ═══ FAST MENU TAB ═══ */}
      {tab === "fastmenu" && (
        <div style={{ maxWidth: 560 }}>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${M.border}`, padding: 22 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 4px" }}>⚡ Brzi Meni</h3>
            <p style={{ fontSize: 14, color: M.muted, margin: "0 0 16px" }}>Proizvodi koji se pojavljuju na prvom tabu POS ekrana</p>
            {fastMenu.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: M.muted, background: "#f9fafb", borderRadius: 8 }}><Package size={30} /><p style={{ marginTop: 8 }}>Prazan. Dodajte iz tab-a Proizvodi.</p></div>
            : <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {fastMenu.map((id, idx) => { const p = products.find(pr => pr.id === id); if (!p) return null; const gross = parseFloat(p.price) * 1.21; return (
                <div key={id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#f9fafb", borderRadius: 8, border: `1px solid ${M.border}` }}>
                  <span style={{ fontSize: 14, color: M.muted, fontWeight: 600, minWidth: 24 }}>#{idx + 1}</span>
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div><div style={{ fontSize: 13, color: M.muted }}>{fmt(gross)} €</div></div>
                  <button onClick={() => moveFastItem(id, "up")} disabled={idx === 0} style={{ ...M.btnSm("#f3f4f6", M.muted), padding: "4px 8px", opacity: idx === 0 ? 0.3 : 1 }}>↑</button>
                  <button onClick={() => moveFastItem(id, "down")} disabled={idx === fastMenu.length - 1} style={{ ...M.btnSm("#f3f4f6", M.muted), padding: "4px 8px", opacity: idx === fastMenu.length - 1 ? 0.3 : 1 }}>↓</button>
                  <button onClick={() => toggleFastMenu(id)} style={M.btnSm("#fef2f2", M.red)}><X size={13} /></button>
                </div>
              ); })}
            </div>}
          </div>
          <div style={{ background: "white", borderRadius: 12, border: `1px solid ${M.border}`, padding: 22, marginTop: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px" }}>Dodaj u brzi meni</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {products.filter(p => !fastMenu.includes(p.id)).map(p => <button key={p.id} onClick={() => toggleFastMenu(p.id)} style={{ ...M.btnSm("#f3f4f6", M.dark), borderRadius: 20 }}><PlusCircle size={13} />{p.name}</button>)}
              {products.filter(p => !fastMenu.includes(p.id)).length === 0 && <span style={{ fontSize: 14, color: M.muted }}>Svi su dodani</span>}
            </div>
          </div>
        </div>
      )}

      {/* ═══ PARTNERS TAB ═══ */}
      {tab === "partners" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{partners.length} partnera</span>
            <button onClick={() => setEditPartner({ name: "", tax_id: "", partner_type: "customer", email: "", phone: "", address: "", city: "", postal_code: "", country: "Montenegro", bank_name: "", bank_account: "", notes: "" })} style={{ ...M.btnSm(M.green, "white"), padding: "6px 12px" }}><PlusCircle size={13} />Novi</button>
          </div>
          {partnerDetail && (
            <div style={{ ...M.card, padding: 12, marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{partnerDetail.name}</span>
                <button onClick={() => setPartnerDetail(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={14} color={M.muted} /></button>
              </div>
              {partnerDetail.stats ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                  {[{ l: "Mjesec", c: partnerDetail.stats.month_count, v: partnerDetail.stats.month_total },
                    { l: "Godina", c: partnerDetail.stats.year_count, v: partnerDetail.stats.year_total },
                    { l: "Ukupno", c: partnerDetail.stats.all_count, v: partnerDetail.stats.all_total }
                  ].map((s, i) => (
                    <div key={i} style={{ background: "#f9fafb", borderRadius: 8, padding: 8, textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: M.muted, fontWeight: 600 }}>{s.l}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: M.green }}>{fmt(s.v || 0)}€</div>
                      <div style={{ fontSize: 10, color: M.muted }}>{s.c || 0} rač.</div>
                    </div>
                  ))}
                </div>
              ) : <div style={{ textAlign: "center", color: M.muted, fontSize: 12 }}>Nema podataka</div>}
            </div>
          )}
          {partners.map(p => (
            <div key={p.id} onClick={() => loadPartnerDetail(p)} style={{ ...M.card, padding: "10px 12px", marginBottom: 6, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", background: partnerDetail?.id === p.id ? "#eff6ff" : "white" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                <div style={{ fontSize: 11, color: M.muted }}>{p.tax_id ? `PIB: ${p.tax_id}` : ""}{p.city ? ` • ${p.city}` : ""}{p.email ? ` • ${p.email}` : ""}</div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); setEditPartner({ id: p.id, name: p.name, tax_id: p.tax_id || "", partner_type: p.partner_type || "customer", email: p.email || "", phone: p.phone || "", address: p.address || "", city: p.city || "", postal_code: p.postal_code || "", country: p.country || "Montenegro", bank_name: p.bank_name || "", bank_account: p.bank_account || "", notes: p.notes || "" }); }} style={{ width: 28, height: 28, border: `1px solid ${M.border}`, borderRadius: 6, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Edit3 size={12} color={M.muted} /></button>
              <button onClick={(e) => { e.stopPropagation(); deletePartner(p.id); }} style={{ width: 28, height: 28, border: "1px solid #fecaca", borderRadius: 6, background: "#fef2f2", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Trash2 size={12} color={M.red} /></button>
            </div>
          ))}
        </div>
      )}

      {/* ═══ PRODUCT EDIT MODAL ═══ */}
      {editProd && <Modal onClose={() => setEditProd(null)} title={editProd.id ? "Uredi proizvod" : "Novi proizvod"}>
        {inputRow("Naziv *", "name", "text", "npr. Coca Cola 0.33L")}
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 2 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Neto cijena (€) *</label><input type="number" step="0.01" value={editProd.price} onChange={e => setEditProd(p => ({ ...p, price: e.target.value }))} style={M.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>PDV %</label><input type="number" value={editProd.vat_rate} onChange={e => setEditProd(p => ({ ...p, vat_rate: e.target.value }))} style={M.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Jedinica</label><select value={editProd.unit} onChange={e => setEditProd(p => ({ ...p, unit: e.target.value }))} style={M.input}><option value="kom">kom</option><option value="kg">kg</option><option value="l">l</option><option value="m">m</option><option value="sub">sub</option></select></div>
        </div>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Kategorija</label><select value={editProd.category_id} onChange={e => setEditProd(p => ({ ...p, category_id: e.target.value }))} style={M.input}><option value="">-- Bez kategorije --</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        {/* is_decimal_unit: when checked, this product's quantity in the cart can be entered
            as a decimal (e.g. 1.75 kg) via a tappable numpad button. When unchecked, only
            integer +/- stepper. Per-product so a Coca-Cola can't accidentally be sold as 0.5. */}
        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }}>
            <input type="checkbox" checked={!!editProd.is_decimal_unit} onChange={e => setEditProd(p => ({ ...p, is_decimal_unit: e.target.checked ? 1 : 0 }))} style={{ width: 18, height: 18, accentColor: M.blue }} />
            <span style={{ fontWeight: 600 }}>Decimalna količina</span>
          </label>
          <span style={{ fontSize: 12, color: M.muted }}>Dozvoli npr. 1,75 (kg, l, m)</span>
        </div>
        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }}>
            <input type="checkbox" checked={!!editProd.track_stock} onChange={e => setEditProd(p => ({ ...p, track_stock: e.target.checked }))} style={{ width: 18, height: 18, accentColor: M.blue }} />
            <span style={{ fontWeight: 600 }}>Praćenje zaliha</span>
          </label>
          <span style={{ fontSize: 12, color: M.muted }}>Automatski oduzima sa lagera</span>
        </div>
        {editProd.track_stock && <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Količina na stanju</label><input type="number" step="1" value={editProd.stock_quantity ?? 0} onChange={e => setEditProd(p => ({ ...p, stock_quantity: e.target.value }))} style={M.input} /></div>}
        {inputRow("SKU", "sku", "text")}
        {inputRow("Barkod", "barcode", "text")}
        {inputRow("Opis", "description", "text")}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => setEditProd(null)} style={{ ...M.btn("#f3f4f6", M.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={saveProduct} disabled={saving} style={{ ...M.btn(M.green), flex: 1, justifyContent: "center" }}>{saving ? "..." : <><Save size={15} />{editProd.id ? "Sačuvaj" : "Kreiraj"}</>}</button>
        </div>
      </Modal>}

      {/* ═══ CATEGORY EDIT MODAL ═══ */}
      {editCat && <Modal onClose={() => setEditCat(null)} title={editCat.id ? "Uredi kategoriju" : "Nova kategorija"}>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Naziv *</label><input value={editCat.name} onChange={e => setEditCat(p => ({ ...p, name: e.target.value }))} style={M.input} /></div>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Opis</label><input value={editCat.description} onChange={e => setEditCat(p => ({ ...p, description: e.target.value }))} style={M.input} /></div>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Redoslijed</label><input type="number" value={editCat.sort_order} onChange={e => setEditCat(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} style={M.input} /></div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => setEditCat(null)} style={{ ...M.btn("#f3f4f6", M.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={saveCategory} disabled={saving} style={{ ...M.btn(M.green), flex: 1, justifyContent: "center" }}>{saving ? "..." : <><Save size={15} />{editCat.id ? "Sačuvaj" : "Kreiraj"}</>}</button>
        </div>
      </Modal>}

      {/* ═══ PARTNER EDIT MODAL ═══ */}
      {editPartner && <Modal onClose={() => setEditPartner(null)} title={editPartner.id ? "Uredi partnera" : "Novi partner"}>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Naziv *</label><input value={editPartner.name} onChange={e => setEditPartner(p => ({ ...p, name: e.target.value }))} style={M.input} /></div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>PIB</label><input value={editPartner.tax_id} onChange={e => setEditPartner(p => ({ ...p, tax_id: e.target.value }))} style={M.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Tip</label><select value={editPartner.partner_type} onChange={e => setEditPartner(p => ({ ...p, partner_type: e.target.value }))} style={M.input}><option value="customer">Kupac</option><option value="supplier">Dobavljač</option><option value="both">Oba</option></select></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Email</label><input value={editPartner.email} onChange={e => setEditPartner(p => ({ ...p, email: e.target.value }))} style={M.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Telefon</label><input value={editPartner.phone} onChange={e => setEditPartner(p => ({ ...p, phone: e.target.value }))} style={M.input} /></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 2 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Adresa</label><input value={editPartner.address} onChange={e => setEditPartner(p => ({ ...p, address: e.target.value }))} style={M.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Grad</label><input value={editPartner.city} onChange={e => setEditPartner(p => ({ ...p, city: e.target.value }))} style={M.input} /></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Banka</label><input value={editPartner.bank_name} onChange={e => setEditPartner(p => ({ ...p, bank_name: e.target.value }))} style={M.input} /></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Žiro račun</label><input value={editPartner.bank_account} onChange={e => setEditPartner(p => ({ ...p, bank_account: e.target.value }))} style={M.input} /></div>
        </div>
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Napomena</label><input value={editPartner.notes} onChange={e => setEditPartner(p => ({ ...p, notes: e.target.value }))} style={M.input} /></div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => setEditPartner(null)} style={{ ...M.btn("#f3f4f6", M.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={savePartner} disabled={saving} style={{ ...M.btn(M.green), flex: 1, justifyContent: "center" }}>{saving ? "..." : <><Save size={15} />{editPartner.id ? "Sačuvaj" : "Kreiraj"}</>}</button>
        </div>
      </Modal>}

      {/* ═══ STOLOVI (TABLES) TAB ═══ */}
      {tab === "stolovi" && (
        <div>
          {/* Toggle */}
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${M.border}`, padding: 20, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div><div style={{ fontWeight: 700, fontSize: 16 }}>Koristi stolove</div><div style={{ fontSize: 13, color: M.muted }}>Prikaži Stolovi sektor u navigaciji</div></div>
              <button onClick={async () => {
                const nv = !tablesEnabled;
                if (!nv) {
                  if (!window.confirm("Isključivanjem stolova izgubićete sve informacije o stolovima. Da li ste sigurni?")) return;
                }
                setTablesEnabled(nv);
                onTablesToggle && onTablesToggle(nv);
                await api("/tables/settings", { method: "POST", body: JSON.stringify({ tables_enabled: nv ? 1 : 0 }) });
                showToast(nv ? "Stolovi uključeni" : "Stolovi isključeni");
              }} style={{ width: 52, height: 28, borderRadius: 14, border: "none", cursor: "pointer", background: tablesEnabled ? M.green : "#d1d5db", position: "relative", transition: "background 0.2s" }}>
                <div style={{ width: 22, height: 22, borderRadius: 11, background: "white", position: "absolute", top: 3, left: tablesEnabled ? 27 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
              </button>
            </div>
          </div>

          {tablesEnabled && <>
            {/* Quick add */}
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${M.border}`, padding: 20, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Brzo dodaj stolove</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { type: "table", label: "Sto", icon: "🟫", seats: 4 },
                  { type: "bar", label: "Bar", icon: "🍸", seats: 2 },
                  { type: "couch", label: "Kauč", icon: "🛋", seats: 3 },
                  { type: "vip", label: "VIP", icon: "⭐", seats: 6 },
                  { type: "outdoor", label: "Terasa", icon: "☀", seats: 4 },
                  { type: "chair", label: "Stolica", icon: "🪑", seats: 0 },
                  { type: "wall", label: "Zid/Pregrada", icon: "🧱", seats: 0 },
                ].map(t => (
                  <button key={t.type} onClick={async () => {
                    const count = rTables.filter(tb => tb.table_type === t.type).length;
                    const name = t.type === "wall" ? `Zid ${count + 1}` : t.type === "chair" ? `Stolica ${count + 1}` : `${t.label} ${count + 1}`;
                    const row = Math.floor(count / 5);
                    const col = count % 5;
                    const isWall = t.type === "wall";
                    const isChair = t.type === "chair";
                    const sizes = { S: { w: 8, h: 7 }, M: { w: 14, h: 12 }, L: { w: 20, h: 16 } };
                    const sz = sizes.M;
                    const d = await api("/tables", { method: "POST", body: JSON.stringify({ name, table_type: t.type, seats: t.seats, x: 5 + col * 18, y: 5 + row * 16, width: isWall ? 22 : isChair ? 5 : sz.w, height: isWall ? 3 : isChair ? 5 : sz.h, shape: t.type === "outdoor" ? "circle" : "rect", color: t.type === "vip" ? "#eab308" : t.type === "bar" ? "#8b5cf6" : t.type === "outdoor" ? "#22c55e" : t.type === "couch" ? "#f97316" : t.type === "wall" ? "#78716c" : t.type === "chair" ? "#a1a1aa" : "#4f46e5" }) });
                    if (d.success) { showToast(`${name} dodat`); loadAll(); }
                  }} style={{ ...M.btn("#f3f4f6", M.dark), fontSize: 13 }}>{t.icon} + {t.label}</button>
                ))}
              </div>
            </div>

            {/* Tables list */}
            <div style={{ background: "white", borderRadius: 10, border: `1px solid ${M.border}`, overflow: "hidden" }}>
              <div style={{ padding: "14px 16px", borderBottom: `1px solid ${M.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{rTables.length} stolova</span>
              </div>
              {rTables.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: M.muted }}>Nema stolova. Koristite dugmad iznad za brzo dodavanje.</div>
              : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead><tr style={{ background: "#f9fafb" }}><th style={th}>Naziv</th><th style={th}>Tip</th><th style={th}>Mjesta</th><th style={th}>Boja</th><th style={{ ...th, textAlign: "right" }}>Akcije</th></tr></thead>
                <tbody>{rTables.map(t => (
                  <tr key={t.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ ...td, fontWeight: 600 }}>{t.name}</td>
                    <td style={td}>{t.table_type === "table" ? "Sto" : t.table_type === "bar" ? "Bar" : t.table_type === "couch" ? "Kauč" : t.table_type === "vip" ? "VIP" : t.table_type === "wall" ? "Zid" : t.table_type === "chair" ? "Stolica" : "Terasa"}</td>
                    <td style={td}>{t.seats}</td>
                    <td style={td}><div style={{ width: 22, height: 22, borderRadius: 6, background: t.color || "#4f46e5" }} /></td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button onClick={() => setEditTable({ ...t })} style={M.btnSm("#f3f4f6", M.muted)}><Edit3 size={12} /></button>
                        <button onClick={async () => { if (!window.confirm("Obrisati " + t.name + "?")) return; const d = await api(`/tables/delete/${t.id}`, { method: "POST" }); if (d.success) { showToast("Obrisano"); loadAll(); } else showToast(d.message, "error"); }} style={M.btnSm("#fef2f2", M.red)}><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>}
            </div>
          </>}
        </div>
      )}

      {/* ═══ TABLE EDIT MODAL ═══ */}
      {editTable && <Modal onClose={() => setEditTable(null)} title="Uredi sto">
        <div style={{ marginBottom: 12 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Naziv *</label><input value={editTable.name} onChange={e => setEditTable(p => ({ ...p, name: e.target.value }))} style={M.input} /></div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Tip</label><select value={editTable.table_type} onChange={e => setEditTable(p => ({ ...p, table_type: e.target.value }))} style={M.input}><option value="table">Sto</option><option value="bar">Bar</option><option value="couch">Kauč</option><option value="vip">VIP</option><option value="outdoor">Terasa</option><option value="chair">Stolica</option><option value="wall">Zid/Pregrada</option></select></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Mjesta</label><input type="number" value={editTable.seats} onChange={e => setEditTable(p => ({ ...p, seats: parseInt(e.target.value) || 0 }))} style={M.input} /></div>
        </div>
        {/* Size S/M/L */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Veličina</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ id: "S", l: "S — Mali", w: 8, h: 7 }, { id: "M", l: "M — Srednji", w: 14, h: 12 }, { id: "L", l: "L — Veliki", w: 20, h: 16 }].map(sz => {
              const isH = (editTable.width || 14) > (editTable.height || 12);
              const curW = parseFloat(editTable.width) || 14;
              const curH = parseFloat(editTable.height) || 12;
              const isActive = (Math.abs(curW - (isH ? sz.w : sz.h)) < 3 && Math.abs(curH - (isH ? sz.h : sz.w)) < 3) || (Math.abs(curW - sz.w) < 3 && Math.abs(curH - sz.h) < 3);
              return <button key={sz.id} onClick={() => {
                const isHoriz = (editTable.width || 14) >= (editTable.height || 12);
                setEditTable(p => ({ ...p, width: isHoriz ? sz.w : sz.h, height: isHoriz ? sz.h : sz.w }));
              }} style={{ flex: 1, padding: "8px 6px", border: "2px solid", borderColor: isActive ? M.blue : M.border, background: isActive ? "#eef2ff" : "white", color: isActive ? M.blue : M.dark, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", textAlign: "center" }}>{sz.id}<div style={{ fontSize: 10, fontWeight: 400, color: M.muted, marginTop: 2 }}>{sz.l}</div></button>;
            })}
          </div>
        </div>
        {/* Direction H/V */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Smjer</label>
          <div style={{ display: "flex", gap: 6 }}>
            {[{ id: "H", l: "↔ Horizontalno" }, { id: "V", l: "↕ Vertikalno" }].map(dir => {
              const curW = parseFloat(editTable.width) || 14;
              const curH = parseFloat(editTable.height) || 12;
              const isH = curW >= curH;
              const isActive = (dir.id === "H" && isH) || (dir.id === "V" && !isH);
              return <button key={dir.id} onClick={() => {
                const newRot = dir.id === "H" ? 0 : 90;
                setEditTable(p => ({ ...p, width: p.height, height: p.width, rotation: newRot }));
              }} style={{ flex: 1, padding: "9px 8px", border: "2px solid", borderColor: isActive ? M.blue : M.border, background: isActive ? "#eef2ff" : "white", color: isActive ? M.blue : M.dark, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{dir.l}</button>;
            })}
          </div>
        </div>
        {/* Facing direction (for couch, chair, bar) */}
        {(editTable.table_type === "couch" || editTable.table_type === "chair" || editTable.table_type === "bar") && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Okretanje (strana)</label>
            <div style={{ display: "flex", gap: 6 }}>
              {[{ rot: 0, l: "⬆ Gore" }, { rot: 90, l: "➡ Desno" }, { rot: 180, l: "⬇ Dole" }, { rot: 270, l: "⬅ Lijevo" }].map(f => {
                const curRot = parseInt(editTable.rotation) || 0;
                const isActive = curRot === f.rot;
                return <button key={f.rot} onClick={() => {
                  setEditTable(p => ({ ...p, rotation: f.rot }));
                }} style={{ flex: 1, padding: "8px 4px", border: "2px solid", borderColor: isActive ? M.blue : M.border, background: isActive ? "#eef2ff" : "white", color: isActive ? M.blue : M.dark, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{f.l}</button>;
              })}
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Oblik</label><select value={editTable.shape} onChange={e => setEditTable(p => ({ ...p, shape: e.target.value }))} style={M.input}><option value="rect">Pravougaonik</option><option value="circle">Krug</option></select></div>
          <div style={{ flex: 1 }}><label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Boja</label><input type="color" value={editTable.color || "#4f46e5"} onChange={e => setEditTable(p => ({ ...p, color: e.target.value }))} style={{ ...M.input, padding: 4, height: 42 }} /></div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => setEditTable(null)} style={{ ...M.btn("#f3f4f6", M.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={async () => {
            setSaving(true);
            const d = await api(`/tables/update/${editTable.id}`, { method: "POST", body: JSON.stringify(editTable) });
            setSaving(false);
            if (d.success) { showToast("Sto ažuriran"); setEditTable(null); loadAll(); } else showToast(d.message, "error");
          }} disabled={saving} style={{ ...M.btn(M.green), flex: 1, justifyContent: "center" }}>{saving ? "..." : <><Save size={15} />Sačuvaj</>}</button>
        </div>
      </Modal>}
    </div>
  );
}

// ═══ STOLOVI VIEW (Restaurant tables) ═══

// ═══════════════════════════════════════════════════
// VIRMAN — Mobile form + list
// ═══════════════════════════════════════════════════
function VirmanView({ user, showToast, online }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [partners, setPartners] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [fiscalizing, setFiscalizing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [manualPartner, setManualPartner] = useState(null); // { name, tax_id }
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [paymentDue, setPaymentDue] = useState("end_of_month");
  const [invoiceDiscount, setInvoiceDiscount] = useState({ type: "none", value: 0 });
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [lastFiscalized, setLastFiscalized] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [subView, setSubView] = useState("create"); // create | list
  // Non-cash invoices list
  const [ncInvoices, setNcInvoices] = useState([]);
  const [ncLoading, setNcLoading] = useState(false);
  const [ncPg, setNcPg] = useState({ total: 0, page: 1, per_page: 20, total_pages: 0 });
  const [ncDetail, setNcDetail] = useState(null);
  const [stornoModal, setStornoModal] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  // qtyModal — same as Nova Prodaja: opens decimal numpad for is_decimal_unit products.
  const [qtyModal, setQtyModal] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [p, c, pa] = await Promise.all([api("/products"), api("/invoices/categories"), api("/partners")]);
    if (p.success) setProducts(p.data?.products || []);
    if (c.success) setCategories(c.data?.categories || []);
    if (pa.success) setPartners(pa.data?.partners || []);
    setLoading(false);
  };

  const loadNcInvoices = async () => {
    setNcLoading(true);
    const tcr = getTcr();
    const params = new URLSearchParams({ page: ncPg.page, per_page: 20 });
    if (tcr) params.set("tcr_code", tcr);
    const data = await api(`/invoices?${params}`);
    if (data.success) {
      const all = data.data.invoices || [];
      const filtered = all.filter(inv => inv.invoice_type === "non_cash" || inv.type_of_inv === "NONCASH" || inv.payment_method === "bank_transfer" || inv.payment_method === "other");
      setNcInvoices(filtered);
      setNcPg(p => ({ ...p, ...data.data.pagination }));
    }
    setNcLoading(false);
  };

  useEffect(() => { if (subView === "list") loadNcInvoices(); }, [subView, ncPg.page]);

  const addToCart = (product) => {
    setCart(prev => {
      const ex = prev.find(c => c.id === product.id);
      if (ex) return prev.map(c => c.id === product.id ? { ...c, quantity: parseFloat((c.quantity + 1).toFixed(2)) } : c);
      return [...prev, { id: product.id, name: product.name, price: parseFloat(product.price), quantity: 1, vat_rate: parseFloat(product.vat_rate ?? 21), unit: product.unit || "kom", is_decimal_unit: parseInt(product.is_decimal_unit) === 1 ? 1 : 0, discount_type: "none", discount_value: 0, custom_price: null }];
    });
  };
  const updateCartItem = (id, u) => setCart(prev => prev.map(c => c.id === id ? { ...c, ...u } : c));
  const removeFromCart = (id) => setCart(prev => prev.filter(c => c.id !== id));
  const clearAll = () => { setCart([]); setInvoiceDiscount({ type: "none", value: 0 }); setSelectedPartner(null); setManualPartner(null); setPaymentDue("end_of_month"); setPaymentMethod("bank_transfer"); };

  const cartCalc = useMemo(() => {
    let netTotal = 0;
    cart.forEach(item => {
      const up = item.custom_price !== null ? item.custom_price : item.price;
      const lineNet = item.quantity * up;
      let disc = 0;
      if (item.discount_type === "percentage") disc = lineNet * (item.discount_value / 100);
      else if (item.discount_type === "fixed") disc = item.discount_value;
      netTotal += lineNet - disc;
    });
    let invDisc = 0;
    if (invoiceDiscount.type === "percentage") invDisc = netTotal * (invoiceDiscount.value / 100);
    else if (invoiceDiscount.type === "fixed") invDisc = invoiceDiscount.value;
    const subtotal = Math.max(0, netTotal - invDisc);
    let vatAmount = 0; const vatGroups = {};
    cart.forEach(item => { const up = item.custom_price !== null ? item.custom_price : item.price; const ln = item.quantity * up; let d = 0; if (item.discount_type === "percentage") d = ln * (item.discount_value / 100); else if (item.discount_type === "fixed") d = item.discount_value || 0; const af = ln - d; const r = parseFloat(item.vat_rate ?? 21); const v = af * (r / 100); vatAmount += v; if (!vatGroups[r]) vatGroups[r] = { base: 0, vat: 0 }; vatGroups[r].base += af; vatGroups[r].vat += v; });
    if (invDisc > 0 && netTotal > 0) { vatAmount *= (netTotal - invDisc) / netTotal; }
    const total = subtotal + vatAmount;
    return { netTotal, invDisc, subtotal, vatAmount, total, vatGroups };
  }, [cart, invoiceDiscount]);

  const buyerName = selectedPartner ? selectedPartner.name : manualPartner?.name || "";
  const buyerPib = selectedPartner ? selectedPartner.tax_id : manualPartner?.tax_id || "";

  const buildPayload = (fiscNow) => ({
    location_id: user?.location_id || 1,
    tcr_code: getTcr(),
    payment_method: paymentMethod,
    invoice_type: "non_cash",
    type_of_inv: "NONCASH",
    fiscalize_now: fiscNow,
    discount_type: invoiceDiscount.type,
    discount_value: invoiceDiscount.value,
    partner_id: selectedPartner?.id || null,
    buyer_name: buyerName,
    buyer_pib: buyerPib,
    buyer_address: selectedPartner?.address || "",
    buyer_city: selectedPartner?.city || "",
    payment_due: paymentDue,
    items: cart.map(item => ({ product_id: item.id, name: item.name, quantity: item.quantity, unit_price: item.custom_price !== null ? item.custom_price : item.price, vat_rate: item.vat_rate, unit: item.unit, discount_type: item.discount_type, discount_value: item.discount_value }))
  });

  const handleFiscalize = async () => {
    if (!buyerName) { showToast("Izaberite partnera ili unesite naziv", "error"); return; }
    if (!online) { const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Offline! Draft sačuvan lokalno (${cnt}). Fiskalizacija kad se povežete.`, "error"); setShowConfirm(false); return; }
    setShowConfirm(false); setFiscalizing(true);
    const data = await api("/invoices", { method: "POST", body: JSON.stringify(buildPayload(true)) });
    setFiscalizing(false);
    if (data.success) { setLastFiscalized({ fic: data.data.fic, invoice: data.data.invoice, total: cartCalc.total, verification_url: data.data.verification_url }); clearAll(); showToast("Fiskalizacija uspješna!"); if (data.data?.invoice?.id) { printReceipt(data.data.invoice.id); } }
    else { if (data._offline) { const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Nema mreže! Draft sačuvan lokalno (${cnt}).`, "error"); } else showToast(data.message || "Greška", "error"); }
  };
  const handleSaveDraft = async () => {
    if (!cart.length) return;
    if (!buyerName) { showToast("Izaberite partnera ili unesite naziv", "error"); return; }
    if (!online) { const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Offline! Draft sačuvan lokalno (${cnt})`, "success"); return; }
    setSaving(true);
    const data = await api("/invoices", { method: "POST", body: JSON.stringify(buildPayload(false)) });
    setSaving(false);
    if (data.success) { clearAll(); showToast("Virman sačuvan! #" + (data.data.invoice?.invoice_number || "")); if (data.data?.invoice?.id) { printReceipt(data.data.invoice.id); } }
    else { if (data._offline) { const cnt = saveOfflineDraft(buildPayload(false)); clearAll(); showToast(`Nema mreže! Draft sačuvan lokalno (${cnt})`, "error"); } else showToast(data.message || "Greška", "error"); }
  };

  const viewNcInvoice = async (id) => { const d = await api(`/invoices/show/${id}`); if (d.success) setNcDetail(d.data); };
  const handleStorno = async (id, reason) => {
    const d = await api(`/invoices/storno/${id}`, { method: "POST", body: JSON.stringify({ storno_reason: reason, fiscalize_now: true }) });
    if (d.success) { showToast("Storno uspješan!"); setStornoModal(null); setNcDetail(null); loadNcInvoices(); } else showToast(d.message, "error");
  };
  const handleEmail = async (id, email) => {
    const d = await api(`/invoices/email/${id}`, { method: "POST", body: JSON.stringify({ email }) });
    if (d.success) showToast("Email poslat!"); else showToast(d.message || "Greška", "error");
    setEmailModal(null);
  };

  const filtered = products.filter(p => {
    if (search) return p.name.toLowerCase().includes(search.toLowerCase());
    if (activeCategory === "all") return true;
    return String(p.category_id) === String(activeCategory);
  });

  const filteredPartners = partners.filter(p => !partnerSearch || p.name.toLowerCase().includes(partnerSearch.toLowerCase()) || (p.tax_id && p.tax_id.includes(partnerSearch)));

  const payDueLabel = (v) => ({ end_of_month: "Kraj mjeseca", avans: "AVANS", unspecified: "Neodređeno" }[v] || v);

  if (lastFiscalized) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", padding: 40 }}>
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <div style={{ width: 70, height: 70, background: "#ecfdf5", borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}><Check size={36} color={M.green} /></div>
        <h2 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 6px" }}>Virman fiskalizovan!</h2>
        <p style={{ color: M.muted, fontSize: 16, marginBottom: 22 }}>Bezgotovinsi račun registrovan kod Poreske Uprave</p>
        <div style={{ background: "white", borderRadius: 12, border: `1px solid ${M.border}`, padding: 20, marginBottom: 22, textAlign: "left" }}>
          <Row label="FIC" value={<span style={{ fontFamily: M.mono, fontSize: 12, color: M.green, wordBreak: "break-all" }}>{lastFiscalized.fic}</span>} />
          <Row label="Ukupno" value={<span style={{ fontWeight: 700, fontSize: 22 }}>{fmt(lastFiscalized.total)} €</span>} />
          <Row label="Broj" value={<span style={{ fontFamily: M.mono, fontSize: 13 }}>{lastFiscalized.invoice?.invoice_number || "-"}</span>} last />
          {lastFiscalized.verification_url && (() => {
              // Use the backend-built verification URL (8 params, Europe/Podgorica TZ).
              const vurl = lastFiscalized.verification_url;
              return <div style={{ textAlign: "center", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${M.border}` }}><img src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(vurl)}`} alt="QR" style={{ width: 80, height: 80 }} /><div style={{ fontSize: 10, color: M.muted, marginTop: 4 }}>Verifikacija</div></div>;
            })()}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button onClick={() => { const id = lastFiscalized.invoice?.id; if (id) openPdf(`/invoices/pdf/${id}?type=a4`); }} style={M.btn("#f3f4f6", M.muted)}><Download size={17} />PDF</button>
          <button onClick={async () => { const id = lastFiscalized.invoice?.id; if (id) await printReceipt(id, true); }} style={M.btn("#f3f4f6", M.muted)}><Printer size={17} />Štampaj</button>
          <button onClick={() => setLastFiscalized(null)} style={M.btn(M.dark)}><Plus size={17} />Novi virman</button>
        </div>
      </div>
    </div>
  );

  // NC invoice detail
  if (ncDetail) {
    const inv = ncDetail.invoice; const items = ncDetail.items || [];
    const isStorno = inv.is_storno == 1;
    return (
      <div style={{ height: "100%", overflow: "auto", background: M.bg }}>
        <div style={{ padding: "14px 20px", background: "white", borderBottom: `1px solid ${M.border}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => setNcDetail(null)} style={{ padding: 8, background: "#f3f4f6", border: "none", borderRadius: 6, cursor: "pointer", display: "flex" }}><ArrowLeft size={17} /></button>
          <div style={{ flex: 1 }}><h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{isStorno && "⊖ "}{inv.invoice_number}</h2><span style={{ fontSize: 13, color: "#9ca3af" }}>{fmtDateTime(inv.invoice_date)}</span></div>
          <Badge status={inv.status} />
          <button onClick={() => openPdf(`/invoices/pdf/${inv.id}?type=a4`)} style={M.btnSm("#f3f4f6", M.muted)}><Download size={13} />PDF</button>
          <button onClick={async () => { await printReceipt(inv.id, true); }} style={M.btnSm("#f3f4f6", M.muted)}><Printer size={13} />Štampaj</button>
          <button onClick={() => setEmailModal(inv.id)} style={M.btnSm("#f3f4f6", M.muted)}><Mail size={13} />Email</button>
          {inv.status === "draft" && <button onClick={async () => { const d = await api(`/invoices/fiscalize/${inv.id}`, { method: "POST" }); if (d.success) { showToast("Fiskalizovano!"); loadNcInvoices(); viewNcInvoice(inv.id); } else showToast(d.message, "error"); }} style={M.btnSm("#f0fdf4", "#15803d")}><Zap size={13} />Fiskalizuj</button>}
          {ncDetail.can_storno && <button onClick={() => setStornoModal(inv.id)} style={M.btnSm("#fef2f2", M.red)}><RotateCcw size={13} />Storniraj</button>}
        </div>
        <div style={{ padding: 20, maxWidth: 700, margin: "0 auto" }}>
          {inv.fic && <div style={{ ...M.card, padding: 14, marginBottom: 10, background: "#f0fdf4", borderColor: "#bbf7d0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 8 }}><Check size={14} color="#15803d" /><span style={{ fontWeight: 700, color: "#15803d", fontSize: 13 }}>Fiskalizovano</span></div>
            <Row label="FIC" value={<span style={{ fontFamily: M.mono, fontSize: 10, wordBreak: "break-all" }}>{inv.fic}</span>} />
            <Row label="IIC" value={<span style={{ fontFamily: M.mono, fontSize: 10, wordBreak: "break-all" }}>{inv.iic || "-"}</span>} last />
            {ncDetail.verification_url && (() => {
              // Use the backend-built verification URL (mapr.tax.gov.me, 8 params,
              // Europe/Podgorica TZ). Same QR as receipts/PDFs.
              const vurl = ncDetail.verification_url;
              return <div style={{ textAlign: "center", marginTop: 10, paddingTop: 10, borderTop: "1px solid #bbf7d0" }}><img src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(vurl)}`} alt="QR" style={{ width: 100, height: 100 }} /><div style={{ fontSize: 10, color: "#15803d", marginTop: 4 }}>Verifikacioni QR kod</div></div>;
            })()}
          </div>}
          {inv.buyer_name && <div style={{ background: "white", borderRadius: 10, border: `1px solid ${M.border}`, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Kupac</div>
            <Row label="Naziv" value={inv.buyer_name} />
            {inv.buyer_pib && <Row label="PIB" value={inv.buyer_pib} />}
            {inv.payment_due && <Row label="Rok plaćanja" value={payDueLabel(inv.payment_due)} last />}
          </div>}
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${M.border}`, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${M.border}`, fontWeight: 700, fontSize: 15 }}>Stavke</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead><tr style={{ background: "#f9fafb" }}><th style={th}>Naziv</th><th style={{ ...th, textAlign: "center" }}>Kol.</th><th style={{ ...th, textAlign: "right" }}>Cijena</th><th style={{ ...th, textAlign: "right" }}>Popust</th><th style={{ ...th, textAlign: "right" }}>Ukupno</th></tr></thead>
              <tbody>{items.map((it, i) => <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}><td style={td}>{it.item_name || it.name}</td><td style={{ ...td, textAlign: "center" }}>{fmtQty(it.quantity)}</td><td style={{ ...td, textAlign: "right" }}>{fmt(it.unit_price)} €</td><td style={{ ...td, textAlign: "right", color: parseFloat(it.discount_amount || 0) > 0 ? M.orange : M.muted }}>{parseFloat(it.discount_amount || 0) > 0 ? `-${fmt(it.discount_amount)} €` : "-"}</td><td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{fmt(it.total_amount || it.quantity * it.unit_price)} €</td></tr>)}</tbody>
            </table>
          </div>
          <div style={{ background: "white", borderRadius: 10, border: `1px solid ${M.border}`, padding: 18 }}>
            <Row label="Neto" value={`${fmt(inv.subtotal)} €`} /><Row label="PDV" value={`${fmt(inv.vat_amount)} €`} />
            {parseFloat(inv.discount_amount || 0) > 0 && <Row label="Popust" value={<span style={{ color: M.orange }}>-{fmt(inv.discount_amount)} €</span>} />}
            <Row label="Plaćanje" value={inv.payment_method === "cash" ? "Gotovina" : inv.payment_method === "card" ? "Kartica" : inv.payment_method === "split" ? "Podijeljeno" : inv.payment_method === "bank_transfer" ? "Virman" : inv.payment_method === "other" ? "Ostalo" : inv.payment_method} />
            {inv.payment_due && <Row label="Rok plaćanja" value={payDueLabel(inv.payment_due)} />}
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 12, borderTop: "1px solid #f3f4f6", marginTop: 10 }}><span style={{ fontWeight: 700, fontSize: 18 }}>Ukupno</span><span style={{ fontWeight: 700, fontSize: 22, color: isStorno ? M.red : M.green }}>{fmt(inv.total_amount)} €</span></div>
          </div>
        </div>
        {stornoModal && <Modal onClose={() => setStornoModal(null)} title="Storno"><StornoForm onStorno={r => handleStorno(stornoModal, r)} onClose={() => setStornoModal(null)} /></Modal>}
        {emailModal && <Modal onClose={() => setEmailModal(null)} title="Pošalji email"><EmailForm onSend={e => handleEmail(emailModal, e)} onClose={() => setEmailModal(null)} /></Modal>}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Tab: Create | List */}
      <div style={{ padding: "5px 10px", background: "white", borderBottom: `1px solid ${M.border}`, display: "flex", gap: 4, flexShrink: 0 }}>
        <button onClick={() => setSubView("create")} style={{ ...M.btnSm(subView === "create" ? M.dark : "#f3f4f6", subView === "create" ? "white" : M.muted), fontSize: 11 }}><PlusCircle size={12} />Novi</button>
        <button onClick={() => setSubView("list")} style={{ ...M.btnSm(subView === "list" ? M.dark : "#f3f4f6", subView === "list" ? "white" : M.muted), fontSize: 11 }}><FileText size={12} />Lista</button>
      </div>

      {subView === "list" ? (
        <div style={{ flex: 1, overflow: "auto", padding: 10 }}>
          {ncLoading ? <div style={{ textAlign: "center", padding: 30 }}><Loader2 size={22} className="spin" color="#bbb" /></div>
          : ncInvoices.length === 0 ? <div style={{ textAlign: "center", padding: 30, color: "#bbb", fontSize: 13 }}><Building size={28} /><p style={{ marginTop: 6 }}>Nema virmana</p></div>
          : ncInvoices.map(inv => {
            const isStorno = inv.is_storno == 1;
            return (
              <div key={inv.id} onClick={() => viewNcInvoice(inv.id)} style={{ ...M.card, padding: "10px 12px", marginBottom: 6, cursor: "pointer", background: isStorno ? "#fef2f2" : "white", display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ fontFamily: M.mono, fontSize: 11, fontWeight: 600 }}>{inv.invoice_number}</span><Badge status={isStorno ? "storno" : inv.status} /></div>
                  <div style={{ fontSize: 11, color: M.muted }}>{fmtDate(inv.invoice_date)} • {inv.buyer_name || "-"}</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{fmt(inv.total_amount)}€</div>
                <ChevronRight size={14} color="#ccc" />
              </div>
            );
          })}
        </div>
      ) : (<>
        {/* TOP 34% — Cart */}
        <div style={{ flex: "0 0 34%", display: "flex", flexDirection: "column", background: "white", borderBottom: `2px solid ${M.border}` }}>
          {buyerName && <div style={{ padding: "3px 10px", background: "#eff6ff", borderBottom: `1px solid ${M.border}`, fontSize: 11, color: M.blue, flexShrink: 0 }}><Building size={10} style={{ verticalAlign: -1 }} /> {buyerName} {buyerPib && `(${buyerPib})`}</div>}
          <div style={{ flex: 1, overflow: "auto", padding: "2px 8px", WebkitOverflowScrolling: "touch" }}>
            {cart.length === 0 ? <div style={{ textAlign: "center", padding: 10, color: "#bbb", fontSize: 12 }}>Dodaj stavke i partnera</div>
            : cart.map(item => { const up = item.custom_price !== null ? item.custom_price : item.price; let disc = 0; if (item.discount_type === "percentage") disc = (item.quantity * up) * (item.discount_value / 100); else if (item.discount_type === "fixed") disc = item.discount_value; const lineGross = ((item.quantity * up) - disc) * (1 + item.vat_rate / 100); return (
              <div key={item.id} style={{ padding: "3px 0", borderBottom: "1px solid #f5f5f5" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <div onClick={() => setEditingItem(editingItem === item.id ? null : item.id)} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}><div style={{ fontWeight: 600, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div><div style={{ fontSize: 10, color: "#999" }}>{fmt(up)}x{fmtQty(item.quantity)}{disc > 0 ? ` -${fmt(disc)}` : ""}</div></div>
                  {/* Same minus-removes-at-min logic as Nova Prodaja cart, scaled to Virman's smaller buttons. */}
                  <button onClick={() => {
                    const step = item.is_decimal_unit ? 0.01 : 1;
                    if (item.quantity <= step + 0.0001) { removeFromCart(item.id); return; }
                    updateCartItem(item.id, { quantity: parseFloat((item.quantity - step).toFixed(2)) });
                  }} style={{ width: 20, height: 20, border: `1px solid ${M.border}`, borderRadius: 4, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>{item.quantity <= (item.is_decimal_unit ? 0.01 : 1) + 0.0001 ? <Trash2 size={9} color={M.red} /> : <Minus size={9} />}</button>
                  {item.is_decimal_unit ? (
                    <button onClick={() => setQtyModal(item)} style={{ fontSize: 11, fontWeight: 600, minWidth: 36, textAlign: "center", padding: "2px 4px", border: `1px dashed ${M.blue}`, borderRadius: 4, background: "white", cursor: "pointer", color: M.dark }} title="Unesi tačnu količinu">{fmtQty(item.quantity)}</button>
                  ) : (
                    <span style={{ fontSize: 12, fontWeight: 600, minWidth: 16, textAlign: "center" }}>{item.quantity}</span>
                  )}
                  <button onClick={() => updateCartItem(item.id, { quantity: parseFloat((item.quantity + (item.is_decimal_unit ? 0.01 : 1)).toFixed(2)) })} style={{ width: 20, height: 20, border: `1px solid ${M.border}`, borderRadius: 4, background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={9} /></button>
                  <div style={{ fontWeight: 700, fontSize: 11, minWidth: 42, textAlign: "right" }}>{fmt(lineGross)}€</div>
                </div>
                {editingItem === item.id && <div style={{ padding: "4px 0", display: "flex", gap: 3, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 60px" }}><label style={{ fontSize: 9, color: M.muted }}>Cijena</label><input type="number" step="0.01" value={item.custom_price !== null ? item.custom_price : item.price} onChange={e => updateCartItem(item.id, { custom_price: parseFloat(e.target.value) || 0 })} style={{ ...M.input, padding: "4px 6px", fontSize: 11 }} /></div>
                  {/* Inline qty: branched parsing on is_decimal_unit, same as Nova Prodaja. */}
                  <div style={{ flex: "1 1 50px" }}><label style={{ fontSize: 9, color: M.muted }}>Kol.</label><input type="number" step={item.is_decimal_unit ? "0.01" : "1"} value={item.quantity} onChange={e => updateCartItem(item.id, { quantity: item.is_decimal_unit ? Math.max(0.01, parseFloat(String(e.target.value).replace(",", ".")) || 1) : Math.max(1, parseInt(e.target.value) || 1) })} style={{ ...M.input, padding: "4px 6px", fontSize: 11 }} /></div>
                  <div style={{ flex: "1 1 80px" }}><label style={{ fontSize: 9, color: M.muted }}>Popust</label><div style={{ display: "flex", gap: 2 }}><select value={item.discount_type} onChange={e => updateCartItem(item.id, { discount_type: e.target.value, discount_value: 0 })} style={{ ...M.input, padding: "3px", fontSize: 10, flex: 1 }}><option value="none">-</option><option value="percentage">%</option><option value="fixed">€</option></select>{item.discount_type !== "none" && <input type="number" step="0.01" value={item.discount_value} onChange={e => updateCartItem(item.id, { discount_value: parseFloat(e.target.value) || 0 })} style={{ ...M.input, padding: "3px", fontSize: 10, width: 40 }} />}</div></div>
                </div>}
              </div>); })}
          </div>
          {cart.length > 0 && <div style={{ padding: "6px 8px", borderTop: `1px solid ${M.border}`, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ fontWeight: 700, fontSize: 16 }}>{fmt(cartCalc.total)} €</span><span style={{ background: "#f3f4f6", padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600, color: M.muted }}>{cart.length}</span></div>
              <button onClick={() => setShowDiscountModal(true)} style={{ ...M.btnSm(invoiceDiscount.type !== "none" ? "#fef3c7" : "#f3f4f6", invoiceDiscount.type !== "none" ? "#92400e" : M.muted), fontSize: 11, padding: "4px 10px" }}><Percent size={11} />{invoiceDiscount.type !== "none" ? `${invoiceDiscount.value}${invoiceDiscount.type === "percentage" ? "%" : "€"}` : "Popust"}</button>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button onClick={clearAll} style={{ padding: "8px 12px", background: "#fef2f2", color: M.red, border: "none", borderRadius: 8, cursor: "pointer", fontFamily: M.font, fontWeight: 600, fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}><Trash2 size={13} /></button>
              <button onClick={handleSaveDraft} style={{ flex: 1, padding: "8px 12px", background: M.blue, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: M.font, fontWeight: 600, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}><Archive size={13} />Draft</button>
              {buyerName && <button onClick={() => setShowConfirm(true)} style={{ flex: 2, padding: "8px 12px", background: M.green, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: M.font, fontWeight: 600, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}><Zap size={13} />Fiskalizuj</button>}
            </div>
          </div>}
        </div>

        {/* MIDDLE 33% — Products */}
        <div style={{ flex: "0 0 33%", display: "flex", flexDirection: "column", borderBottom: `2px solid ${M.border}` }}>
          <div style={{ padding: "3px 8px", background: "white", display: "flex", gap: 3, borderBottom: `1px solid ${M.border}`, flexShrink: 0 }}>
            <div style={{ flex: 1, position: "relative" }}><Search size={12} style={{ position: "absolute", left: 7, top: "50%", transform: "translateY(-50%)", color: "#bbb" }} /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Proizvodi..." style={{ ...M.input, paddingLeft: 24, padding: "4px 6px 4px 24px", fontSize: 12, borderRadius: 6 }} /></div>
          </div>
          <div style={{ padding: "2px 8px", background: "white", display: "flex", gap: 3, overflowX: "auto", borderBottom: `1px solid ${M.border}`, flexShrink: 0 }}>
            <CatTab active={activeCategory === "all"} onClick={() => setActiveCategory("all")} label="Sve" />
            {categories.map(c => <CatTab key={c.id} active={String(activeCategory) === String(c.id)} onClick={() => setActiveCategory(c.id)} label={c.name} />)}
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: 4, background: M.bg }}>
            {loading ? <div style={{ textAlign: "center", padding: 16 }}><Loader2 size={18} className="spin" color="#bbb" /></div>
            : <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
              {filtered.map(p => { const inCart = cart.find(c => c.id === p.id); const gross = parseFloat(p.price) * (1 + parseFloat(p.vat_rate ?? 21) / 100); return (
                <button key={p.id} onClick={() => addToCart(p)} style={{ position: "relative", padding: "6px 3px", background: inCart ? "#f0fdf4" : "white", border: inCart ? `2px solid ${M.green}` : `1px solid ${M.border}`, borderRadius: 6, cursor: "pointer", textAlign: "center", fontFamily: M.font }}>
                  {inCart && <div style={{ position: "absolute", top: -4, right: -4, width: 16, height: 16, background: M.green, borderRadius: "50%", color: "white", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{fmtQty(inCart.quantity)}</div>}
                  <div style={{ fontWeight: 600, fontSize: 10, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: M.green }}>{fmt(gross)}€</div>
                </button>); })}
            </div>}
          </div>
        </div>

        {/* BOTTOM 33% — Partners */}
        <div style={{ flex: "0 0 33%", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "4px 8px", background: "white", display: "flex", gap: 4, borderBottom: `1px solid ${M.border}`, flexShrink: 0, alignItems: "center" }}>
            <div style={{ flex: 1, position: "relative" }}><Search size={12} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#bbb" }} /><input value={partnerSearch} onChange={e => setPartnerSearch(e.target.value)} placeholder="Partneri..." style={{ ...M.input, padding: "7px 8px 7px 26px", fontSize: 12, borderRadius: 8 }} /></div>
            <button onClick={() => setManualPartner(manualPartner ? null : { name: "", tax_id: "" })} style={{ padding: "7px 10px", background: manualPartner ? M.orange : "#f3f4f6", color: manualPartner ? "white" : M.muted, border: "none", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Edit3 size={13} /></button>
          </div>
          {manualPartner && <div style={{ padding: "3px 8px", background: "#fff7ed", borderBottom: `1px solid ${M.border}`, display: "flex", gap: 3, flexShrink: 0 }}>
            <input value={manualPartner.name} onChange={e => setManualPartner(p => ({ ...p, name: e.target.value }))} placeholder="Naziv" style={{ ...M.input, padding: "4px 6px", fontSize: 11, flex: 2 }} />
            <input value={manualPartner.tax_id} onChange={e => setManualPartner(p => ({ ...p, tax_id: e.target.value }))} placeholder="PIB" style={{ ...M.input, padding: "4px 6px", fontSize: 11, flex: 1 }} />
          </div>}
          <div style={{ flex: 1, overflow: "auto", padding: 4, background: M.bg }}>
            {filteredPartners.length === 0 ? <div style={{ textAlign: "center", padding: 14, color: "#bbb", fontSize: 11 }}>Nema partnera</div>
            : filteredPartners.map(p => {
              const sel = selectedPartner?.id === p.id;
              return (
                <button key={p.id} onClick={() => { setSelectedPartner(sel ? null : p); setManualPartner(null); }} style={{ width: "100%", padding: "5px 8px", background: sel ? "#f0fdf4" : "white", border: sel ? `2px solid ${M.green}` : `1px solid ${M.border}`, borderRadius: 6, cursor: "pointer", textAlign: "left", fontFamily: M.font, marginBottom: 3, display: "flex", alignItems: "center", gap: 5 }}>
                  {sel && <Check size={11} color={M.green} />}
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>{p.tax_id && <div style={{ fontSize: 10, color: M.muted }}>{p.tax_id}</div>}</div>
                </button>
              );
            })}
          </div>
        </div>
      </>)}

      {showConfirm && <Modal onClose={() => setShowConfirm(false)} title="Potvrda virmana">
        <div style={{ background: "#f9fafb", borderRadius: 10, padding: 14, marginBottom: 12, textAlign: "center" }}>
          <div style={{ fontSize: 13, color: M.muted, marginBottom: 4 }}>{buyerName} {buyerPib && `(${buyerPib})`}</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: M.green }}>{fmt(cartCalc.total)} €</div>
          <div style={{ fontSize: 12, color: M.muted, marginTop: 4 }}>{cart.length} stavki</div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Način plaćanja</div>
        <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
          {[{ id: "bank_transfer", l: "Virman", i: Building }, { id: "other", l: "Ostalo", i: Wallet }].map(m => (
            <button key={m.id} onClick={() => setPaymentMethod(m.id)} style={{ flex: 1, padding: "7px", border: "1px solid", borderColor: paymentMethod === m.id ? M.green : M.border, background: paymentMethod === m.id ? "#f0fdf4" : "white", color: paymentMethod === m.id ? M.green : M.muted, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 3, fontFamily: M.font }}><m.i size={13} />{m.l}</button>))}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Rok plaćanja</div>
        <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
          {[{ id: "end_of_month", l: "Kraj mj." }, { id: "avans", l: "AVANS" }, { id: "unspecified", l: "Neodređeno" }].map(d => (
            <button key={d.id} onClick={() => setPaymentDue(d.id)} style={{ flex: 1, padding: "7px", border: "1px solid", borderColor: paymentDue === d.id ? M.blue : M.border, background: paymentDue === d.id ? "#eef2ff" : "white", color: paymentDue === d.id ? M.blue : M.muted, borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: M.font }}>{d.l}</button>))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowConfirm(false)} style={{ ...M.btn("#f3f4f6", M.muted), flex: 1, justifyContent: "center" }}>Otkaži</button>
          <button onClick={handleFiscalize} style={{ ...M.btn(M.green), flex: 1, justifyContent: "center" }}><Zap size={15} />Fiskalizuj</button>
        </div>
      </Modal>}
      {showDiscountModal && <Modal onClose={() => setShowDiscountModal(false)} title="Popust"><DiscountForm current={invoiceDiscount} onApply={d => { setInvoiceDiscount(d); setShowDiscountModal(false); }} onClose={() => setShowDiscountModal(false)} /></Modal>}
      {qtyModal && <QtyNumpadModal product={qtyModal} onClose={() => setQtyModal(null)} onSave={qty => { updateCartItem(qtyModal.id, { quantity: qty }); setQtyModal(null); }} />}
      {stornoModal && <Modal onClose={() => setStornoModal(null)} title="Storno"><StornoForm onStorno={r => handleStorno(stornoModal, r)} onClose={() => setStornoModal(null)} /></Modal>}
      {emailModal && <Modal onClose={() => setEmailModal(null)} title="Email"><EmailForm onSend={e => handleEmail(emailModal, e)} onClose={() => setEmailModal(null)} /></Modal>}
    </div>
  );
}

export default function App() {
  const [auth, setAuth] = useState(() => {
    try { const t = localStorage.getItem("minipos_token"); const u = localStorage.getItem("minipos_user"); return t && u ? { token: t, user: JSON.parse(u) } : null; } catch(e) { return null; }
  });
  const handleLogin = (token, user) => { localStorage.setItem("minipos_token", token); localStorage.setItem("minipos_user", JSON.stringify(user)); setAuth({ token, user }); };
  const handleLogout = () => { localStorage.removeItem("minipos_token"); localStorage.removeItem("minipos_user"); setAuth(null); };
  if (!auth) return <LoginScreen onLogin={handleLogin} />;
  return <MiniPOSApp user={auth.user} onLogout={handleLogout} />;
}
